import React, { Component, createContext, useState, useEffect } from 'react';
import { Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { ArrowsFullscreen, InfoCircle } from 'react-bootstrap-icons';
import update from 'immutability-helper';
import Fullscreen from 'fullscreen-react';
import * as areaPolygon from 'area-polygon';
import * as convertUnits from 'convert-units';
import disableScroll from 'disable-scroll';

import { LeadContext } from 'Leads/Details/hooks/context';
import { computeUtilityTotalUsageKwh } from 'Leads/Details/utils/utility';
import {
  buildFixedInverterPanelCapacityNotification,
  FIXED_INVERTER_PANEL_CAPACITY_CALLBACK,
  countProductionPayloadPanels,
  FIXED_INVERTER_PANEL_CAPACITY_MESSAGE,
  hasFixedInverterQuantity,
  matchesPrimaryInverterType,
  resolveAppliedPanelCount,
  resolveMicroInverterQuantity,
} from 'Leads/Details/utils/inverter';
import {
  Canvas3D,
  DesignContext,
  setSegmentAttributes,
  filterDictByKeys,
  createProposalData,
} from '@aerialytic/canvas-library';

import {
  distance,
  scaleShape,
  rotateSegmentPanels,
  findCommonLine,
  drawOrientation,
  isPointEqual,
  isPointInShape,
  filterDuplicates,
  isPointNearLine,
  retry,
  ios,
  getSegmentArea,
} from './utils';
import Controls from './Controls';
import ControlsBottom from './ControlsBottom';
import Menu from './Menu';
import canvasHandler from './DrawCanvas/handlers/canvasHandler';
// import PlacePanels from './PlacePanels';
import ShadeReport from '../ShadeReport';
import IFrameLogo from 'components/IFrameLogo';
import {
  buildProductionCallbackPayload,
  getPanelCounts,
  mapRoofKeyedPayloadToDesign,
  mapSelectedPanelsPerRoofToDesign,
  mapStringingDataToDesign,
} from 'Leads/Details/utils/productionCallback';
import {
  buildPanelsByIndexFromPanelShadingData,
  buildPerPanelShadingArrays,
  buildLossPayload,
  finiteNumberOrDefault,
  normalizeMonthlyLoss,
  normalizeMonthlySolarAccess,
} from 'Leads/Details/solar-production/payload-utils';
import { CURRENT_PROPOSAL_VERSION } from 'IFrame/postMessage';
import { buildEffectiveSystemLosses } from './system-losses';
import {
  buildEffectiveInlineHardwareSpec,
  findPrimaryInverterUsage,
  resolveSlotHardwareFromCanvas,
} from './hardware-selection';

const CANVAS_SIZE = 1024;
const SCALE = 0.688;
const samplingRate = 30;
const konvaKeys = ['shading', 'setback', 'design', 'revise', 'production'];
const canvasKeys = ['segments', 'obstacles', 'trees'];

class DesignTool extends Component {
  constructor(props) {
    super(props);

    const { iframe, designFrame } = this.props;

    this.state = {
      tool: 'Polygon',
      designType: 'M',
      panelNotSet: false,

      segments: [],
      obstacles: [],
      trees: [],
      panels: [],
      shade: false,
      buffered: [], // setback-buffered segments

      package: {},
      hasAutoCalculatedInverterQuantity: false, // Track if user has manually set inverter quantity
      buffer: 12.7,
      rowSpacing: 0,
      panelTilt: 0,
      azimuthRestriction: [0, 360],
      powerOptimizer: null,
      selectedRoof: -1,
      selectedLabel: 'Eave',
      selectedPanels: false,
      roofAreasSqFt: [],
      roofAreasUnit: 'ft²',
      roofAreasSignature: '',

      setbackMode: 0,
      fireSetbacks: {
        general: 18,
        eave: 0,
        ridge: 18,
        rake: 18,
        hip: 18,
        valley: 18,
      },

      mode: 'select',
      moveMode: 'group',
      polyEdit: -1,
      focusedPolygonToEdit: -1,
      currentAccordion: 'segments',
      currentTab: null,
      scale: SCALE,

      showOverlay: false,
      colorRange: [0, 2450],

      isFullScreen: false,
      viewMode: '2D', // !iframe || designFrame ? '2D' : '3D',
      selectionMode: 'segment',
      coordinates: [],
      selectedObjects: {
        roofSegments: [],
        roofVertices: {},
        roofLines: {},
        obstacles: [],
        fireSetbacks: {},
        panels: {},
        trees: [],
      },
      optPitchAndAzimuth: { pitch: 24.0, azimuth: 184.0 },
      optPOA: null,
      shadingTexture: null,
      shadingMonth: 0,
      shadingStatus: 0,
      shadingIsRunning: false,
      serverSideShadingIsRunning: false,
      optimizer1IsRunning: false,
      optimizer2IsRunning: false,
      edgeDetectionIsRunning: false,
      measureIsRunning: false,
      panelPlacementIsRunning: false,
      AIIsRunning: false,
      automaticAzimuth: false,
      pvwattsHandled: false,
      solarPanels: [],
      inverters: [],
      losses: null,
      updateRGBTextureIsRunning: false,
      panelPlacementFillMode: 3,
      panelPlacementLayoutMode: 2,
      panelPlacementOnlySelectedRoof: false,
      // Solar Production state variables
      productionIsRunning: false,
      productionHandled: false,
      totalAnnualKwh: 0,
      totalMonthlyKwh: Array(12).fill(0),
      totalClippingKwh: 0,
      totalMonthlyClippingKwh: Array(12).fill(0),
      dcAcRatio: null,
      stringingData: null,
      invertersUsed: null,
      productionPerPanel: 0,
      productionPerPanelData: null,
      productionPerStringData: null,
      hourlyProduction: null,
      productionHardware: null,
      productionSaveError: null,
      selectedPanelCount: 0,
      stringingPanelIndexMode: 'all',
      stringingSelectedPanelIndexMap: null,
      systemSizeKw: null,
      mpptCountTotal: null,
      // Target production settings
      useTargetProduction: false,
      targetProductionKwh: 0,
      wiring: {
        ohm_per_m_string: 0.004,
        loop_factor: 2,
      },
      wiringConfigured: false,
      maxDcAcRatio: 1.5,
      includeHourlyProduction: false,
      onlySelectedPanels: false,
      // Auto quantity settings for inverters
      primaryInverterAutoQuantity: true,
      // Original Installation Package additional inverters (snapshot at mount).
      // Used by the Production accordion to lazily prefill "+ Add" rows
      // with the user's saved Settings defaults. Order is preserved.
      packageAdditionalInverters: [],
      weatherDatasetSelected: 'nsrdb_goes_v4_2024',
      weatherDatasetFetchedPath: null,
      isCreatingProposal: false,
      pitch_in_degrees: true,
      // Shading detection
      shadingHasRun: false,
      shadingNeedsRerun: false,
      shadingRerunNoticeShown: false,
    };

    this.histories = [];
    this.historyState = {
      current: -1,
      state: '',
    };
    this.suppressSolarProductionFromSelection = false;
    this.pendingSolarProductionRun = null;
    this.lastProductionSnapshot = null;
    this.panelPlacementCallbackArmed = false;
    this.panelPlacementFinalizeTimer = null;
    this.panelPlacementCompletion = null;
    this.skipNextSegmentsAutoSave = false;
  }

  unlockPageScroll = () => {
    try {
      disableScroll.off();
    } catch (e) { }
  };

  handleWindowBlur = () => {
    this.unlockPageScroll();
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.unlockPageScroll();
    }
  };

  handleWindowMouseOut = (e) => {
    if (!e?.relatedTarget && !e?.toElement) {
      this.unlockPageScroll();
    }
  };

  handleWindowMouseUp = () => {
    try {
      const hovered = this.container?.matches?.(':hover');
      if (!hovered) this.unlockPageScroll();
    } catch (e) {
      this.unlockPageScroll();
    }
  };

  blockDesignAltAreaSelect = (e) => {
    if (this.state.currentAccordion !== 'design') return;
    if (!this.container || !this.container.contains(e.target)) return;
    if (
      !e.altKey &&
      !(typeof e.getModifierState === 'function' && e.getModifierState('Alt'))
    ) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
    if (e.type === 'pointerdown' || e.type === 'mousedown') {
      try {
        this.container.querySelectorAll('.selectBox').forEach((node) => {
          if (node.parentElement) node.parentElement.removeChild(node);
        });
      } catch (err) { }
    }
  };

  componentDidMount() {
    const { company, uid, packageAPI, CanvasAPI, lead, newEndpoint } =
      this.context;
    const {
      iframe,
      designFrame,
      context: iframeContext = {},
      callback: iframeCallback,
    } = this.props;

    const resolution = Number(lead.property.resolution);
    const { production: overlay, design_type: designType } = lead.property;
    const setbackAPI = newEndpoint('hardware/setbacks/');
    const solarPanelsAPI = newEndpoint('hardware/panels/');
    const invertersAPI = newEndpoint('hardware/inverters/');
    const lossesEndpoint = newEndpoint('hardware/losses/');
    const nsrdbDatasetsAPI = newEndpoint('api/nsrdb/datasets/');

    const imagery = lead?.property?.rgb;

    if (iframe && !imagery) {
      iframeCallback('Error', { code: '46', message: 'Lead has no imagery' });
    }

    // load canvas objects
    Promise.all([
      CanvasAPI.getCanvas(uid),
      packageAPI.list().then((data) => (data.length !== 0 ? data : Object())),
      setbackAPI.retrieve(company),
      iframe ? Promise.resolve([]) : solarPanelsAPI.list(),
      iframe ? Promise.resolve([]) : invertersAPI.list(),
      lossesEndpoint.retrieve(company),
      iframe ? Promise.resolve(null) : nsrdbDatasetsAPI.list().catch(() => null),
    ])
      .then(
        async ([
          canvasData,
          packageData,
          fireSetbacks,
          solarPanels,
          inverters,
          losses,
          nsrdbDatasetsMeta,
        ]) => {
          const parseDatasetKeyFromPath = (p) => {
            try {
              if (!p || typeof p !== 'string') return null;
              const base = p.split('/').pop() || '';
              if (!base.endsWith('.csv')) return null;
              return base.slice(0, -'.csv'.length) || null;
            } catch (e) {
              return null;
            }
          };
          const weatherDatasetFetchedPath =
            canvasData && typeof canvasData.weather_dataset === 'string'
              ? canvasData.weather_dataset
              : null;
          const weatherDatasetSelectedFromDb =
            parseDatasetKeyFromPath(weatherDatasetFetchedPath) ||
            nsrdbDatasetsMeta?.default ||
            'nsrdb_goes_v4_2024';

          const statePackage = packageData.filter(
            (item, index) => item.state === lead.address.state
          )[0];
          const defaultPackage = packageData.filter(
            (item, index) => item.state === null
          )[0];
          // console.log('selected package: ', statePackage !== undefined ? statePackage : defaultPackage);

          const package_ = statePackage || defaultPackage;
          const flagPackage = { ...package_ };
          // Normalize one additional-inverter entry (from package API,
          // canvas, or iframe context) into the canonical
          // {id, autoQuantity} shape that the Production accordion UI
          // expects. Entries without a resolvable id are dropped.
          const normalizeAdditionalInverter = (entry) => {
            if (!entry || typeof entry !== 'object') return null;
            const id =
              entry.id ??
              entry.inverter_id ??
              entry.inverterId ??
              entry.companyInverterID ??
              entry.companyInverterId ??
              null;
            if (!id) return null;
            const autoQuantity =
              entry.autoQuantity ?? entry.auto_quantity ?? true;
            return { id, autoQuantity: autoQuantity !== false };
          };
          const normalizeAdditionalInverters = (list) =>
            Array.isArray(list)
              ? list.map(normalizeAdditionalInverter).filter(Boolean)
              : [];
          // Snapshot the package's saved additional inverters BEFORE any
          // canvas/iframe overrides below. Used by the Production accordion
          // to lazily prefill rows when the user clicks "+ Add".
          const packageAdditionalInvertersSnapshot =
            normalizeAdditionalInverters(package_?.additional_inverters);
          // Rewrite package_.additional_inverters in-place to the canonical
          // shape so the Production accordion's "Inverter N" rows render
          // the saved inverter selection correctly on first paint.
          package_.additional_inverters = packageAdditionalInvertersSnapshot;
          const { panel: panelHardware } = package_;
          const hasIframeCompanyPanelID =
            iframeContext?.companyPanelID !== null &&
            iframeContext?.companyPanelID !== undefined;
          const hasIframePanelType = Boolean(
            iframeContext?.panelType &&
              typeof iframeContext.panelType === 'object'
          );
          const hasExplicitIframePanelMode =
            iframeContext?.panelDefault !== undefined &&
            iframeContext?.panelDefault !== null;
          const hasInvalidExplicitPanelConfig =
            hasExplicitIframePanelMode &&
            iframeContext?.panelDefault !== 'default' &&
            !hasIframeCompanyPanelID &&
            !hasIframePanelType;
          if (
            iframe &&
            iframeContext &&
            !hasIframeCompanyPanelID &&
            (hasIframePanelType || hasInvalidExplicitPanelConfig)
          ) {
            if (iframe && !designFrame && hasIframePanelType) {
              iframeContext.panelType = {
                ...panelHardware,
                ...iframeContext.panelType,
              };
              // console.log(`(************ iframe panel: ${iframeContext.panelType}`)
              package_.panel = iframeContext.panelType;
              if (
                package_.panel?.model === flagPackage.panel?.model &&
                Number(package_.panel?.power) ===
                Number(flagPackage.panel?.power)
              ) {
                iframeCallback('Error', {
                  code: '445',
                  message: 'iframe panel config is not set properly! (same)',
                });
                this.setState({
                  panelNotSet: true,
                });
              }
            } else {
              if (!designFrame) {
                iframeCallback('Error', {
                  code: '445',
                  message: 'iframe panel config is not set properly!',
                });
                this.setState({
                  panelNotSet: true,
                });
              }
            }
          }

          if (iframeContext?.panelDefault === 'default') {
            iframeContext.panelType = { ...panelHardware };
            package_.panel = iframeContext.panelType;
          }

          if (
            iframe &&
            iframeContext &&
            iframeContext?.companyPanelID !== null &&
            iframeContext?.companyPanelID !== undefined
          ) {
            const { companyPanelID } = iframeContext;
            // console.log(`************ companyPanelID = ${companyPanelID} in didMount`);
            const companyPanelsAPI = newEndpoint('panels/company/');
            companyPanelsAPI.retrieve(companyPanelID).then((panelData) => {
              package_.panel = panelData;
            });
          }
          if (
            iframe &&
            iframeContext &&
            iframeContext?.companyInverterID !== null &&
            iframeContext?.companyInverterID !== undefined
          ) {
            const { companyInverterID } = iframeContext;
            // console.log(`************ companyInverterID = ${companyInverterID} in didMount`);
            const companyInverterAPI = newEndpoint('inverters/company/');
            companyInverterAPI
              .retrieve(companyInverterID)
              .then((inverterData) => {
                package_.inverter = inverterData;
              });
          }
          const filteredIframeContext = filterDictByKeys(iframeContext, [
            'buffer',
            'rowSpacing',
            'panelTilt',
            'maxDcAcRatio',
            'includeHourlyProduction',
            'azimuthRestriction',
            'setback',
            'panelType',
            'wiring',
            'powerOptimizer',
            'defaultBtnView',
          ]);

          const normalizedOptimizerFromConfig =
            this.normalizePowerOptimizerPayload(filteredIframeContext?.powerOptimizer);
          if (normalizedOptimizerFromConfig) {
            filteredIframeContext.powerOptimizer = normalizedOptimizerFromConfig;
          } else if (
            iframe &&
            iframeContext?.companyPowerOptimizerID !== null &&
            iframeContext?.companyPowerOptimizerID !== undefined
          ) {
            try {
              const companyOptimizersAPI = newEndpoint('power_optimizers/company/');
              const optimizerData = await companyOptimizersAPI.retrieve(
                iframeContext.companyPowerOptimizerID
              );
              const normalizedOptimizer =
                this.normalizePowerOptimizerPayload(optimizerData);
              if (normalizedOptimizer) {
                filteredIframeContext.powerOptimizer = normalizedOptimizer;
              }
            } catch (err) {
              console.log(`iframe power optimizer lookup error: ${err}`);
            }
          }

          const normalizeWiringConfig = (value) => {
            if (!value || typeof value !== 'object') return null;
            const next = {};
            const ohm = Number(value.ohm_per_m_string);
            const loop = Number(value.loop_factor);
            if (Number.isFinite(ohm) && ohm > 0) {
              next.ohm_per_m_string = ohm;
            }
            if (Number.isFinite(loop) && loop > 0) {
              next.loop_factor = loop;
            }
            if (!Object.keys(next).length) return null;
            return { ...this.state.wiring, ...next };
          };
          const wiringFromIframe =
            normalizeWiringConfig(iframeContext?.wiring) ||
            normalizeWiringConfig(
              iframeContext?.inverter?.ohm_per_m_string !== undefined ||
                iframeContext?.inverter?.loop_factor !== undefined
                ? {
                  ohm_per_m_string: iframeContext.inverter.ohm_per_m_string,
                  loop_factor: iframeContext.inverter.loop_factor,
                }
                : null
            );
          if (wiringFromIframe) {
            filteredIframeContext.wiring = wiringFromIframe;
            filteredIframeContext.wiringConfigured = true;
          }

          const losses_ = buildEffectiveSystemLosses({
            baseLosses: losses,
            canvasLosses: canvasData.losses,
            iframeLosses:
              iframe && iframeContext ? iframeContext?.losses : null,
            lead,
            forceStateSnow: true,
          });

          console.log(`--> system losses: ${JSON.stringify(losses_)}`);

          solarPanels = solarPanels.filter((el, i) => el?.is_hidden == false);
          inverters = inverters.filter((el, i) => el?.is_hidden == false);

          // Use saved hardware selection from canvas if available, otherwise use package defaults.
          // Precedence per slot: raw spec (inline hardware, no company id) → FK/id (registered
          // hardware) → default package hardware. The spec wins so an inline spec survives reload
          // instead of collapsing to the default (whose id it inherits via the settings merge).
          const panelResolved = resolveSlotHardwareFromCanvas({
            spec: canvasData.selected_panel_spec,
            fkObject: canvasData.selected_panel,
            idValue: canvasData.selected_panel_id,
            catalog: solarPanels,
          });
          if (panelResolved.hardware) {
            package_.panel = panelResolved.hardware;
            if (panelResolved.source === 'spec') {
              package_.selectedPanelSpec = panelResolved.hardware;
            }
          }
          const inverterResolved = resolveSlotHardwareFromCanvas({
            spec: canvasData.selected_inverter_spec,
            fkObject: canvasData.selected_inverter,
            idValue: canvasData.selected_inverter_id,
            catalog: inverters,
          });
          if (inverterResolved.hardware) {
            package_.inverter = inverterResolved.hardware;
            if (inverterResolved.source === 'spec') {
              package_.selectedInverterSpec = inverterResolved.hardware;
            }
          }
          if (
            canvasData.inverter_quantity !== undefined &&
            canvasData.inverter_quantity !== null
          ) {
            package_.inverterQuantity = canvasData.inverter_quantity;
          } else if (
            iframeContext?.inverterQuantity !== undefined &&
            iframeContext?.inverterQuantity !== null
          ) {
            package_.inverterQuantity = iframeContext.inverterQuantity;
          }
          if (canvasData.additional_inverters) {
            // Older canvases or external writers may use mixed shapes; run
            // through the same normalizer so the Production accordion's UI
            // always sees {id, autoQuantity}.
            package_.additional_inverters = normalizeAdditionalInverters(
              canvasData.additional_inverters
            );
          } else if (
            iframeContext?.companyAdditionalInverterIDs ||
            iframeContext?.additionalInverters
          ) {
            const rawAdditionalInverters =
              Array.isArray(iframeContext?.additionalInverters) &&
                iframeContext.additionalInverters.length > 0
                ? iframeContext.additionalInverters
                : iframeContext.companyAdditionalInverterIDs || [];
            const configAdditionalInverters = rawAdditionalInverters
              .map((item) => {
                if (typeof item === 'string') {
                  return { id: item, quantity: 1, autoQuantity: true };
                }
                if (item && typeof item === 'object') {
                  const id = item.companyInverterID || item.id;
                  if (!id) return null;
                  return {
                    id,
                    quantity: item.quantity || 1,
                    autoQuantity: item.autoQuantity !== false,
                  };
                }
                return null;
              })
              .filter(Boolean);
            if (configAdditionalInverters.length > 0) {
              package_.additional_inverters = configAdditionalInverters;
            }
          }

          // Load primary inverter auto quantity state
          const savedPrimaryInverterAutoQuantity =
            canvasData.primary_inverter_auto_quantity;
          const primaryInverterAutoQuantityFromDb =
            savedPrimaryInverterAutoQuantity !== undefined &&
              savedPrimaryInverterAutoQuantity !== null
              ? savedPrimaryInverterAutoQuantity
              : this.state.primaryInverterAutoQuantity;
          const primaryInverterAutoQuantityFromConfig =
            typeof iframeContext?.primaryInverterAutoQuantity === 'boolean'
              ? iframeContext.primaryInverterAutoQuantity
              : null;
          const primaryInverterAutoQuantityResolved =
            primaryInverterAutoQuantityFromConfig !== null
              ? primaryInverterAutoQuantityFromConfig
              : primaryInverterAutoQuantityFromDb;

          let totalAnnualKwhFromDb = canvasData.total_annual_kwh || 0;
          let totalMonthlyKwhFromDb =
            canvasData.total_monthly_kwh || Array(12).fill(0);
          let totalClippingKwhFromDb = canvasData.total_clipping_kwh || 0;
          const totalMonthlyClippingKwhFromDb =
            canvasData.total_monthly_clipping_kwh || Array(12).fill(0);
          const dcAcRatioFromDb = canvasData.dc_ac_ratio ?? null;
          const stringingDataFromDb = canvasData.stringing_data || null;
          const invertersUsedFromDb = canvasData.inverters_used || null;
          const productionPerPanelDataFromDb =
            canvasData.production_per_panel || null;
          const productionPerStringDataFromDb =
            canvasData.production_per_string || null;
          const hourlyProductionFromDb =
            canvasData.hourly_production || null;
          const countPanelsFromProduction = (data) =>
            Array.isArray(data)
              ? data.reduce(
                (sum, roof) =>
                  sum +
                  (Array.isArray(roof?.roofPanels)
                    ? roof.roofPanels.filter((v) => Number(v) > 0).length
                    : 0),
                0
              )
              : 0;
          const selectedPanelCountFromDb = Array.isArray(stringingDataFromDb)
            ? stringingDataFromDb.reduce(
              (sum, s) => sum + (s?.panels?.length || 0),
              0
            )
            : countPanelsFromProduction(productionPerPanelDataFromDb);
          let productionHandledFromDb =
            Boolean(stringingDataFromDb) ||
            Boolean(invertersUsedFromDb) ||
            totalAnnualKwhFromDb > 0 ||
            totalClippingKwhFromDb > 0;
          if (
            productionPerPanelDataFromDb ||
            productionPerStringDataFromDb ||
            hourlyProductionFromDb
          ) {
            productionHandledFromDb = true;
          }

          if (
            productionHandledFromDb &&
            (!canvasData.total_annual_kwh ||
              canvasData.total_annual_kwh === 0) &&
            Array.isArray(totalMonthlyKwhFromDb) &&
            totalMonthlyKwhFromDb.length >= 12
          ) {
            const derived = totalMonthlyKwhFromDb.reduce(
              (a, b) => a + (Number(b) || 0),
              0
            );
            if (derived > 0) totalAnnualKwhFromDb = derived;
          }

          if (
            productionHandledFromDb &&
            (!canvasData.total_clipping_kwh ||
              canvasData.total_clipping_kwh === 0) &&
            Array.isArray(totalMonthlyClippingKwhFromDb) &&
            totalMonthlyClippingKwhFromDb.length >= 12
          ) {
            const derivedClip = totalMonthlyClippingKwhFromDb.reduce(
              (a, b) => a + (Number(b) || 0),
              0
            );
            if (derivedClip > 0) totalClippingKwhFromDb = derivedClip;
          }

          const productionPerPanelFromDb =
            selectedPanelCountFromDb > 0
              ? totalAnnualKwhFromDb / selectedPanelCountFromDb
              : 0;

          const systemSizeKwFromDb =
            canvasData.system_size_kw !== undefined &&
              canvasData.system_size_kw !== null
              ? canvasData.system_size_kw
              : package_?.panel?.power && selectedPanelCountFromDb > 0
                ? (Number(package_.panel.power) * selectedPanelCountFromDb) / 1000
                : null;
          const inverterType = package_?.inverter?.type;
          const invertersCountFromDb = Array.isArray(invertersUsedFromDb)
            ? invertersUsedFromDb.reduce((sum, i) => sum + (i.count || 0), 0)
            : null;
          const mpptPerInv =
            package_?.inverter?.mppt_count_per_inverter !== undefined &&
              package_?.inverter?.mppt_count_per_inverter !== null
              ? Number(package_.inverter.mppt_count_per_inverter)
              : null;
          const mpptCountTotalFromDb =
            canvasData.mppt_count_total !== undefined &&
              canvasData.mppt_count_total !== null
              ? canvasData.mppt_count_total
              : inverterType === 'M' || inverterType === 'Micro'
                ? invertersCountFromDb
                : invertersCountFromDb !== null && mpptPerInv
                  ? invertersCountFromDb * mpptPerInv
                  : null;

          const rawUseTargetProduction = canvasData.use_target_production;
          const rawTargetProductionKwh = canvasData.target_production_kwh;

          const hasSavedTargetProductionSettings =
            rawUseTargetProduction !== undefined &&
              rawUseTargetProduction !== null
              ? true
              : rawTargetProductionKwh !== undefined &&
              rawTargetProductionKwh !== null &&
              Number(rawTargetProductionKwh) > 0;

          const utilityTotalUsageKwh = computeUtilityTotalUsageKwh(
            lead?.utility
          );
          const defaultTargetProductionKwh =
            Number.isFinite(Number(utilityTotalUsageKwh)) &&
              Number(utilityTotalUsageKwh) > 0
              ? Math.round(Number(utilityTotalUsageKwh))
              : 0;

          const rawTargetNum = Number(rawTargetProductionKwh);
          const hasPositiveTarget =
            rawTargetProductionKwh !== undefined &&
            rawTargetProductionKwh !== null &&
            rawTargetProductionKwh !== '' &&
            Number.isFinite(rawTargetNum) &&
            rawTargetNum > 0;

          const targetProductionKwhFromDb = hasPositiveTarget
            ? rawTargetNum
            : defaultTargetProductionKwh;

          const useTargetProductionFromDb = hasSavedTargetProductionSettings
            ? Boolean(rawUseTargetProduction) ||
            Number(targetProductionKwhFromDb) > 0
            : true;
          const hasIframeTargetProduction =
            iframeContext &&
            Object.prototype.hasOwnProperty.call(
              iframeContext,
              'targetProduction'
            );
          const iframeTargetProductionValue = hasIframeTargetProduction
            ? Number(iframeContext.targetProduction)
            : null;
          const iframeTargetProductionOverride = hasIframeTargetProduction
            ? {
              useTargetProduction:
                Number.isFinite(iframeTargetProductionValue) &&
                iframeTargetProductionValue > 0,
              targetProductionKwh:
                Number.isFinite(iframeTargetProductionValue) &&
                  iframeTargetProductionValue > 0
                  ? iframeTargetProductionValue
                  : 0,
            }
            : null;
          const resolvedUseTargetProduction =
            iframeTargetProductionOverride?.useTargetProduction ??
            useTargetProductionFromDb;
          const resolvedTargetProductionKwh =
            iframeTargetProductionOverride?.targetProductionKwh ??
            targetProductionKwhFromDb;
          const maxDcAcRatioFromDb =
            canvasData.max_dc_ac_ratio !== undefined &&
              canvasData.max_dc_ac_ratio !== null
              ? canvasData.max_dc_ac_ratio
              : this.state.maxDcAcRatio;
          const onlySelectedPanelsFromDb = Boolean(
            canvasData.only_selected_panels
          );

          // Detect if shading has been run
          const shadingHasRun = this.detectShadingHasRun(canvasData);

          this.setStatex(
            {
              ...canvasData,
              segments: canvasData?.segments,
              package: package_,
              packageAdditionalInverters: packageAdditionalInvertersSnapshot,
              // ensure UI reflects persisted inverter + production state on refresh
              primaryInverterAutoQuantity: primaryInverterAutoQuantityResolved,
              hasAutoCalculatedInverterQuantity:
                primaryInverterAutoQuantityResolved === null
                  ? this.state.hasAutoCalculatedInverterQuantity
                  : !primaryInverterAutoQuantityResolved,
              // target production state
              useTargetProduction: resolvedUseTargetProduction,
              targetProductionKwh: resolvedTargetProductionKwh,
              maxDcAcRatio: maxDcAcRatioFromDb,
              onlySelectedPanels: onlySelectedPanelsFromDb,
              stringingPanelIndexMode: 'all',
              productionHandled: productionHandledFromDb,
              totalAnnualKwh: totalAnnualKwhFromDb,
              totalMonthlyKwh: totalMonthlyKwhFromDb,
              totalClippingKwh: totalClippingKwhFromDb,
              totalMonthlyClippingKwh: totalMonthlyClippingKwhFromDb,
              dcAcRatio: dcAcRatioFromDb,
              stringingData: stringingDataFromDb,
              invertersUsed: invertersUsedFromDb,
              selectedPanelCount: selectedPanelCountFromDb,
              productionPerPanel: productionPerPanelFromDb,
              productionPerPanelData: productionPerPanelDataFromDb,
              productionPerStringData: productionPerStringDataFromDb,
              hourlyProduction: hourlyProductionFromDb,
              productionHardware: productionHandledFromDb
                ? {
                  panel: package_?.panel || null,
                  inverter: package_?.inverter || null,
                  additionalInverters: package_?.additional_inverters || [],
                  inverterQuantity: package_?.inverterQuantity || 1,
                }
                : null,
              systemSizeKw: systemSizeKwFromDb,
              mpptCountTotal: mpptCountTotalFromDb,
              fireSetbacks,
              solarPanels,
              inverters,
              resolution,
              overlay,
              designType,
              losses: losses_,
              weatherDatasetSelected: weatherDatasetSelectedFromDb,
              weatherDatasetFetchedPath,
              shadingHasRun,
              ...filteredIframeContext,
            },
            () => {
              this.updateSetbacks();
            }
          );

          try {
            const shouldPersistDefaultPanel =
              !iframe &&
              !canvasData?.selected_panel &&
              (canvasData?.selected_panel_id === undefined || canvasData?.selected_panel_id === null) &&
              package_?.panel?.id !== undefined &&
              package_?.panel?.id !== null;
            if (shouldPersistDefaultPanel) {
              this.context.CanvasAPI.saveCanvas({
                selected_panel_id: String(package_.panel.id),
              });
            }
          } catch (e) { }

          if (stringingDataFromDb) {
            setTimeout(() => {
              try {
                if (this.canvas3DElement?.selectPanelsFromStringing) {
                  this.canvas3DElement.selectPanelsFromStringing(
                    stringingDataFromDb,
                    'all'
                  );
                }
              } catch (e) { }
            }, 0);
          }

          if (iframe && iframeContext?.setback) {
            const s = iframeContext.setback;
            if (Array.isArray(s)) {
              const bfireSetbacks = {
                ...fireSetbacks,
                eave: s[0],
                ridge: s[1],
                rake: s[2],
                hip: s[3],
                valley: s[4],
              };
              this.updateSetbacks(bfireSetbacks, 1);
            } else {
              const bfireSetbacks = { ...fireSetbacks, general: s };
              this.updateSetbacks(bfireSetbacks, 0);
            }
          }
        }
      )
      .catch((e) => {
        console.log(e);
      });

    document.addEventListener('keydown', this.onKeyDown, false);
    [
      'pointerdown',
      'pointermove',
      'pointerup',
      'mousedown',
      'mousemove',
      'mouseup',
    ].forEach((type) => {
      document.addEventListener(type, this.blockDesignAltAreaSelect, true);
    });

    this.setState3x({
      coordinates: lead.property.coordinates,
    });

    window.addEventListener('blur', this.handleWindowBlur);
    window.addEventListener('mouseout', this.handleWindowMouseOut);
    window.addEventListener('mouseup', this.handleWindowMouseUp);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  getActiveDesignId = () => {
    const { uid, designId } = this.context;
    return designId || this.state.canvasId || uid;
  };

  // TODO: Implement saving for each annotation type instead of always calling saveAll
  componentDidUpdate(prevProps, prevState) {
    if (!(this.canvas3DElement && this.canvas3DElement.isLoaded)) return;

    const { currentAccordion, fireSetbacks } = this.state;

    if (currentAccordion === 'segments') {
      this.refreshRoofAreasFromCanvas();
    }

    const enteringProduction =
      prevState.currentAccordion !== 'production' &&
      currentAccordion === 'production';
    if (
      enteringProduction &&
      this.state.stringingData &&
      this.canvas3DElement?.selectPanelsFromStringing
    ) {
      try {
        this.canvas3DElement.selectPanelsFromStringing(
          this.state.stringingData,
          'all'
        );
        const countSelected = () => {
          try {
            const byVertex = this.canvas3DElement?.getSelectedPanelsByVertex
              ? this.canvas3DElement.getSelectedPanelsByVertex()
              : {};
            return Object.values(byVertex || {}).reduce(
              (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
              0
            );
          } catch {
            return 0;
          }
        };
        if (countSelected() === 0) {
          let tries = 0;
          const retry = () => {
            tries += 1;
            if (tries > 6) return;
            try {
              this.canvas3DElement.selectPanelsFromStringing(
                this.state.stringingData,
                'all'
              );
              if (countSelected() > 0) return;
            } catch (e) { }
            setTimeout(retry, 100);
          };
          setTimeout(retry, 100);
        }
      } catch (e) { }
    }
    ['segments', 'obstacles', 'trees', 'panels', 'annotations'].forEach(
      (accordion) => {
        if (
          (prevState.currentAccordion === accordion &&
            currentAccordion !== accordion) ||
          prevState[accordion] !== this.state[accordion]
        ) {
          if (accordion === 'segments' && this.skipNextSegmentsAutoSave) {
            this.skipNextSegmentsAutoSave = false;
            return;
          }
          // console.log(accordion, 'closed.');
          const data = [...this.state[accordion]];
          this.context.CanvasAPI.saveCanvas({ [accordion]: data });
        }
      }
    );

    const { iframe, context: iframeContext = {} } = this.props;
    const { currentAccordion: prevAccordion } = prevState;
    const { context: prevContext = {} } = prevProps;

    if (
      iframe &&
      iframeContext?.setback &&
      JSON.stringify(prevContext?.setback) !==
      JSON.stringify(iframeContext?.setback)
    ) {
      const s = iframeContext.setback;
      if (Array.isArray(s)) {
        const bfireSetbacks = {
          ...fireSetbacks,
          eave: s[0],
          ridge: s[1],
          rake: s[2],
          hip: s[3],
          valley: s[4],
        };
        this.updateSetbacks(bfireSetbacks, 1);
      } else {
        const bfireSetbacks = { ...fireSetbacks, general: s };
        this.updateSetbacks(bfireSetbacks, 0);
      }
    } else if (
      !konvaKeys.includes(prevAccordion) &&
      konvaKeys.includes(currentAccordion)
    ) {
      console.log('update setbacks.');
      this.updateSetbacks();
    }

    // Auto-enable `panelPlacementOnlySelectedRoof` whenever the user
    // gains a roof-segment selection. This is intentionally
    // one-directional: we never force the toggle OFF here. The toggle
    // is otherwise a user preference — they can flip it on preemptively
    // before selecting, or leave it on after a selection-wipe (e.g.
    // entering Revise, which calls clearSelection with forceClear=true
    // in libs/canvas-library/src/Canvas3D.js). A bidirectional
    // derivation broke the user's ability to manually toggle ON in the
    // common no-selection state — see comment in
    // setPanelPlacementOnlySelectedRoof for the matching setter
    // simplification. Guarded with an equality check so we don't loop.
    const selectedRoofSegmentCount =
      this.state.selectedObjects?.roofSegments?.length || 0;
    if (
      selectedRoofSegmentCount > 0 &&
      !this.state.panelPlacementOnlySelectedRoof
    ) {
      this.setState({ panelPlacementOnlySelectedRoof: true });
      try {
        if (this.context?.CanvasAPI?.saveCanvas) {
          this.context.CanvasAPI.saveCanvas({
            panelPlacementOnlySelectedRoof: true,
          });
        }
      } catch (e) {}
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown, false);
    [
      'pointerdown',
      'pointermove',
      'pointerup',
      'mousedown',
      'mousemove',
      'mouseup',
    ].forEach((type) => {
      document.removeEventListener(type, this.blockDesignAltAreaSelect, true);
    });
    try {
      window.removeEventListener('blur', this.handleWindowBlur);
      window.removeEventListener('mouseout', this.handleWindowMouseOut);
      window.removeEventListener('mouseup', this.handleWindowMouseUp);
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    } catch (e) { }
    this.unlockPageScroll();
    try {
      clearTimeout(this.solarProductionDebounceTimer);
    } catch (e) { }
    try {
      clearTimeout(this.panelPlacementFinalizeTimer);
    } catch (e) { }
    this.resolvePanelPlacementCompletion(new Error('Panel placement cancelled'));
  }

  resolvePanelPlacementCompletion = (error = null) => {
    const completion = this.panelPlacementCompletion;
    if (!completion) return;
    this.panelPlacementCompletion = null;
    if (error) completion.reject(error);
    else completion.resolve();
  };

  refreshRoofAreasFromCanvas = () => {
    try {
      const getter = this.canvas3DElement?.getRoofAreas3D;
      if (typeof getter !== 'function') return;
      const data = getter();
      const signature = data?.signature || '';
      if (signature === this.state.roofAreasSignature) return;
      const areasSqFt = Array.isArray(data?.areasSqFt) ? data.areasSqFt : [];
      const unit = data?.unit || 'ft²';
      this.setState({
        roofAreasSqFt: areasSqFt,
        roofAreasUnit: unit,
        roofAreasSignature: signature,
      });
    } catch (e) { }
  };

  onKeyDown = (e) => {
    // if (e.key === '0') this.handleResetScale();
    if (e.key === '+') this.handleScaleUp();
    if (e.key === '-') this.handleScaleDown();
  };

  handleSaveAll = () => {
    const { CanvasAPI } = this.context;
    const { segments, obstacles, trees, colorRange } = this.state;
    CanvasAPI.saveCanvas({
      segments,
      obstacles,
      trees,
      colorRange,
    }).then((canvasData) => this.setState2x({ ...canvasData }));
  };

  // This function is for sending data to backend and receive auto-placed panels in return.
  exportCanvas = (includeChildren = false) => {
    const {
      lead: { property },
      uid,
      user,
    } = this.context;
    const { obstacles, segments, trees, resolution } = this.state;
    console.log('exporting...');

    const [lat, lng] = property.coordinates;
    const request = {
      lat,
      lng,
      uid,
      company: user?.company?.id,
      resolution,
      ai_design: false, // property.design_type === 'A',
      obstacles,
    };

    request.obstacles = obstacles;

    request.roofs = segments.map(({ pitch, ...roof }, index) => {
      if (includeChildren) {
        roof.children = segments
          .filter(({ geometry: shape }, jdx) => {
            if (index === jdx) return false;
            return shape.every((pt) => isPointInShape(pt, roof.geometry));
          })
          .map((child) => child.geometry);
      }
      return { ...roof, pitch };
    });

    request.trees = trees.map(({ geometry: points, ...tree }) => {
      let radius = 0;
      let geometry = points;
      if (tree.shape === 'Circle') {
        const [p1, p2] = points;
        radius = Math.ceil(distance(p1, p2));
        geometry = [p1];
      }
      return { ...tree, geometry, radius };
    });
    return request;
  };

  handlePlacement = async (
    params = null,
    serverSide = false,
    fillMode = 3,
    layoutMode = 2
  ) => {
    this.unlockPageScroll();
    this.panelPlacementCallbackArmed = false;
    if (this.panelPlacementFinalizeTimer) {
      clearTimeout(this.panelPlacementFinalizeTimer);
      this.panelPlacementFinalizeTimer = null;
    }
    this.resolvePanelPlacementCompletion(new Error('Panel placement superseded'));

    await this.applyPendingPanelHardware();

    const {
      iframe,
      context: iframeContext,
      callback: iframeCallback,
    } = this.props;
    const placementCompletion = new Promise((resolve, reject) => {
      this.panelPlacementCompletion = { resolve, reject };
    });
    placementCompletion.catch(() => {});
    this.clearSelectedPanelsOnly();
    this.setState3x({ panelPlacementIsRunning: true });
    if (!serverSide) {
      const companyPanelID = iframe
        ? params?.companyPanelID !== undefined
          ? params?.companyPanelID
          : iframeContext?.companyPanelID
        : null;

      if (companyPanelID !== null && companyPanelID !== undefined) {
        try {
          const { newEndpoint } = this.context;
          const companyPanelsAPI = newEndpoint('panels/company/');
          const panelData = await companyPanelsAPI.retrieve(companyPanelID);
          await this.changePanelHardware(panelData);
        } catch (err) {
          console.warn(`Failed to load company panel hardware ${companyPanelID}`, err);
        }
      }
      try {
        let panelPlacementFillMode = fillMode;
        let panelPlacementLayoutMode = layoutMode;
        if (params?.fill_mode) {
          panelPlacementFillMode = params?.fill_mode;
        } else if (iframeContext?.panelOrientation) {
          panelPlacementFillMode = iframeContext?.panelOrientation;
        }
        if (params?.layout_mode) {
          panelPlacementLayoutMode = params?.layout_mode;
        } else if (iframeContext?.panelLayout) {
          panelPlacementLayoutMode = iframeContext?.panelLayout;
        }
        const {
          panelPlacementOnlySelectedRoof,
          selectedObjects,
        } = this.state;
        const selectedRoofSegments = Array.isArray(
          selectedObjects?.roofSegments
        )
          ? [...selectedObjects.roofSegments]
          : [];
        const hasRoofSelection = selectedRoofSegments.length > 0;
        const restrictToSelection =
          panelPlacementOnlySelectedRoof || hasRoofSelection;
        const fillModeNum = Number(panelPlacementFillMode);
        const layoutModeNum = Number(panelPlacementLayoutMode);
        setTimeout(
          () => {
            try {
              this.panelPlacementCallbackArmed = true;
              if (restrictToSelection && hasRoofSelection) {
                this.canvas3DElement.panelPlacementByIndices(
                  selectedRoofSegments,
                  fillModeNum,
                  layoutModeNum
                );
              } else {
                this.canvas3DElement.panelPlacement(
                  false,
                  fillModeNum,
                  layoutModeNum
                );
              }
              if (
                this.panelPlacementCallbackArmed &&
                !this.panelPlacementFinalizeTimer
              ) {
                throw new Error('Panel placement did not complete');
              }
              this.resetLegacyFlagsAfterPlacement(
                restrictToSelection && hasRoofSelection
                  ? selectedRoofSegments
                  : null
              );
            } catch (err) {
              console.log(`Error in 3D panel placement: ${err}`);
              this.panelPlacementCallbackArmed = false;
              this.setState3x({ panelPlacementIsRunning: false });
              this.unlockPageScroll();
              this.resolvePanelPlacementCompletion(err);
            }
          },
          1500
        );
      } catch (err) {
        console.log(`Error in 3D panel placement: ${err}`);
        this.panelPlacementCallbackArmed = false;
        this.setState3x({ panelPlacementIsRunning: false });
        this.unlockPageScroll();
        this.resolvePanelPlacementCompletion(err);
      }
    } else {
      const {
        segments,
        buffer,
        rowSpacing,
        panelTilt,
        azimuthRestriction,
        package: { panel },
      } = this.state;
      const fireSetbacks = segments.map((seg) => seg.setbacks);
      const { panelPlacementAPI, uid } = this.context;
      panelPlacementAPI
        .create({
          uid,
          panel,
          fireSetbacks,
          buffer,
          rowSpacing,
          panelTilt,
          azimuthRestriction,
          fillMode,
        })
        .then((data) => {
          const panels = data;
          this.panelPlacementCallbackArmed = true;
          this.setPanelPlacement(panels);
          if (
            this.panelPlacementCallbackArmed &&
            !this.panelPlacementFinalizeTimer
          ) {
            throw new Error('Panel placement did not complete');
          }
          this.resetLegacyFlagsAfterPlacement();
        })
        .catch((err) => {
          this.resolvePanelPlacementCompletion(err);
        })
        .finally(() => {
          if (this.panelPlacementCallbackArmed) return;
          this.setState3x({ panelPlacementIsRunning: false });
          this.unlockPageScroll();
        });
    }
    return placementCompletion;
    // const { package: { panel: { length, width } }, buffer } = this.state;

    // const request = this.exportCanvas(true);
    // request.panelSize = [length, width].map((n) => (+n + buffer) / 10);
    // // request.setback = +convertUnits(+setbackSize).from('in').to('cm').toFixed(5);

    // this.context.panelFillAPI.create(request).then((data) => {
    //   // load panel data
    //   const panels = data.map((panel) => {
    //     const { vertical, horizontal } = panel;
    //     vertical.selected = Array(vertical.points.length).fill(false);
    //     horizontal.selected = Array(horizontal.points.length).fill(false);
    //     return { ...panel, horizontal, vertical };
    //   });
    //   this.setStatex({ panels });
    // }).catch((err) => console.error(err));
  };

  resetLegacyFlagsAfterPlacement = (targetIndices = null) => {
    try {
      const { CanvasAPI } = this.context;
      const { segments, selectedObjects } = this.state;
      if (!Array.isArray(segments) || segments.length === 0) return;

      const indices = Array.isArray(targetIndices)
        ? targetIndices
        : Array.isArray(selectedObjects?.roofSegments) &&
          selectedObjects.roofSegments.length
          ? selectedObjects.roofSegments
          : segments.map((_, idx) => idx);
      if (!indices.length) return;

      const indexSet = new Set(indices.map((i) => Number(i)));
      const nextSegments = segments.map((seg, idx) =>
        indexSet.has(idx) ? { ...seg, is_lagacy: false } : seg
      );

      // const changed = nextSegments.some(
      //   (s, i) => (segments?.[i]?.is_lagacy ?? null) !== (s?.is_lagacy ?? null)
      // );
      // if (!changed) return;

      this.setState3x({ segments: nextSegments });
      try {
        if (CanvasAPI?.saveCanvas) CanvasAPI.saveCanvas({ segments: nextSegments });
      } catch (e) {}
    } catch (e) {}
  };

  // TODO: do we need to enforce that request ids match segment ids?
  // this point would be generally applicable to other methods/functions
  handleMeasure = (serverSide = false) => {
    const { mode } = this.state;
    if (mode !== 'select') {
      this.handleMode('select');
      setTimeout(() => {
        this.handleMeasure(serverSide);
      }, 200);
      return;
    }

    this.setState3x({ measureIsRunning: true });
    if (!serverSide) {
      setTimeout(this.canvas3DElement.measure, 0);
    } else {
      const { measureAPI, uid } = this.context;
      // const { segments } = this.state;
      measureAPI
        .create({ uid })
        .then((data) => {
          const [newSegments, offset] = data;
          this.setSegmentParameters(newSegments);
          // const copySegments = [...segments];
          // newSegments.forEach((seg, i) => {
          //   copySegments[i].pitch = seg.pitch.toFixed(2);
          //   copySegments[i].azimuth = seg.azimuth.toFixed(2);
          //   copySegments[i].height = seg.height.toFixed(2);
          // });
          // this.setStatex({ segments: copySegments });
          this.canvas3DElement.changeOffset(offset);
        })
        .finally(() => this.setState3x({ measureIsRunning: false }));
    }
  };

  handleDownload3DJSON = () => {
    try {
      this.canvas3DElement?.downloadJSON?.();
    } catch (e) {}
  };

  handleDownloadDXF = () => {
    try {
      this.canvas3DElement?.downloadDXF?.();
    } catch (e) {}
  };

  setPVwattsLosses = (losses) => {
    this.setState2x({ losses });
    try {
      this.context.CanvasAPI.saveCanvas({ losses });
    } catch (e) { }
  };

  countPlacedPanels = (panels) => {
    if (!Array.isArray(panels)) return 0;
    return panels.reduce((total, segment) => {
      const horizontalCount = segment?.horizontal?.points?.length || 0;
      const verticalCount = segment?.vertical?.points?.length || 0;
      return total + horizontalCount + verticalCount;
    }, 0);
  };

  maybeApplyPendingPanelForFirstPlacement = (prevPanels, nextPanels) => {
    const hadNoPlacedPanels =
      this.countPlacedPanels(prevPanels) === 0 ||
      this.lastRenderedPlacedPanelCount === 0;
    const nextHasPanels = this.countPlacedPanels(nextPanels) > 0;
    if (this.firstManualInsertPanel && nextHasPanels) {
      const panel = this.firstManualInsertPanel;
      this.firstManualInsertPanel = null;
      return panel;
    }
    if (!hadNoPlacedPanels || !nextHasPanels) return null;
    return this.state.package?.pendingPanel || null;
  };

  handlePVWatts = async () => {
    const { newEndpoint, company: id } = this.context;
    const {
      iframe,
      context: iframeContext,
      callback: iframeCallback,
    } = this.props;
    const {
      PVWattsAPI,
      displaySnack,
      updateLead,
      lead: { property },
    } = this.context;
    const {
      package: { inverter, inverterQuantity = 1, additional_inverters = [] },
      losses,
    } = this.state;

    // Ensure we have required inverter data before proceeding
    if (!inverter) {
      console.warn('handlePVWatts: No inverter selected');
      if (iframe && iframeCallback) {
        iframeCallback('Error', {
          code: '50',
          message: 'No inverter selected for PVWatts calculation',
        });
      }
      return;
    }

    let tryCount = 3;
    let [optPitch, optAzimuth] = [24.0, 184.0];
    try {
      // calculate optimal pitch and azimuth for poa request
      [optPitch, optAzimuth] = this.canvas3DElement.getOptPitchAndAzimuth();
      await new Promise((r) => setTimeout(r, 4000));
      if (
        tryCount > 0 &&
        (optPitch === undefined || optAzimuth === undefined)
      ) {
        [optPitch, optAzimuth] = this.canvas3DElement.getOptPitchAndAzimuth();
        await new Promise((r) => setTimeout(r, 2000));
        tryCount -= 1;
      }
    } catch (err) {
      if (iframe && tryCount === 0 /*&& err.response.data.code*/) {
        // console.log(err);
        iframeCallback('Error', {
          code: '22',
          message: 'Error in calculating optimal pitch and azimuth',
        });
      }
    }

    // alert(`${property.optimal_roof.pitch.toFixed(1)}, ${property.optimal_roof.azimuth.toFixed(1)}`);

    const request = this.exportCanvas(true);
    if (property.optimal_poa.reduce((a, b) => a + b, 0) === 0) {
      request.optPitchAndAzimuth = [
        property?.optimal_roof?.pitch
          ? property?.optimal_roof?.pitch.toFixed(1)
          : optPitch.toFixed(1),
        property?.optimal_roof?.azimuth
          ? property?.optimal_roof?.azimuth.toFixed(1)
          : optAzimuth.toFixed(1),
      ];
    }
    request.inverter_efficiency = inverter?.efficiency;
    if (iframe) {
      const filteredIframeContext = filterDictByKeys(iframeContext, [
        'panelType',
        'inverter',
        'monitoring',
        'mounting',
      ]);
      if (iframeContext?.companyInverterID) {
        request.inverter_efficiency = inverter?.efficiency;
      } else if (filteredIframeContext?.inverter?.efficiency) {
        request.inverter_efficiency =
          filteredIframeContext?.inverter.efficiency;
      } else {
        console.log('default inverter efficiency');
        request.inverter_efficiency = 97.0;
      }
    }

    request.inverter = inverter;
    if (inverter?.type !== 'M' && inverter?.type !== 'Micro') {
      // Transform additional inverters to the format expected by PVWatts API
      const contextInverters = this.context?.hardware?.inverters;
      const stateInverters = this.state?.inverters;
      const inverterCatalog =
        Array.isArray(contextInverters) && contextInverters.length > 0
          ? contextInverters
          : Array.isArray(stateInverters)
            ? stateInverters
            : [];
      request.additional_inverters = additional_inverters
        .map((inv) => {
          if (!inv || typeof inv !== 'object') return null;
          const inverterId = inv.id ?? inv.companyInverterID ?? null;
          if (!inverterId) return null;
          return { ...inv, id: inverterId };
        })
        .filter((inv) => inv?.id) // Only include entries with valid IDs
        .map((inv) => {
          // Look up full hardware details when available to get capacity.
          const inverterHardware = inverterCatalog.find(
            (h) => h.id === inv.id
          );
          return {
            inverter_id: inv.id,
            capacity:
              Number(inverterHardware?.capacity) ||
              Number(inv?.capacity) ||
              0,
            quantity: inv?.quantity || 1,
          };
        });
    }

    request.losses = losses;

    PVWattsAPI.create(request)
      .then((data) => {
        const result = JSON.parse(data);
        const { segments } = this.state;
        const updatedSegments = [...segments];
        updatedSegments.forEach((seg, i) => {
          const ac = result['ac'][i]?.split(',').map(parseFloat) ?? 0;
          const poa = result['poa'][i]?.split(',').map(parseFloat) ?? 0;
          updatedSegments[i].acMonthly = ac;
          updatedSegments[i].poaMonthly = poa;
          updatedSegments[i].acAnnual = ac.reduce((a, b) => a + b, 0);
          updatedSegments[i].pvwatts_update = false;
        });
        if (property.optimal_poa.reduce((a, b) => a + b, 0) === 0) {
          const optPOA = result['poa']['opt']?.split(',').map(parseFloat) ?? 0;
          updateLead({ property: { optimal_poa: optPOA } }, true);
        }
        this.setState2x({ segments: updatedSegments, pvwattsHandled: true });
        if (iframe) {
          this.setState({
            pvwattsHandled: true,
          });
          console.log('Data updated from PVWatts');
        } else {
          displaySnack({
            variant: 'success',
            message: 'Data updated from PVWatts',
          });
        }
      })
      .catch((err) => {
        if (iframe && err?.response?.data?.code) {
          iframeCallback('Error', {
            code: err.response.data.code,
            message: err.response.data.msg,
          });
        }
        if (iframe) {
          console.log(`Failed to update data from PVWatts: {err}`);
        } else {
          displaySnack({
            variant: 'danger',
            message: 'Failed to update data from PVWatts',
          });
        }
      });
  };

  // handleSaveShadings = (gradients) => {
  //   const { uid, user, saveShadingsAPI } = this.context;
  //   const request = {
  //     uid,
  //     company: user.company.id,
  //     gradients,
  //   };
  //   saveShadingsAPI.create(request).then((data) => {
  //     const result = JSON.parse(data);
  //     console.log(result.url);
  //   });
  // }

  download_production_json = async () => {
    const { newEndpoint, lead, displaySnack } = this.context;
    const { segments, package: packageData, coordinates } = this.state;

    try {
      if (!segments || segments.length === 0) {
        displaySnack({
          variant: 'warning',
          message: 'No segments found. Please design segments first.',
        });
        return;
      }

      if (!packageData || !packageData.panel) {
        displaySnack({
          variant: 'warning',
          message:
            'No panel hardware selected. Please select panel hardware first.',
        });
        return;
      }

      const [lat, lon] = coordinates || lead?.property?.coordinates || [];
      if (!lat || !lon) {
        displaySnack({
          variant: 'warning',
          message: 'Coordinates not found. Please set location first.',
        });
        return;
      }

      // Prepare pitches and azimuths from segments
      // const pitches = segments.map(seg => seg.pitch || 0);
      // const azimuths = segments.map(seg => seg.azimuth || 0);

      // // Call the production JSON endpoint
      // const productionJsonAPI = newEndpoint('designer/production/json/');
      // const response = await productionJsonAPI.create({
      //   lat,
      //   lon,
      //   pitches,
      //   azimuths
      // });

      const productionData = await this.buildProductionJson(lat, lon);

      const dataStr = JSON.stringify(productionData, null, 2);
      const dataUri =
        'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

      const exportFileDefaultName = `production_${lead?.uid || 'data'}_${new Date().toISOString().split('T')[0]
        }.json`;

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();

      displaySnack({
        variant: 'success',
        message: 'Production JSON exported successfully!',
      });
    } catch (error) {
      console.error('Error exporting production JSON:', error);
      displaySnack({
        variant: 'danger',
        message: 'Failed to export production JSON. Please try again.',
      });
    }
  };

  normalizeSelectedPanelIndices = (raw = []) => {
    if (!Array.isArray(raw)) return [];
    const unique = Array.from(
      new Set(
        raw
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value))
      )
    );
    unique.sort((a, b) => a - b);
    return unique;
  };

  normalizeSelectedPanelsBySegment = (source = {}) => {
    if (!source || typeof source !== 'object') return {};
    const normalized = {};
    Object.keys(source).forEach((segmentKey) => {
      const segmentIndex = Number(segmentKey);
      if (!Number.isFinite(segmentIndex)) return;
      const selected = this.normalizeSelectedPanelIndices(source[segmentKey]);
      if (selected.length > 0) normalized[segmentIndex] = selected;
    });
    return normalized;
  };

  getSelectedPanelsBySegmentFromPanels = () => {
    const mapped = {};
    const { panels } = this.state;
    if (!Array.isArray(panels)) return mapped;

    panels.forEach((panel, segmentIndex) => {
      const horizontal = Array.isArray(panel?.horizontal?.selected)
        ? panel.horizontal.selected
        : [];
      const vertical = Array.isArray(panel?.vertical?.selected)
        ? panel.vertical.selected
        : [];
      const selected = [];

      horizontal.forEach((isSelected, panelIndex) => {
        if (isSelected) selected.push(panelIndex);
      });

      const horizontalCount = horizontal.length;
      vertical.forEach((isSelected, panelIndex) => {
        if (isSelected) selected.push(panelIndex + horizontalCount);
      });

      if (selected.length > 0) mapped[segmentIndex] = selected;
    });

    return mapped;
  };

  getSelectedPanelsBySegment = () => {
    const selectedFromState = this.normalizeSelectedPanelsBySegment(
      this.state?.selectedObjects?.panels || {}
    );
    if (
      Object.keys(selectedFromState).length > 0 ||
      this.state?.selectedPanels === false
    ) {
      return selectedFromState;
    }

    const selectedFromPanels = this.getSelectedPanelsBySegmentFromPanels();
    if (Object.keys(selectedFromPanels).length > 0) {
      return this.normalizeSelectedPanelsBySegment(selectedFromPanels);
    }

    try {
      const selectedFromCanvas = this.canvas3DElement?.getSelectedPanelsByVertex
        ? this.canvas3DElement.getSelectedPanelsByVertex() || {}
        : {};
      return this.normalizeSelectedPanelsBySegment(selectedFromCanvas);
    } catch (e) {
      return {};
    }
  };

  buildProductionJson = async (lat, lon, options = {}) => {
    const { segments, package: packageData, losses } = this.state;
    const onlySelectedPanels =
      typeof options.onlySelectedPanels === 'boolean'
        ? options.onlySelectedPanels
        : this.state.onlySelectedPanels;
    const includeHourlyProduction =
      typeof options.includeHourlyProduction === 'boolean'
        ? options.includeHourlyProduction
        : this.state.includeHourlyProduction;
    const persistSelectionState =
      options.persistSelectionState !== undefined
        ? options.persistSelectionState
        : true;
    const panelShadingOptions =
      options.waitForPanelShadingTextures === false
        ? { waitForTextures: false, maxTextureWaits: 0 }
        : {};
    const selectedPanelsBySegment = onlySelectedPanels
      ? this.getSelectedPanelsBySegment()
      : {};
    const selectedPanelIndexMap = [];
    const roofIndexMap = [];

    const parsedPackage =
      typeof packageData === 'string' ? JSON.parse(packageData) : packageData;

    let panelShadingData = [];
    if (this.canvas3DElement?.getAnualPanelsShadings) {
      try {
        panelShadingData =
          await this.canvas3DElement.getAnualPanelsShadings(
            panelShadingOptions
          );
      } catch (error) { }
    }

    const roofs = [];
    segments.forEach((segment, index) => {
      const segmentData =
        typeof segment === 'string' ? JSON.parse(segment) : segment;

      const panelData = this.canvas3DElement?.panelData?.[index];
      let panels = null;

      const segmentMonthlySA = normalizeMonthlySolarAccess(
        segmentData.solarAccess ?? segmentData.solar_access
      );
      const segmentMonthlyTOF = normalizeMonthlyLoss(segmentData.tof);
      const segmentMonthlyTSRF = normalizeMonthlyLoss(segmentData.tsrf);
      const segmentAnnualSA = finiteNumberOrDefault(
        segmentData.solarAccess?.[12] ?? segmentData.solar_access?.[12] ?? 1,
        1
      );
      const segmentAnnualTOF = finiteNumberOrDefault(segmentData.tof?.[12] ?? 0, 0);
      const segmentAnnualTSRF = finiteNumberOrDefault(segmentData.tsrf?.[12] ?? 0, 0);

      if (panelData) {
        const parsedPanelData =
          typeof panelData === 'string' ? JSON.parse(panelData) : panelData;

        const selectedIdxsRaw =
          selectedPanelsBySegment?.[index] ??
          selectedPanelsBySegment?.[String(index)] ??
          [];
        const selectedIdxs = this.normalizeSelectedPanelIndices(selectedIdxsRaw);
        const selectedSet = onlySelectedPanels ? new Set(selectedIdxs) : null;

        const realPanels = parsedPanelData.realPanels;
        const hasRealPanels =
          (Array.isArray(realPanels) && realPanels.length > 0) ||
          (!!realPanels && typeof realPanels === 'object');
        const panelIndicesInPayload = (parsedPanelData.points3D || [])
          .map((_, pi) => pi)
          .filter((pi) => (!hasRealPanels ? true : !!realPanels?.[pi]))
          .filter((pi) => (!onlySelectedPanels ? true : selectedSet?.has(pi)));
        if (panelIndicesInPayload.length === 0) return;
        const payloadRoofIdx = roofs.length;
        roofIndexMap.push(index);
        if (onlySelectedPanels) selectedPanelIndexMap[payloadRoofIdx] = panelIndicesInPayload;

        const geometries =
          panelIndicesInPayload
            .map((pi) => parsedPanelData.points3D?.[pi])
            .filter(Boolean)
            .map((panel) =>
              panel.map((point) => [point.x, point.y, point.z])
            ) || [];

        const panelCount = panelIndicesInPayload.length || geometries.length;

        const panelsByIndex = buildPanelsByIndexFromPanelShadingData({
          panelShadingData,
          segmentIndex: index,
        });

        const {
          monthly_solar_access,
          monthly_tof,
          monthly_tsrf,
          annual_solar_access,
          annual_tof,
          annual_tsrf,
        } = buildPerPanelShadingArrays({
          panelIndicesInPayload,
          panelCount,
          panelsByIndex,
          segmentMonthlySA,
          segmentMonthlyTOF,
          segmentMonthlyTSRF,
          segmentAnnualSA,
          segmentAnnualTOF,
          segmentAnnualTSRF,
        });

        panels = {
          width: parsedPackage.panel?.width || 1134,
          length: parsedPackage.panel?.length || 1722,
          panel_count: panelCount,
          geometries: geometries,
          monthly_solar_access,
          monthly_tof,
          monthly_tsrf,
          annual_solar_access,
          annual_tof,
          annual_tsrf,
        };
      }

      if (!panels || !panels.panel_count) return;

      roofs.push({
        id: segmentData.id || index.toString(),
        pitch: segmentData.pitch || 0,
        azimuth: segmentData.azimuth || 0,
        monthly_solar_access: segmentMonthlySA,
        monthly_tof: segmentMonthlyTOF,
        monthly_tsrf: segmentMonthlyTSRF,
        monthly_poa: segmentData.poaMonthly || [],
        annual_solar_access: segmentAnnualSA,
        annual_tof: segmentAnnualTOF,
        annual_tsrf: segmentAnnualTSRF,
        annual_poa: segmentData.poaMonthly?.reduce((a, b) => a + b, 0) || 0,
        panels: panels,
      });
    });

    try {
      const debugFlag =
        typeof window !== 'undefined' &&
        window?.localStorage?.getItem('SOLAR_PROD_DEBUG') === '1';
      if (debugFlag) {
        const roofSummaries = roofs.map((r, i) => {
          const msa = r?.panels?.monthly_solar_access || [];
          const flat = Array.isArray(msa) ? msa.flat() : [];
          const nums = flat.map((v) => Number(v)).filter(Number.isFinite);
          const min = nums.length ? Math.min(...nums) : null;
          const max = nums.length ? Math.max(...nums) : null;
          return {
            roof: i,
            panels: r?.panels?.panel_count || 0,
            saMin: min,
            saMax: max,
          };
        });
        console.log(
          '[solar-production payload] roof solar access stats',
          roofSummaries
        );
      }
    } catch (e) { }

    if (persistSelectionState) {
      if (onlySelectedPanels) {
        this.setState({
          stringingSelectedPanelIndexMap: selectedPanelIndexMap,
          stringingRoofIndexMap: roofIndexMap,
        });
      } else {
        this.setState({
          stringingSelectedPanelIndexMap: null,
          stringingRoofIndexMap: roofIndexMap,
        });
      }
    }
    this.lastSelectedPanelIndexMapForPayload = onlySelectedPanels
      ? selectedPanelIndexMap
      : null;
    this.lastRoofIndexMapForPayload = roofIndexMap;

    // Build inverter_hardware array with max_count for each inverter
    const inverterHardwareList = [];
    const { primaryInverterAutoQuantity } = this.state;

    const mapInverterType = (value) => {
      if (value == null) return 'C';
      const raw = String(value).trim();
      const upper = raw.toUpperCase();
      if (upper === 'C' || upper === 'M' || upper === 'P') return upper;
      if (upper === 'CENTRAL') return 'C';
      if (upper === 'MICRO') return 'M';
      if (upper === 'POWEROPTIMIZER' || upper === 'POWER OPTIMIZER') return 'P';
      return 'C';
    };

    // Add primary inverter - only set max_count if not in auto mode
    if (parsedPackage.inverter) {
      const isMicro =
        parsedPackage.inverter.type === 'M' ||
        parsedPackage.inverter.type === 'Micro';
      const primaryInverterData = {
        id: parsedPackage.inverter.id || '',
        model: parsedPackage.inverter.model || '',
        type: mapInverterType(parsedPackage.inverter.type),
        capacity: parsedPackage.inverter.capacity || '6000.0',
        efficiency: parsedPackage.inverter.efficiency || '96.00',
        max_dc_voltage: parsedPackage.inverter.max_dc_voltage || '60.00',
        max_dc_current: parsedPackage.inverter.max_dc_current || '12.00',
        min_mppt_voltage: parsedPackage.inverter.min_mppt_voltage || '22.00',
        mppt_count_per_inverter:
          parsedPackage.inverter.mppt_count_per_inverter || 1,
        strings_per_mppt_max: parsedPackage.inverter.strings_per_mppt_max || 2,
        manufacturer_id: parsedPackage.inverter.manufacturer?.name || 'Unknown',
      };
      // Only set max_count if not in auto mode
      if (!isMicro && !primaryInverterAutoQuantity) {
        primaryInverterData.exact_count = parsedPackage.inverterQuantity || 1;
        primaryInverterData.max_count = parsedPackage.inverterQuantity || 1;
      }
      inverterHardwareList.push(primaryInverterData);
    }

    // Add additional inverters - only set max_count if not in auto mode (autoQuantity !== false)
    const primaryIsMicro =
      parsedPackage?.inverter?.type === 'M' ||
      parsedPackage?.inverter?.type === 'Micro';
    const additionalInverters = primaryIsMicro
      ? []
      : parsedPackage.additional_inverters || [];
    const { inverters: allInverters } = this.state;
    const { iframe } = this.props;
    const { newEndpoint } = this.context;
    const companyInvertersAPI =
      iframe && newEndpoint ? newEndpoint('inverters/company/') : null;

    for (const additionalInv of additionalInverters) {
      const additionalId =
        additionalInv?.id ??
        additionalInv?.inverter_id ??
        additionalInv?.inverterId ??
        additionalInv?.companyInverterID ??
        additionalInv?.companyInverterId;
      if (!additionalId) {
        continue;
      }

      // Prefer already-loaded hardware; fallback to company lookup for iframe runs.
      let resolvedInverterData = allInverters?.find(
        (inv) => String(inv.id) === String(additionalId)
      );
      if (!resolvedInverterData && companyInvertersAPI) {
        try {
          resolvedInverterData = await companyInvertersAPI.retrieve(additionalId);
        } catch (err) {
          console.log(`iframe additional inverter lookup error: ${err}`);
        }
      }

      const source = resolvedInverterData || additionalInv;
      const isMicro = source.type === 'M' || source.type === 'Micro';
      const additionalInverterData = {
        id: source.id || additionalId || '',
        model: source.model || '',
        type: mapInverterType(source.type),
        capacity: source.capacity || '6000.0',
        efficiency: source.efficiency || '96.00',
        max_dc_voltage: source.max_dc_voltage || '60.00',
        max_dc_current: source.max_dc_current || '12.00',
        min_mppt_voltage: source.min_mppt_voltage || '22.00',
        mppt_count_per_inverter: source.mppt_count_per_inverter || 1,
        strings_per_mppt_max: source.strings_per_mppt_max || 2,
        manufacturer_id: source.manufacturer?.name || 'Unknown',
      };

      // Only set max_count if autoQuantity is explicitly false (not auto mode)
      const additionalAutoQuantity =
        additionalInv?.autoQuantity ?? additionalInv?.auto_quantity;
      const additionalQuantityRaw = Number(
        additionalInv?.quantity ??
          additionalInv?.max_count ??
          additionalInv?.maxCount ??
          1
      );
      const additionalQuantity =
        Number.isFinite(additionalQuantityRaw) && additionalQuantityRaw > 0
          ? additionalQuantityRaw
          : 1;
      if (!isMicro && additionalAutoQuantity === false) {
        additionalInverterData.exact_count = additionalQuantity;
        additionalInverterData.max_count = additionalQuantity;
      }
      inverterHardwareList.push(additionalInverterData);
    }

    const pickPositiveNumber = (...values) => {
      for (const value of values) {
        const num = Number(value);
        if (Number.isFinite(num) && num > 0) {
          return num;
        }
      }
      return null;
    };
    const wiringDefaults = { ohm_per_m_string: 0.004, loop_factor: 2 };
    const wiringConfig = this.state.wiring || wiringDefaults;
    const primaryInverterWiring = parsedPackage?.inverter || {};
    const wiringMatchesDefaults =
      Number(wiringConfig.ohm_per_m_string) === wiringDefaults.ohm_per_m_string &&
      Number(wiringConfig.loop_factor) === wiringDefaults.loop_factor;
    const preferStateWiring =
      this.state.wiringConfigured || !wiringMatchesDefaults;
    const wiringOhmPerM = preferStateWiring
      ? pickPositiveNumber(
          wiringConfig.ohm_per_m_string,
          primaryInverterWiring.ohm_per_m_string
        )
      : pickPositiveNumber(
          primaryInverterWiring.ohm_per_m_string,
          wiringConfig.ohm_per_m_string
        );
    const wiringLoopFactor = preferStateWiring
      ? pickPositiveNumber(
          wiringConfig.loop_factor,
          primaryInverterWiring.loop_factor
        )
      : pickPositiveNumber(
          primaryInverterWiring.loop_factor,
          wiringConfig.loop_factor
        );

    const powerOptimizerConfig =
      this.state.powerOptimizer || parsedPackage?.powerOptimizer;
    const normalizedPowerOptimizerConfig =
      this.normalizePowerOptimizerPayload(powerOptimizerConfig);
    const powerOptimizerPayload =
      normalizedPowerOptimizerConfig &&
        typeof normalizedPowerOptimizerConfig === 'object' &&
        Object.keys(normalizedPowerOptimizerConfig).length > 0
        ? normalizedPowerOptimizerConfig
        : {
          id: 'default',
          company_id: 'default',
          status: 'active',
          display_name: 'Default Power Optimizer',
          manufacturer: 'Default',
          Efficiency: 99.5,
          Voc_Max: 60,
          Isc_Max: 15,
        };

    const productionJson = {
      lat,
      long: lon,
      roofs: roofs,
      // List of inverter hardware with max_count for each
      inverter_hardware: inverterHardwareList,
      power_optimizer: powerOptimizerPayload,
      panel_hardware: {
        id: parsedPackage.panel?.id || '',
        model: parsedPackage.panel?.model || '',
        name: parsedPackage.panel?.model || '',
        series: parsedPackage.panel?.series || '',
        power: parsedPackage.panel?.power || 405,
        length: parsedPackage.panel?.length || 1722,
        width: parsedPackage.panel?.width || 1134,
        degradation: parsedPackage.panel?.degradation || '0.25',
        first_year_degradation:
          parsedPackage.panel?.first_year_degradation || '2.00',
        efficiency: parsedPackage.panel?.efficiency || '20.74',
        has_micro: parsedPackage.panel?.has_micro || false,
        v_oc: parsedPackage.panel?.v_oc || '37.23',
        temp_coeff_v_oc: parsedPackage.panel?.temp_coeff_v_oc || '-0.102',
        v_mpp: parsedPackage.panel?.v_mpp || '31.21',
        temp_coeff_p_mpp: parsedPackage.panel?.temp_coeff_p_mpp || '-0.350',
        i_sc: parsedPackage.panel?.i_sc || '13.87',
        temp_coeff_i_sc: parsedPackage.panel?.temp_coeff_i_sc || '0.040',
        installation_method:
          parsedPackage.panel?.installation_method || 'Rack-Mounted',
        manufacturer_id: parsedPackage.panel?.manufacturer?.name || 'Unknown',
      },
      wiring: {
        ohm_per_m_string:
          wiringOhmPerM !== null ? wiringOhmPerM : wiringDefaults.ohm_per_m_string,
        loop_factor:
          wiringLoopFactor !== null ? wiringLoopFactor : wiringDefaults.loop_factor,
      },
      // Use actual losses from state (set via System Losses button in Shading Analysis menu)
      loss: buildLossPayload(losses, { Availability: 1 }),
    };

    if (includeHourlyProduction) {
      productionJson.return_hourly_production = true;
    }

    return productionJson;
  };

  normalizeProductionLosses = (losses = {}) => {
    if (!losses || typeof losses !== 'object') {
      return null;
    }
    const keyMap = {
      soiling: 'soiling',
      shading: 'shading',
      snow: 'snow',
      mismatch: 'mismatch',
      wiring: 'wiring',
      connections: 'connections',
      lid: 'lid',
      nameplate_rating: 'nameplate_rating',
      nameplateRating: 'nameplate_rating',
      age: 'age',
      availability: 'availability',
    };
    const normalized = {};
    Object.keys(keyMap).forEach((key) => {
      if (losses[key] === undefined || losses[key] === null) {
        return;
      }
      const value = Number(losses[key]);
      if (Number.isFinite(value)) {
        normalized[keyMap[key]] = value;
      }
    });
    return Object.keys(normalized).length ? normalized : null;
  };

  normalizePowerOptimizerPayload = (optimizer = null) => {
    if (!optimizer || typeof optimizer !== 'object') {
      return null;
    }

    const pick = (...keys) => {
      for (const key of keys) {
        if (!key) continue;
        const value = optimizer[key];
        if (value === undefined || value === null || value === '') continue;
        return value;
      }
      return undefined;
    };

    const numberOrNull = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    const formatRange = (minValue, maxValue) => {
      if (minValue === null && maxValue === null) {
        return null;
      }
      if (minValue === null) {
        return String(maxValue);
      }
      if (maxValue === null) {
        return String(minValue);
      }
      return `${minValue}-${maxValue}`;
    };

    const mpptMin = numberOrNull(
      pick('mppt_voltage_min', 'mppt_voltageMin', 'mppt_v_min')
    );
    const mpptMax = numberOrNull(
      pick('mppt_voltage_max', 'mppt_voltageMax', 'mppt_v_max')
    );
    const tempMin = numberOrNull(pick('oper_temp_min_c', 'operTempMinC'));
    const tempMax = numberOrNull(pick('oper_temp_max_c', 'operTempMaxC'));

    const manufacturer = (() => {
      const raw = pick('manufacturer');
      if (raw && typeof raw === 'object') {
        return raw?.name || raw?.id || null;
      }
      return raw;
    })();

    const normalized = {
      id: pick('id'),
      company_id: pick('company_id', 'company'),
      status: pick('status'),
      display_name: pick('display_name', 'displayName', 'model', 'name'),
      manufacturer,
      efficiency: numberOrNull(pick('efficiency', 'Efficiency')),
      Voc_max: numberOrNull(pick('Voc_max', 'Voc_Max', 'voc_max')),
      Isc_max: numberOrNull(pick('Isc_max', 'Isc_Max', 'isc_max')),
      efficiency_weighted: numberOrNull(
        pick('efficiency_weighted', 'Efficiency_Weighted')
      ),
      rated_power: numberOrNull(pick('rated_power', 'Rated_Power')),
      mppt_voltage_range:
        pick('mppt_voltage_range', 'MPPT_Voltage_Range') ||
        formatRange(mpptMin, mpptMax),
      start_voltage: numberOrNull(pick('start_voltage', 'Start_Voltage')),
      operating_temperature_range:
        pick('operating_temperature_range', 'Operating_Temperature_Range') ||
        formatRange(tempMin, tempMax),
      Vout_nom: numberOrNull(pick('Vout_nom', 'vout_nom')),
      Vout_min: numberOrNull(pick('Vout_min', 'vout_min')),
      Vout_max: numberOrNull(pick('Vout_max', 'vout_max')),
      Iout_max: numberOrNull(pick('Iout_max', 'iout_max')),
    };

    // Keep payload minimal for solar-production request validation.
    const pruned = {};
    Object.keys(normalized).forEach((key) => {
      const value = normalized[key];
      if (value === undefined || value === null || value === '') return;
      pruned[key] = value;
    });

    return Object.keys(pruned).length ? pruned : null;
  };

  applyProductionSettings = async (settings = {}) => {
    if (!settings || typeof settings !== 'object') {
      return;
    }

    const updates = {};
    const canvasUpdates = {};
    const packageUpdates = {};
    const currentPackage = this.state.package || {};
    const allInverters = this.state.inverters || [];
    const allPanels = this.state.solarPanels || [];
    const { iframe } = this.props;
    const { newEndpoint } = this.context;

    const normalizedLosses = this.normalizeProductionLosses(settings.losses);
    if (normalizedLosses) {
      updates.losses = { ...(this.state.losses || {}), ...normalizedLosses };
      canvasUpdates.losses = updates.losses;
    }

    const rawOptimizerId =
      settings.companyPowerOptimizerID ??
      settings.companyPowerOptimizerId ??
      settings.powerOptimizerID ??
      settings.powerOptimizerId;
    const directPowerOptimizer =
      settings.powerOptimizer && typeof settings.powerOptimizer === 'object'
        ? settings.powerOptimizer
        : settings.power_optimizer && typeof settings.power_optimizer === 'object'
        ? settings.power_optimizer
        : null;
    if (rawOptimizerId === null && !directPowerOptimizer) {
      updates.powerOptimizer = null;
    } else {
      let resolvedOptimizer = null;

      if (rawOptimizerId !== undefined && rawOptimizerId !== null && iframe && newEndpoint) {
        try {
          const companyOptimizersAPI = newEndpoint('power_optimizers/company/');
          const fetched = await companyOptimizersAPI.retrieve(rawOptimizerId);
          resolvedOptimizer = this.normalizePowerOptimizerPayload(fetched);
        } catch (err) {
          console.log(`iframe power optimizer lookup error: ${err}`);
        }
      }

      if (directPowerOptimizer) {
        resolvedOptimizer =
          this.normalizePowerOptimizerPayload(directPowerOptimizer) ||
          resolvedOptimizer;
      }

      if (resolvedOptimizer) {
        updates.powerOptimizer = resolvedOptimizer;
      }
    }

    const rawPanelId =
      settings.companyPanelID ??
      settings.companyPanelId ??
      settings.panelID ??
      settings.panelId;
    const directPanel =
      settings.panelType && typeof settings.panelType === 'object'
        ? settings.panelType
        : settings.panel && typeof settings.panel === 'object'
        ? settings.panel
        : null;
    let resolvedPanel = null;
    let panelFromCompanyId = false;
    if (rawPanelId !== undefined && rawPanelId !== null) {
      resolvedPanel = allPanels.find(
        (panel) => String(panel.id) === String(rawPanelId)
      );
      if (!resolvedPanel && iframe && newEndpoint) {
        try {
          const companyPanelsAPI = newEndpoint('panels/company/');
          resolvedPanel = await companyPanelsAPI.retrieve(rawPanelId);
        } catch (err) {
          console.log(`iframe panel lookup error: ${err}`);
          throw new Error(`Panel with id ${rawPanelId} not found`);
        }
      }
      if (resolvedPanel) panelFromCompanyId = true;
    }
    // Registered hardware (company id) is canonical: only treat a panel object as a raw
    // spec when no real company id resolved for this slot.
    const panelFromSpec = Boolean(directPanel) && !panelFromCompanyId;
    if (panelFromSpec) {
      resolvedPanel = buildEffectiveInlineHardwareSpec(
        currentPackage.panel,
        directPanel
      );
    }
    if (resolvedPanel) {
      packageUpdates.panel = resolvedPanel;
      if (panelFromSpec) {
        // Inline spec (no registered hardware row): persist the spec and clear the id,
        // otherwise the merged object's inherited default id would resolve to the default on reload.
        packageUpdates.selectedPanelSpec = resolvedPanel;
        canvasUpdates.selected_panel_spec = resolvedPanel;
        canvasUpdates.selected_panel_id = null;
      } else {
        // Registered hardware: persist the id and clear any prior spec.
        packageUpdates.selectedPanelSpec = null;
        canvasUpdates.selected_panel_id = resolvedPanel?.id || null;
        canvasUpdates.selected_panel_spec = null;
      }
    }

    const rawInverterId =
      settings.companyInverterID ??
      settings.companyInverterId ??
      settings.primaryInverterID ??
      settings.primaryInverterId ??
      settings.inverterID ??
      settings.inverterId;
    const directInverter =
      settings.inverter && typeof settings.inverter === 'object'
        ? settings.inverter
        : null;
    let nextInverter = currentPackage.inverter || null;
    let inverterFromCompanyId = false;
    if (rawInverterId !== undefined && rawInverterId !== null) {
      const match = allInverters.find(
        (inv) => String(inv.id) === String(rawInverterId)
      );
      if (match) {
        nextInverter = match;
        packageUpdates.inverter = match;
        packageUpdates.selectedInverterSpec = null;
        canvasUpdates.selected_inverter_id = match.id;
        canvasUpdates.selected_inverter_spec = null;
        inverterFromCompanyId = true;
      } else if (iframe && newEndpoint) {
        try {
          const companyInvertersAPI = newEndpoint('inverters/company/');
          const fetched = await companyInvertersAPI.retrieve(rawInverterId);
          if (fetched) {
            nextInverter = fetched;
            packageUpdates.inverter = fetched;
            packageUpdates.selectedInverterSpec = null;
            canvasUpdates.selected_inverter_id = fetched?.id || null;
            canvasUpdates.selected_inverter_spec = null;
            inverterFromCompanyId = true;
          }
        } catch (err) {
          console.log(`iframe inverter lookup error: ${err}`);
          throw new Error(`Inverter with id ${rawInverterId} not found`);
        }
      }
    }
    // Registered hardware (company id) is canonical: only treat an inverter object as a raw
    // spec when no real company id resolved for this slot.
    if (directInverter && !inverterFromCompanyId) {
      // Inline spec (no registered hardware row): persist the spec and clear the id,
      // otherwise the merged object's inherited default id resolves to the default on reload.
      nextInverter = buildEffectiveInlineHardwareSpec(
        currentPackage.inverter,
        directInverter
      );
      packageUpdates.inverter = nextInverter;
      packageUpdates.selectedInverterSpec = nextInverter;
      canvasUpdates.selected_inverter_spec = nextInverter;
      canvasUpdates.selected_inverter_id = null;
    }

    const previousInverterId = currentPackage?.inverter?.id ?? null;
    const nextInverterId = nextInverter?.id ?? previousInverterId;
    const inverterChanged =
      previousInverterId &&
      nextInverterId &&
      String(previousInverterId) !== String(nextInverterId);

    const hasAutoFlag = typeof settings.primaryInverterAutoQuantity === 'boolean';
    const quantityValue = Number(settings.inverterQuantity);
    const hasQuantity = Number.isFinite(quantityValue);
    let primaryAuto = hasAutoFlag
      ? settings.primaryInverterAutoQuantity
      : this.state.primaryInverterAutoQuantity;

    if (!hasAutoFlag && hasQuantity) {
      primaryAuto = false;
    }

    if (hasAutoFlag || hasQuantity) {
      updates.primaryInverterAutoQuantity = primaryAuto;
      updates.hasAutoCalculatedInverterQuantity = !primaryAuto;
      canvasUpdates.primary_inverter_auto_quantity = primaryAuto;
    }

    if (!primaryAuto && hasQuantity) {
      const nextQty = Math.max(1, quantityValue);
      packageUpdates.inverterQuantity = nextQty;
      canvasUpdates.inverter_quantity = nextQty;
    } else if (!primaryAuto && inverterChanged && !hasQuantity) {
      packageUpdates.inverterQuantity = 1;
      canvasUpdates.inverter_quantity = 1;
    }

    const additionalInput = Array.isArray(settings.additionalInverters)
      ? settings.additionalInverters
      : Array.isArray(settings.companyAdditionalInverterIDs)
        ? settings.companyAdditionalInverterIDs.map((entry) => {
            if (entry && typeof entry === 'object') {
              return entry;
            }
            return { id: entry };
          })
        : null;

    if (additionalInput) {
      const normalizedAdditional = additionalInput
        .map((entry) => {
          if (!entry || typeof entry !== 'object') {
            return null;
          }
          const id =
            entry.companyInverterID ??
            entry.companyInverterId ??
            entry.id;
          if (!id) {
            return null;
          }
          const idValue = Number(id);
          const normalizedId = Number.isFinite(idValue) ? idValue : id;
          const payload = { id: normalizedId };
          if (typeof entry.autoQuantity === 'boolean') {
            payload.autoQuantity = entry.autoQuantity;
          }
          const quantityNum = Number(entry.quantity);
          if (Number.isFinite(quantityNum)) {
            payload.quantity = quantityNum;
          }
          return payload;
        })
        .filter(Boolean);

      const primaryIsMicro =
        nextInverter?.type === 'M' || nextInverter?.type === 'Micro';
      const companyInvertersAPI =
        iframe && newEndpoint ? newEndpoint('inverters/company/') : null;
      const resolvedAdditional = await Promise.all(
        normalizedAdditional.map(async (entry) => {
          let inverterRecord = allInverters.find(
            (inv) => String(inv.id) === String(entry.id)
          );
          if (!inverterRecord && companyInvertersAPI) {
            try {
              inverterRecord = await companyInvertersAPI.retrieve(entry.id);
            } catch (err) {
              console.log(`iframe additional inverter lookup error: ${err}`);
              throw new Error(`Inverter with id ${entry.id} not found`);
            }
          }
          return { entry, inverterRecord };
        })
      );

      const filteredAdditional = primaryIsMicro
        ? []
        : resolvedAdditional
            .filter(({ entry, inverterRecord }) => {
              if (nextInverterId && String(entry.id) === String(nextInverterId)) {
                return false;
              }
              if (!inverterRecord) {
                // Keep unresolved entries; buildProductionJson can resolve them later.
                return true;
              }
              return (
                !nextInverter ||
                matchesPrimaryInverterType(nextInverter, inverterRecord)
              );
            })
            .map(({ entry, inverterRecord }) => ({
              ...entry,
              ...(inverterRecord
                ? {
                    model: inverterRecord.model,
                    type: inverterRecord.type,
                    capacity: inverterRecord.capacity,
                    efficiency: inverterRecord.efficiency,
                    max_dc_voltage: inverterRecord.max_dc_voltage,
                    max_dc_current: inverterRecord.max_dc_current,
                    min_mppt_voltage: inverterRecord.min_mppt_voltage,
                    mppt_count_per_inverter:
                      inverterRecord.mppt_count_per_inverter,
                    strings_per_mppt_max: inverterRecord.strings_per_mppt_max,
                    manufacturer: inverterRecord.manufacturer,
                  }
                : {}),
            }));

      packageUpdates.additional_inverters = filteredAdditional;
      canvasUpdates.additional_inverters = filteredAdditional;
    } else if (inverterChanged) {
      const primaryIsMicro =
        nextInverter?.type === 'M' || nextInverter?.type === 'Micro';
      const existingAdditional = currentPackage.additional_inverters || [];
      const filteredAdditional = primaryIsMicro
        ? []
        : existingAdditional.filter((entry) => {
          if (!entry?.id) {
            return false;
          }
          const invObj = allInverters.find(
            (inv) => String(inv.id) === String(entry.id)
          );
          if (!invObj) {
            return false;
          }
          return !nextInverter || matchesPrimaryInverterType(nextInverter, invObj);
        });
      if (primaryIsMicro || filteredAdditional.length !== existingAdditional.length) {
        packageUpdates.additional_inverters = filteredAdditional;
        canvasUpdates.additional_inverters = filteredAdditional;
      }
    }

    const rawTarget =
      settings.targetProduction ?? settings.targetProductionKwh;
    const targetValue = Number(rawTarget);
    const hasTarget = Number.isFinite(targetValue);
    const hasUseTargetFlag = typeof settings.useTargetProduction === 'boolean';

    if (hasTarget || hasUseTargetFlag) {
      const useTarget = hasUseTargetFlag
        ? settings.useTargetProduction
        : targetValue > 0;
      const nextTarget = useTarget
        ? hasTarget
          ? targetValue
          : this.state.targetProductionKwh
        : 0;
      updates.useTargetProduction = useTarget;
      updates.targetProductionKwh = nextTarget;
      canvasUpdates.use_target_production = useTarget;
      canvasUpdates.target_production_kwh =
        useTarget && nextTarget > 0 ? nextTarget : null;
    }

    const maxRatioValue = Number(settings.maxDcAcRatio);
    if (Number.isFinite(maxRatioValue)) {
      updates.maxDcAcRatio = maxRatioValue;
      canvasUpdates.max_dc_ac_ratio = maxRatioValue;
    }

    if (typeof settings.includeHourlyProduction === 'boolean') {
      updates.includeHourlyProduction = settings.includeHourlyProduction;
    }

    if (typeof settings.onlySelectedPanels === 'boolean') {
      updates.onlySelectedPanels = settings.onlySelectedPanels;
      canvasUpdates.only_selected_panels = settings.onlySelectedPanels;
    }

    if (Object.keys(packageUpdates).length) {
      updates.package = { ...currentPackage, ...packageUpdates };
    }

    if (!Object.keys(updates).length && !Object.keys(canvasUpdates).length) {
      return;
    }

    await new Promise((resolve) => {
      if (Object.keys(updates).length) {
        this.setStatex(updates, resolve);
      } else {
        resolve();
      }
    });

    if (Object.keys(canvasUpdates).length && this.context?.CanvasAPI?.saveCanvas) {
      this.context.CanvasAPI.saveCanvas(canvasUpdates);
    }
  };

  buildProductionPanelSelection = (panels, selectedPanelsByRoof = {}) => {
    const selectedObjectsPanels = {};
    let hasSelectedPanels = false;

    const nextPanels = Array.isArray(panels)
      ? panels.map((panel, segmentIndex) => {
          const horizontalPoints = Array.isArray(panel?.horizontal?.points)
            ? panel.horizontal.points
            : [];
          const verticalPoints = Array.isArray(panel?.vertical?.points)
            ? panel.vertical.points
            : [];
          const rawSelected =
            selectedPanelsByRoof?.[segmentIndex] ??
            selectedPanelsByRoof?.[String(segmentIndex)] ??
            [];
          const selectedSet = new Set();

          if (Array.isArray(rawSelected)) {
            rawSelected.forEach((rawIndex) => {
              const panelIndex = Number(rawIndex);
              if (
                Number.isInteger(panelIndex) &&
                panelIndex >= 0 &&
                panelIndex < horizontalPoints.length + verticalPoints.length
              ) {
                selectedSet.add(panelIndex);
              }
            });
          }

          const selectedList = Array.from(selectedSet).sort((a, b) => a - b);
          if (selectedList.length > 0) {
            selectedObjectsPanels[segmentIndex] = selectedList;
            hasSelectedPanels = true;
          }

          return {
            ...panel,
            horizontal: {
              ...panel?.horizontal,
              selected: horizontalPoints.map((_, panelIndex) =>
                selectedSet.has(panelIndex)
              ),
            },
            vertical: {
              ...panel?.vertical,
              selected: verticalPoints.map((_, panelIndex) =>
                selectedSet.has(panelIndex + horizontalPoints.length)
              ),
            },
          };
        })
      : panels;

    return {
      hasSelectedPanels,
      panels: nextPanels,
      selectedObjectsPanels,
    };
  };

  /**
   * Handle solar production calculation using the solar-production service.
   * If useTargetProduction is true and targetProductionKwh > 0, performs optimal panel selection.
   * Otherwise, calculates production for ALL panels (no optimization).
   */
  handleSolarProduction = async (options = {}) => {
    const { lead, displaySnack, CanvasAPI, solarProductionDesignAPI } =
      this.context;
    const { iframe, callback: iframeCallback } = this.props;
    const { useTargetProduction, targetProductionKwh, maxDcAcRatio } =
      this.state;
    const primaryAutoForPayload =
      typeof this.state.primaryInverterAutoQuantity === 'boolean'
        ? this.state.primaryInverterAutoQuantity
        : !this.state.hasAutoCalculatedInverterQuantity;
    const onlySelectedPanels =
      typeof options.onlySelectedPanels === 'boolean'
        ? options.onlySelectedPanels
        : this.state.onlySelectedPanels;
    const includeHourlyProduction =
      typeof options.includeHourlyProduction === 'boolean'
        ? options.includeHourlyProduction
        : this.state.includeHourlyProduction;
    const persistSelectionState =
      options.persistSelectionState !== undefined
        ? options.persistSelectionState
        : true;
    const throwOnError = Boolean(options.throwOnError);
    const emitProductionCallback = options.emitCallback !== false;

    if (this.state.productionIsRunning) {
      this.queueSolarProductionRun('production_requested_while_running', options);
      return;
    }
    this.setState({ productionIsRunning: true });

    try {
      if (onlySelectedPanels) {
        const selectedBySegment = this.getSelectedPanelsBySegment();
        const selectedCount = Object.values(selectedBySegment).reduce(
          (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
          0
        );
        if (selectedCount <= 0) {
          this.setState({ productionIsRunning: false });
          displaySnack({
            variant: 'warning',
            message:
              'No panels selected. Select panels or disable "Only Selected Panels".',
          });
          return;
        }
      }

      const lat = lead.property?.coordinates?.[0];
      const lon = lead.property?.coordinates?.[1];
      const nextMode = onlySelectedPanels ? 'selected' : 'all';
      this.setState({ stringingPanelIndexMode: 'all' });
      const payload = await this.buildProductionJson(lat, lon, {
        onlySelectedPanels,
        persistSelectionState,
        includeHourlyProduction,
        waitForPanelShadingTextures: options.waitForPanelShadingTextures,
      });
      payload.lead_uid = lead.uid;

      // Set target_production based on checkbox state
      // If checkbox is checked and target > 0, use target for optimal panel selection
      // Otherwise, set to null to use all panels
      payload.target_production =
        useTargetProduction && targetProductionKwh > 0
          ? targetProductionKwh
          : null;

      // Optional constraint: maximum DC/AC ratio (1-2). Pass through to solar-production.
      const maxRatioNum = Number(maxDcAcRatio);
      payload.dcac_target =
        Number.isFinite(maxRatioNum) && maxRatioNum >= 0.5 && maxRatioNum <= 2
          ? maxRatioNum
          : 1.5;
      const requestedPanelCount = countProductionPayloadPanels(payload);

      const result = await solarProductionDesignAPI.create(payload);

      if (!result.success) {
        throw new Error(
          result.message || 'Solar production calculation failed'
        );
      }

      // Update segments with per-roof data
      const { segments } = this.state;
      const roofIndexMap = Array.isArray(this.lastRoofIndexMapForPayload)
        ? this.lastRoofIndexMapForPayload
        : Array.isArray(this.state.stringingRoofIndexMap)
          ? this.state.stringingRoofIndexMap
          : [];
      const segIdxToRoofIdx = new Map();
      roofIndexMap.forEach((segIdx, roofIdx) => {
        const s = Number(segIdx);
        if (Number.isFinite(s)) segIdxToRoofIdx.set(s, roofIdx);
      });
      const mapPayloadRoofToSegment = (roofIdxRaw) => {
        const roofIdx = Number(roofIdxRaw);
        if (!Number.isFinite(roofIdx)) return null;
        const segIdx = roofIndexMap?.[roofIdx];
        const s = Number(segIdx);
        return Number.isFinite(s) ? s : roofIdx;
      };
      const getByRoof = (obj, roofIdx, fallbackVal) =>
        obj?.[roofIdx] ?? obj?.[String(roofIdx)] ?? fallbackVal;

      const updatedSegments = segments.map((seg, idx) => {
        const roofIdx = segIdxToRoofIdx.get(idx);
        if (roofIdx === undefined || roofIdx === null) return seg;

        const roofAnnualKwh = getByRoof(result.per_roof_annual_kwh, roofIdx, 0);
        const roofMonthlyAc = getByRoof(result.per_roof_monthly_ac, roofIdx, seg.acMonthly);
        const roofMonthlyPoa = getByRoof(result.per_roof_monthly_poa, roofIdx, seg.poaMonthly);
        const roofClippingKwh = getByRoof(result.per_roof_clipping_kwh, roofIdx, 0);
        // Get selected panels array for this roof (can be empty array if no panels selected)
        const selectedPanels = getByRoof(result.selected_panels_per_roof, roofIdx, null);
        const selectedPanelCountForRoof = selectedPanels?.length ?? null;

        return {
          ...seg,
          acMonthly: roofMonthlyAc,
          poaMonthly: roofMonthlyPoa,
          acAnnual: roofAnnualKwh,
          annual_kwh: roofAnnualKwh,
          clipping_kwh: roofClippingKwh,
          selected_panel_count: selectedPanelCountForRoof,
        };
      });

      // Get inverter count from API result if auto mode is enabled
      const { package: installationPackage, primaryInverterAutoQuantity } =
        this.state;
      const invertersUsed = result.solar_design?.[0]?.inverters_used || null;
      const hourlyProduction = (() => {
        const raw = result.production_results;
        if (!raw || typeof raw !== 'object') return null;
        const payload = {};
        const keys = [
          'times',
          'pac_total_W',
          'pac_inverters_W',
          'pdc_inverters_W',
          'pdc_mppt_W',
          'clipping_W',
          'true_clip_W',
        ];
        keys.forEach((key) => {
          if (raw[key] !== undefined) {
            payload[key] = raw[key];
          }
        });
        return Object.keys(payload).length ? payload : null;
      })();
      const { totalCount: numTotalPanels, perRoofCounts } = getPanelCounts({
        panelData: this.canvas3DElement?.panelData,
        panels: this.state.panels,
      });
      let updatedInverterQuantity = installationPackage.inverterQuantity;

      if (
        primaryInverterAutoQuantity &&
        invertersUsed &&
        Array.isArray(invertersUsed)
      ) {
        const primaryInverterData = findPrimaryInverterUsage(
          invertersUsed,
          installationPackage.inverter
        );
        if (primaryInverterData) {
          updatedInverterQuantity = primaryInverterData.count || 1;
        }
      }

      // Select panels used in stringing so they appear "selected" on the canvas.
      const roofIndexMapForPayload = Array.isArray(this.lastRoofIndexMapForPayload)
        ? this.lastRoofIndexMapForPayload
        : roofIndexMap;
      const panelIndexMapForPayload = Array.isArray(
        this.lastSelectedPanelIndexMapForPayload
      )
        ? this.lastSelectedPanelIndexMapForPayload
        : null;
      let strings = mapStringingDataToDesign(
        result.solar_design?.[0]?.strings || null,
        roofIndexMapForPayload,
        panelIndexMapForPayload
      );

      const selectedPanelsPerRoof = mapSelectedPanelsPerRoofToDesign(
        result.selected_panels_per_roof || {},
        roofIndexMapForPayload,
        panelIndexMapForPayload
      );
      const productionPanelSelection = this.buildProductionPanelSelection(
        this.state.panels,
        selectedPanelsPerRoof
      );
      const selectedPanelCount = resolveAppliedPanelCount({
        resultPanelCount: result.panel_count,
        selectedPanelsPerRoof,
        fallbackPanelCount: onlySelectedPanels ? requestedPanelCount : 0,
      });
      const productionPerPanel =
        selectedPanelCount > 0 ? result.annual_kwh / selectedPanelCount : 0;
      const systemSizeKw =
        result.system_size_kw !== undefined ? result.system_size_kw : null;
      const shouldWarnAboutFixedInverterCapacity =
        hasFixedInverterQuantity({
          primaryInverterAutoQuantity: primaryAutoForPayload,
          additionalInverters: installationPackage?.additional_inverters,
        }) && requestedPanelCount > selectedPanelCount;

      const perRoofAnnualKwh = mapRoofKeyedPayloadToDesign(
        result.per_roof_annual_kwh,
        roofIndexMapForPayload
      );
      const perRoofMonthlyAc = mapRoofKeyedPayloadToDesign(
        result.per_roof_monthly_ac,
        roofIndexMapForPayload
      );
      const perRoofMonthlyPoa = mapRoofKeyedPayloadToDesign(
        result.per_roof_monthly_poa,
        roofIndexMapForPayload
      );
      const perRoofClippingKwh = mapRoofKeyedPayloadToDesign(
        result.per_roof_clipping_kwh,
        roofIndexMapForPayload
      );

      if (strings && this.canvas3DElement?.selectPanelsFromStringing) {
        try {
          this.suppressSolarProductionFromSelection = true;
          this.canvas3DElement.selectPanelsFromStringing(strings, 'all');
        } catch (e) {
          // noop
        } finally {
          setTimeout(() => {
            this.suppressSolarProductionFromSelection = false;
          }, 400);
        }
      }

      if (
        (!Array.isArray(strings) || strings.length === 0) &&
        this.canvas3DElement?.selectPanelsFromStringing
      ) {
        if (selectedPanelsPerRoof && Object.keys(selectedPanelsPerRoof).length > 0) {
          const pairs = [];
          Object.keys(selectedPanelsPerRoof).forEach((roofKey) => {
            const segIdx = Number(roofKey);
            if (!Number.isFinite(segIdx)) return;
            const arr = Array.isArray(selectedPanelsPerRoof[roofKey])
              ? selectedPanelsPerRoof[roofKey]
              : [];
            arr.forEach((panelIdxRaw) => {
              const panelIdx = Number(panelIdxRaw);
              if (!Number.isFinite(panelIdx)) return;
              pairs.push([segIdx, panelIdx]);
            });
          });
          if (pairs.length > 0) {
            strings = [{ panels: pairs, draw: false }];
            try {
              this.suppressSolarProductionFromSelection = true;
              this.canvas3DElement.selectPanelsFromStringing(strings, 'all');
            } catch (e) { }
            setTimeout(() => {
              this.suppressSolarProductionFromSelection = false;
            }, 400);
          }
        }
      }

      const productionPerPanelRaw =
        result.production_per_panel ??
        result.productionPerPanel ??
        result.production_results?.production_per_panel ??
        [];
      const productionPerPanelData = this.normalizeProductionPerPanel({
        raw: productionPerPanelRaw,
        perRoofCounts,
        roofIndexMap: this.lastRoofIndexMapForPayload,
        panelIndexMap: Array.isArray(this.lastSelectedPanelIndexMapForPayload)
          ? this.lastSelectedPanelIndexMapForPayload
          : null,
      });
      const productionPerStringRaw =
        result.production_per_string ??
        result.productionPerString ??
        result.production_results?.production_per_string ??
        [];
      const productionPerStringData = Array.isArray(productionPerStringRaw)
        ? productionPerStringRaw
        : [];
      const selectedPanelsPerRoofForSnapshot = selectedPanelsPerRoof;
      this.lastProductionSnapshot = {
        productionPerPanelData,
        productionPerStringData,
        hourlyProduction,
        stringingData: strings || null,
        invertersUsed: invertersUsed || null,
        annualProduction: result.annual_kwh || 0,
        monthlyProduction: result.monthly_kwh || Array(12).fill(0),
        annualClippingKwh: result.annual_clipping_kwh || 0,
        monthlyClippingKwh: result.monthly_clipping_kwh || Array(12).fill(0),
        dcAcRatio: result.dc_ac_ratio ?? null,
        systemSizeKw:
          result.system_size_kw !== undefined
            ? result.system_size_kw
            : systemSizeKw,
        mpptCountTotal:
          result.mppt_count_total !== undefined
            ? result.mppt_count_total
            : null,
        selectedPanelCount,
        selectedPanelsPerRoof: selectedPanelsPerRoofForSnapshot,
        perRoofAnnualKwh,
        perRoofMonthlyAc,
        perRoofMonthlyPoa,
        perRoofClippingKwh,
        limitReached: shouldWarnAboutFixedInverterCapacity,
      };

      await new Promise((resolve) => {
        this.setState(
          {
            segments: updatedSegments,
            productionHandled: true,
            totalAnnualKwh: result.annual_kwh || 0,
            totalMonthlyKwh: result.monthly_kwh || Array(12).fill(0),
            totalClippingKwh: result.annual_clipping_kwh || 0,
            totalMonthlyClippingKwh:
              result.monthly_clipping_kwh || Array(12).fill(0),
            dcAcRatio: result.dc_ac_ratio,
            stringingData: strings || null,
            invertersUsed: invertersUsed,
            productionPerPanel,
            productionPerPanelData,
            productionPerStringData,
            hourlyProduction,
            productionHardware: {
              panel: installationPackage?.panel || null,
              inverter: installationPackage?.inverter || null,
              additionalInverters: installationPackage?.additional_inverters || [],
              inverterQuantity: updatedInverterQuantity,
            },
            selectedPanelCount,
            stringingPanelIndexMode: 'all',
            systemSizeKw,
            mpptCountTotal:
              result.mppt_count_total !== undefined
                ? result.mppt_count_total
                : null,
            productionIsRunning: false,
            // Update package with new inverter quantity
            productionSaveError: null,
            package: {
              ...installationPackage,
              inverterQuantity: updatedInverterQuantity,
            },
            ...(productionPanelSelection.hasSelectedPanels
              ? {
                  panels: productionPanelSelection.panels,
                  selectedPanels: true,
                  selectedObjects: {
                    ...this.state.selectedObjects,
                    panels: productionPanelSelection.selectedObjectsPanels,
                  },
                }
              : {}),
          },
          resolve
        );
      });

      // Persist to canvas via API - pass the calculated values directly
      const inverterConfig = {
        inverterQuantity: updatedInverterQuantity,
        primaryInverterAutoQuantity: primaryAutoForPayload,
        additionalInverters: installationPackage.additional_inverters || [],
        maxDcAcRatio,
        stringingDataOverride: strings || null,
      };

      if (persistSelectionState) {
        inverterConfig.onlySelectedPanels = onlySelectedPanels;
      }

      const persistProductionToCanvas = () =>
        this.saveProductionToCanvas(result, updatedSegments, inverterConfig, {
          productionPerPanel: productionPerPanelData,
          productionPerString: productionPerStringData,
          hourlyProduction,
          panels: productionPanelSelection.hasSelectedPanels
            ? productionPanelSelection.panels
            : this.state.panels,
        });

      if (iframe && iframeCallback) {
        const apiAnnualKwh = result.annual_kwh || 0;
        const apiMonthlyKwh = result.monthly_kwh || Array(12).fill(0);
        const productionPayload = buildProductionCallbackPayload({
          annualUsage: lead?.utility?.usage,
          annualKwh: apiAnnualKwh,
          monthlyKwh: apiMonthlyKwh,
          hourlyProduction,
          systemSizeKw:
            result.system_size_kw !== undefined &&
              result.system_size_kw !== null
              ? result.system_size_kw
              : systemSizeKw,
          numTotalPanels,
          selectedPanelCount: selectedPanelCount || 0,
          perRoofCounts,
          segments: updatedSegments,
          selectedPanelsPerRoof,
          productionPerPanel: productionPerPanelData,
          productionPerString: productionPerStringData,
          strings,
          invertersUsed,
          monthlyClippingKwh: result.monthly_clipping_kwh || Array(12).fill(0),
          clippingKwh: result.annual_clipping_kwh || 0,
          dcAcRatio: result.dc_ac_ratio,
          panelCount: selectedPanelCount || 0,
          mpptCountTotal:
            result.mppt_count_total !== undefined
              ? result.mppt_count_total
              : null,
          perRoofAnnualKwh,
          perRoofMonthlyAc,
          perRoofMonthlyPoa,
          perRoofClippingKwh,
          limitReached: shouldWarnAboutFixedInverterCapacity,
        });

        if (emitProductionCallback) {
          iframeCallback('Production', productionPayload);
        }

        // NOTE: SolarProduction callback is disabled for now. Keep for future reference.
        /*
        iframeCallback('SolarProduction', {
          success: true,
          annual_kwh: result.annual_kwh,
          monthly_kwh: result.monthly_kwh,
          clipping_kwh: result.annual_clipping_kwh,
          dc_ac_ratio: result.dc_ac_ratio,
          strings_count: result.solar_design?.[0]?.strings?.length || 0,
        });
        */
        if (emitProductionCallback && shouldWarnAboutFixedInverterCapacity) {
          iframeCallback(
            FIXED_INVERTER_PANEL_CAPACITY_CALLBACK,
            buildFixedInverterPanelCapacityNotification()
          );
        }
        const productionPersisted = await persistProductionToCanvas();
        if (!productionPersisted) {
          const message =
            'Production calculated, but saving it to the canvas failed. Recalculate before creating or sharing a proposal.';
          if (throwOnError) {
            throw new Error(message);
          }
          if (emitProductionCallback) {
            iframeCallback('Warning', {
              code: '62',
              message,
            });
          }
        }
      } else {
        const productionPersisted = await persistProductionToCanvas();

        if (!productionPersisted) {
          displaySnack({
            variant: 'warning',
            message:
              'Production calculated, but saving it to the canvas failed. Recalculate before creating or sharing a proposal.',
          });
        } else if (shouldWarnAboutFixedInverterCapacity) {
          displaySnack({
            variant: 'warning',
            message: `Production calculated: ${result.annual_kwh?.toFixed(
              0
            )} kWh/year\n${FIXED_INVERTER_PANEL_CAPACITY_MESSAGE}`,
          });
        } else {
          displaySnack({
            variant: 'success',
            message: `Production calculated: ${result.annual_kwh?.toFixed(
              0
            )} kWh/year`,
          });
        }
      }
    } catch (err) {
      this.setState({ productionIsRunning: false });

      if (throwOnError) {
        throw err;
      }

      if (iframe && iframeCallback) {
        iframeCallback('Error', {
          code: '60',
          message: `Solar production calculation failed: ${err.message}`,
        });
      } else {
        displaySnack({
          variant: 'danger',
          message: `Solar production calculation failed: ${err.message}`,
        });
      }

    } finally {
      this.flushQueuedSolarProductionRun();
    }
  };

  normalizeProductionPerPanel = ({
    raw,
    perRoofCounts,
    roofIndexMap,
    panelIndexMap,
  }) => {
    if (!Array.isArray(raw)) return [];
    const counts = Array.isArray(perRoofCounts) ? perRoofCounts : [];
    if (counts.length === 0) return raw;
    const output = counts.map((count, idx) => ({
      idx,
      roofPanels: Array(Number(count) || 0).fill(0),
    }));
    const roofMap = Array.isArray(roofIndexMap) ? roofIndexMap : null;
    raw.forEach((entry, rawIdx) => {
      const payloadRoofIdx = Number(entry?.idx ?? rawIdx);
      if (!Number.isFinite(payloadRoofIdx)) return;
      const segIdx =
        roofMap && Number.isFinite(Number(roofMap[payloadRoofIdx]))
          ? Number(roofMap[payloadRoofIdx])
          : payloadRoofIdx;
      const roofEntry = output[segIdx];
      if (!roofEntry || !Array.isArray(roofEntry.roofPanels)) return;
      const roofPanels = roofEntry.roofPanels;
      const values = Array.isArray(entry?.roofPanels) ? entry.roofPanels : [];
      const map = panelIndexMap?.[payloadRoofIdx];
      values.forEach((val, panelIdx) => {
        const targetIdx = Array.isArray(map) ? map[panelIdx] : panelIdx;
        const idxNum = Number(targetIdx);
        if (!Number.isFinite(idxNum)) return;
        if (idxNum < 0 || idxNum >= roofPanels.length) return;
        roofPanels[idxNum] = Number(val) || 0;
      });
    });
    return output;
  };

  setWeatherDatasetSelected = (value) => {
    // Clear the fetched-path when the selection changes so the previous
    // dataset's CSV isn't silently reused on the next shading run.
    this.setState({
      weatherDatasetSelected: value,
      weatherDatasetFetchedPath: null,
    });
  };

  handleWeatherDatasetFetched = (weatherDatasetPath) => {
    this.setState3x({ weatherDatasetFetchedPath: weatherDatasetPath });
  };

  /**
   * Save production data to canvas via API for persistence
   * @param {Object} result - API result from solar-production
   * @param {Array} updatedSegments - Updated segments with production data
   * @param {Object} inverterConfig - Inverter configuration to save
   */
  saveProductionToCanvas = async (
    result,
    updatedSegments,
    inverterConfig = {},
    productionData = {}
  ) => {
    const { CanvasAPI, uid } = this.context;

    try {
      const invertersUsed = result.solar_design?.[0]?.inverters_used || null;
      const hourlyProduction =
        productionData?.hourlyProduction ??
        (() => {
          const raw = result.production_results;
          if (!raw || typeof raw !== 'object') return null;
          const payload = {};
          const keys = [
            'times',
            'pac_total_W',
            'pac_inverters_W',
            'pdc_inverters_W',
            'pdc_mppt_W',
            'clipping_W',
            'true_clip_W',
          ];
          keys.forEach((key) => {
            if (raw[key] !== undefined) {
              payload[key] = raw[key];
            }
          });
          return Object.keys(payload).length ? payload : null;
        })();
      const productionPerPanel =
        productionData?.productionPerPanel ??
        result.production_per_panel ??
        result.productionPerPanel ??
        result.production_results?.production_per_panel ??
        null;
      const productionPerString =
        productionData?.productionPerString ??
        result.production_per_string ??
        result.productionPerString ??
        result.production_results?.production_per_string ??
        null;
      const panelsForSave = productionData?.panels ?? this.state.panels;

      // Prepare canvas-level production data
      const canvasData = {
        total_annual_kwh: result.annual_kwh || 0,
        total_monthly_kwh: (result.monthly_kwh || []).join(','),
        total_clipping_kwh: result.annual_clipping_kwh || 0,
        total_monthly_clipping_kwh: (result.monthly_clipping_kwh || []).join(
          ','
        ),
        dc_ac_ratio: result.dc_ac_ratio,
        system_size_kw:
          result.system_size_kw !== undefined ? result.system_size_kw : null,
        mppt_count_total:
          result.mppt_count_total !== undefined
            ? result.mppt_count_total
            : null,
        inverter_count_total: Array.isArray(invertersUsed)
          ? invertersUsed.reduce((sum, i) => sum + (i.count || 0), 0)
          : null,
        stringing_data:
          inverterConfig.stringingDataOverride !== undefined
            ? inverterConfig.stringingDataOverride
            : result.solar_design?.[0]?.strings || null,
        inverters_used: invertersUsed,
        production_per_panel: productionPerPanel,
        production_per_string: productionPerString,
        hourly_production: hourlyProduction,
        // Persist UI state for production controls
        max_dc_ac_ratio:
          inverterConfig.maxDcAcRatio !== undefined
            ? inverterConfig.maxDcAcRatio
            : this.state.maxDcAcRatio,
        only_selected_panels:
          inverterConfig.onlySelectedPanels !== undefined
            ? inverterConfig.onlySelectedPanels
            : this.state.onlySelectedPanels,
        // Save inverter configuration passed from caller
        inverter_quantity: inverterConfig.inverterQuantity,
        primary_inverter_auto_quantity:
          inverterConfig.primaryInverterAutoQuantity,
        additional_inverters: inverterConfig.additionalInverters || [],
      };
      // Persist hardware selection, keeping the raw-spec and the id/FK channels mutually
      // exclusive so an inline spec isn't shadowed by the default id it inherited via merge.
      const selectedPanelSpec = this.state.package?.selectedPanelSpec || null;
      if (selectedPanelSpec) {
        canvasData.selected_panel_spec = selectedPanelSpec;
        canvasData.selected_panel_id = null;
      } else {
        const selectedPanelId = this.state.package?.panel?.id;
        if (selectedPanelId !== undefined && selectedPanelId !== null) {
          canvasData.selected_panel_id = String(selectedPanelId);
        }
        canvasData.selected_panel_spec = null;
      }
      const selectedInverterSpec = this.state.package?.selectedInverterSpec || null;
      if (selectedInverterSpec) {
        canvasData.selected_inverter_spec = selectedInverterSpec;
        canvasData.selected_inverter_id = null;
      } else {
        const selectedInverterId = this.state.package?.inverter?.id;
        if (selectedInverterId !== undefined && selectedInverterId !== null) {
          canvasData.selected_inverter_id = String(selectedInverterId);
        }
        canvasData.selected_inverter_spec = null;
      }

      // CanvasAPI wrapper exposes saveCanvas/getCanvas (not update)
      // IMPORTANT: pass segments in DesignTool state shape (camelCase arrays),
      // because the CanvasAPI wrapper serializes them for the backend.
      await CanvasAPI.saveCanvas(
        {
          ...canvasData,
          segments: updatedSegments,
          panels: panelsForSave,
        },
        { strict: true }
      );
      this.setState({ productionSaveError: null });
      return true;
    } catch (err) {
      console.warn('[DesignTool.saveProductionToCanvas] failed', {
        leadId: uid,
        status: err?.response?.status,
        response: err?.response?.data ?? err?.message,
        canvasSave: err?.canvasSave,
      });
      this.setState({
        productionSaveError: err?.message || 'Production canvas save failed',
      });
      return false;
    }
  };

  runOptimizationHandler = (optimizePosition, serverSide = false) => {
    // const newSegments = clusterPoints([...this.state.segments]);
    const { mode } = this.state;
    if (mode !== 'select') {
      this.handleMode('select');
      setTimeout(() => {
        this.runOptimizationHandler(optimizePosition, serverSide);
      }, 200);
      return;
    }

    this.setState3x({
      optimizer1IsRunning: !optimizePosition,
      optimizer2IsRunning: optimizePosition,
    });
    if (!serverSide) {
      setTimeout(
        () =>
          this.canvas3DElement.runOptimizer(true, optimizePosition, serverSide),
        100
      );
    } else {
      const { optimizeAPI, uid } = this.context;
      optimizeAPI
        .create({ uid })
        .then((newSegments) => {
          this.setSegmentParameters(newSegments);
        })
        .finally(() =>
          this.setState3x({
            optimizer1IsRunning: false,
            optimizer2IsRunning: false,
          })
        );
    }
  };

  runEdgeDetectionHandler = (depthForEdgeDetection, serverSide = false) => {
    this.setState3x({ edgeDetectionIsRunning: true });
    if (!serverSide) {
      setTimeout(
        () =>
          this.canvas3DElement.edgeDetection(depthForEdgeDetection, serverSide),
        0
      );
    } else {
      const { edgeDetectionAPI, uid } = this.context;
      edgeDetectionAPI
        .create({ uid, byDSM: true })
        .then((newSegments) => {
          this.changeEdgeTypes(newSegments);
        })
        .finally(() => this.setState3x({ edgeDetectionIsRunning: false }));
    }
  };

  trackTask = (taskId) => {
    if (taskId === undefined) return;
    const { uid, displaySnack, getTaskStatus, CanvasAPI } = this.context;

    getTaskStatus(taskId).then(({ state }) => {
      console.log(taskId, state);
      if (state === 'STARTED') return;
      clearInterval(this.ai_interval);
      this.setState3x({ AIIsRunning: false });
      if (state === 'SUCCESS') {
        displaySnack({ variant: 'info', message: 'Your AI design is ready.' });
        CanvasAPI.getCanvas(uid).then((canvasData) => {
          this.setState2x({ ...canvasData });
        });
      } else {
        displaySnack({
          variant: 'danger',
          message: 'An error was encountered.',
        });
      }
    });
  };

  setUpdateRGBTextureIsRunning = (value) => {
    // value must be boolean
    this.setState({ updateRGBTextureIsRunning: value });
  };

  runAIHandler = (lead_image = null) => {
    const { lead, predictAPI, displaySnack } = this.context;

    const request = {
      uid: lead.uid,
      design_type: 'A',
      no_refinement: 'F',
      lead_image,
    };

    this.setState3x({ AIIsRunning: true });

    this.setState(
      {
        updateRGBTextureIsRunning: true,
      },
      () => {
        this.canvas3DElement.updateRGBTexture();
        this.forceUpdate();
      }
    );

    predictAPI
      .create(request)
      .then((data) => {
        console.log(data.task);
        if (data.task) {
          displaySnack({
            variant: 'info',
            message: 'Your AI Design is processing.',
          });
          this.ai_interval = setInterval(() => this.trackTask(data.task), 1500);
          console.log(this.ai_interval);
        }
      })
      .catch(() => {
        displaySnack({
          variant: 'danger',
          message: 'AI prediction not started.',
        });
      });
  };

  handleShading = async (serverSide = false, serverSideInfo = false, datasetPath = null) => {
    const { shadingStatus, weatherDatasetFetchedPath } = this.state;
    // Prefer the path passed directly by the caller (avoids React async state race)
    // over whatever is currently committed in state.
    const resolvedDatasetPath = datasetPath ?? weatherDatasetFetchedPath;
    const {
      lead: { property, uid },
    } = this.context;
    const {
      iframe,
      designFrame,
      companyName,
      callback: iframeCallback,
    } = this.props;
    if (serverSide) {
      this.setServerSideShadingIsRunning(true);
    }

    // wait for page/tab be visible
    const pageVisibility = !document.hidden;
    while (document.hidden) {
      console.log('wait for page to be visible');
      await new Promise((r) => setTimeout(r, 500));
    }

    if (!pageVisibility) await new Promise((r) => setTimeout(r, 1500));

    if (iframe || designFrame) {
      if (serverSide) {
        await this.canvas3DElement.startServerSideShading();
        if (shadingStatus < 0) {
          console.log('fallback: client-side shading');
          return await this.canvas3DElement.startShading();
        }
      } else {
        return await this.canvas3DElement.startShading();
      }
    } else {
      if (serverSide) {
        const { newEndpoint, user } = this.context;
        if (user.email.includes('@aerialytic')) {
          const shadingInputAPI = newEndpoint('api/shading_input/');
          shadingInputAPI.create({ uid }).then((res) => { });
          await this.canvas3DElement.startServerSideShading();
        } else {
          await this.canvas3DElement.startServerSideShading();
        }
      } else {
        // if (!serverSide) {
        setTimeout(
          () => this.canvas3DElement.startShading(0, null, serverSideInfo),
          250
        );
      }
    }
    // const { uid, shadingAPI, productionMapAPI, CanvasAPI } = this.context;
    // const request = this.exportCanvas();
    // return shadingAPI.create(request)
    //   .then((data) => {
    //     const { roofs, image: production, colorRange } = data;

    //     // update shade factor for each roof in response
    //     const segments = Array.from(this.state.segments);
    //     roofs.forEach(({ solarAccess }, index) => {
    //       // const access = solarAccess.reduce((a, b) => a + b, 0)/solarAccess.length;
    //       segments[index].solarAccess = solarAccess;
    //     });
    //     // save shading data to backend
    //     this.setStatex(
    //       { segments, colorRange, shade: true },
    //       () => CanvasAPI.saveCanvas({ segments })
    //     );
    //     productionMapAPI.update(uid, { production })
    //       .then(({ url }) => this.setStatex({ overlay: url }));
    //     return true;
    //   }).catch(() => false);
  };

  shadingValues = () => {
    // method that is used in iframe to pass back shading values
    const { iframe, callback: iframeCallback } = this.props;

    const { segments, panels } = this.state;
    const annualData = segments.map(({ id, solarAccess, tsrf, tof }) => {
      const panel = panels.find((pnl) => pnl.id === id);

      const annualTSRF = tsrf[12];
      const annualTOF = tof[12];
      const annualSolarAccess = solarAccess[12];

      return {
        id,
        annualSolarAccess,
        annualTSRF,
        annualTOF,
      };
    });

    if (iframeCallback) iframeCallback('ShadingValues', { data: annualData });
  };

  handleProposal = async (params = null, loadAfter = false, options = {}) => {
    /**
     * @param params: iframe params
     */

    // receive design data for creating a new proposal
    const { uid, refreshLead, proposalAPI, navigateTab, displaySnack } =
      this.context;
    const activeDesignId = this.getActiveDesignId();

    // set hardware from state.package if it exists
    const {
      segments,
      package: _package,
      panels,
      losses,
      annotations,
    } = this.state;
    const requireProduction = options?.requireProduction !== false;

    if (this.state.isCreatingProposal) return;
    this.setState({ isCreatingProposal: true });
    try {
      if (requireProduction && !this.state.productionHandled) {
        this.setState({ isCreatingProposal: false });
        if (displaySnack) {
          displaySnack({
            variant: 'warning',
            message: 'Please click "Calculate Production" before creating a proposal.',
          });
        }
        return;
      }
      if (requireProduction && this.state.productionSaveError) {
        this.setState({ isCreatingProposal: false });
        if (displaySnack) {
          displaySnack({
            variant: 'warning',
            message:
              'Production was calculated but not saved. Recalculate production before creating a proposal.',
          });
        }
        return;
      }
    } catch (e) { }

    const {
      iframe,
      designFrame,
      callback: iframeCallback,
      context: iframeContext = {},
    } = this.props;

    const desCompanyPanelId = params?.companyPanelID;
    const desCompanyInverterId = params?.companyInverterID;
    const desCompanyAdditionalInverterIds =
      params?.companyAdditionalInverterIDs;
    const additionalInverters =
      params?.additionalInverters ||
      this.state.package?.additional_inverters ||
      [];
    const primaryInverterAutoQuantityParam =
      typeof params?.primaryInverterAutoQuantity === 'boolean'
        ? params.primaryInverterAutoQuantity
        : this.state.primaryInverterAutoQuantity;
    const requestedInverterQuantity =
      params?.inverterQuantity ?? this.state.package?.inverterQuantity ?? 1;
    const productionSnapshot = this.lastProductionSnapshot;
    const inverterQuantity = resolveMicroInverterQuantity({
      inverter: this.state.package?.inverter,
      primaryInverterAutoQuantity: primaryInverterAutoQuantityParam,
      selectedPanelCount:
        productionSnapshot?.selectedPanelCount ?? this.state.selectedPanelCount,
      invertersUsed:
        productionSnapshot?.invertersUsed ?? this.state.invertersUsed,
      fallbackQuantity: requestedInverterQuantity,
    });

    if (iframeContext?.panelDefault === 'default') {
      const { panel: panelHardware } = _package;
      iframeContext.panelType = { ...panelHardware };
    }

    const effectiveLosses = buildEffectiveSystemLosses({
      baseLosses: losses,
      iframeLosses: params?.losses || iframeContext?.losses,
      lead: this.context.lead,
    });

    // console.log(`handleProposal: ${desCompanyPanelId}, ${desCompanyInverterId}`);
    const data = createProposalData(
      uid,
      this.state,
      panels,
      _package,
      iframeContext,
      desCompanyPanelId,
      desCompanyInverterId,
      designFrame,
      effectiveLosses,
      desCompanyAdditionalInverterIds,
      additionalInverters,
      inverterQuantity,
      // Legacy flag: true = manual qty, false = auto qty
      !primaryInverterAutoQuantityParam
    );
    if (data?.design_data) {
      data.design_data.losses = effectiveLosses;
    }
    if (productionSnapshot && data?.design_data) {
      const sortedSegments = Array.isArray(data?.design_data?.segments)
        ? data.design_data.segments
        : [];
      const originalSegments = Array.isArray(this.state.segments)
        ? this.state.segments
        : [];
      const sortedIndexById = new Map();
      sortedSegments.forEach((segment, idx) => {
        if (segment?.id !== undefined) {
          sortedIndexById.set(segment.id, idx);
        }
      });
      const mapRoofIndex = (origIdxRaw) => {
        const origIdx = Number(origIdxRaw);
        if (!Number.isFinite(origIdx)) return origIdxRaw;
        const segId = originalSegments?.[origIdx]?.id;
        if (segId !== undefined && sortedIndexById.has(segId)) {
          const mappedIdx = sortedIndexById.get(segId);
          return Number.isFinite(Number(mappedIdx)) ? Number(mappedIdx) : origIdx;
        }
        return origIdx;
      };
      const mapRoofKeyedObject = (obj) => {
        if (!obj || typeof obj !== 'object') return null;
        const mapped = Array.isArray(obj) ? [] : {};
        Object.keys(obj).forEach((key) => {
          const origIdx = Number(key);
          if (!Number.isFinite(origIdx)) return;
          const mappedIdx = mapRoofIndex(origIdx);
          if (Array.isArray(mapped)) {
            mapped[mappedIdx] = obj[key];
          } else {
            mapped[mappedIdx] = obj[key];
          }
        });
        return mapped;
      };
      const mapSelectedPanelsPerRoof = (selected) => {
        if (!selected || typeof selected !== 'object') return null;
        const mapped = {};
        Object.keys(selected).forEach((key) => {
          const origIdx = Number(key);
          if (!Number.isFinite(origIdx)) return;
          const mappedIdx = mapRoofIndex(origIdx);
          mapped[mappedIdx] = Array.isArray(selected[key])
            ? selected[key]
            : [];
        });
        return mapped;
      };
      const mapProductionPerPanel = (entries) => {
        if (!Array.isArray(entries)) return null;
        const mapped = entries
          .map((entry, idx) => {
            const origIdx = Number(
              entry?.idx !== undefined ? entry.idx : idx
            );
            const mappedIdx = mapRoofIndex(origIdx);
            return {
              ...entry,
              idx: mappedIdx,
            };
          })
          .filter((entry) => entry && Number.isFinite(Number(entry.idx)));
        return mapped.sort((a, b) => Number(a.idx) - Number(b.idx));
      };
      const mapStringingData = (strings) => {
        if (!Array.isArray(strings)) return strings;
        return strings.map((entry) => {
          const panelsRaw = Array.isArray(entry?.panels) ? entry.panels : [];
          const panels = panelsRaw
            .map((panel) => {
              if (!Array.isArray(panel) || panel.length < 2) return null;
              const mappedIdx = mapRoofIndex(panel[0]);
              return [mappedIdx, panel[1]];
            })
            .filter(Boolean);
          return {
            ...entry,
            panels,
          };
        });
      };
      const mappedProductionPerPanel = mapProductionPerPanel(
        productionSnapshot.productionPerPanelData
      );
      const mappedStringingData = mapStringingData(
        productionSnapshot.stringingData
      );
      const mappedSelectedPanelsPerRoof = mapSelectedPanelsPerRoof(
        productionSnapshot.selectedPanelsPerRoof
      );
      const mappedPerRoofAnnualKwh = mapRoofKeyedObject(
        productionSnapshot.perRoofAnnualKwh
      );
      const mappedPerRoofMonthlyAc = mapRoofKeyedObject(
        productionSnapshot.perRoofMonthlyAc
      );
      const mappedPerRoofMonthlyPoa = mapRoofKeyedObject(
        productionSnapshot.perRoofMonthlyPoa
      );
      const mappedPerRoofClippingKwh = mapRoofKeyedObject(
        productionSnapshot.perRoofClippingKwh
      );
      if (
        Array.isArray(mappedProductionPerPanel) &&
        data.design_data.productionPerPanel == null
      ) {
        data.design_data.productionPerPanel = mappedProductionPerPanel;
      }
      if (
        Array.isArray(productionSnapshot.productionPerStringData) &&
        data.design_data.productionPerString == null
      ) {
        data.design_data.productionPerString =
          productionSnapshot.productionPerStringData;
      }
      if (
        productionSnapshot.hourlyProduction != null &&
        data.design_data.hourlyProduction == null
      ) {
        data.design_data.hourlyProduction =
          productionSnapshot.hourlyProduction;
      }
      if (
        mappedStringingData &&
        data.design_data.stringing_data == null
      ) {
        data.design_data.stringing_data = mappedStringingData;
      }
      if (
        productionSnapshot.invertersUsed &&
        data.design_data.inverters_used == null
      ) {
        data.design_data.inverters_used = productionSnapshot.invertersUsed;
      }
      if (
        mappedSelectedPanelsPerRoof &&
        data.design_data.selectedPanelsPerRoof == null
      ) {
        data.design_data.selectedPanelsPerRoof = mappedSelectedPanelsPerRoof;
      }
      if (
        mappedPerRoofAnnualKwh &&
        data.design_data.perRoofAnnualKwh == null
      ) {
        data.design_data.perRoofAnnualKwh = mappedPerRoofAnnualKwh;
      }
      if (
        mappedPerRoofMonthlyAc &&
        data.design_data.perRoofMonthlyAc == null
      ) {
        data.design_data.perRoofMonthlyAc = mappedPerRoofMonthlyAc;
      }
      if (
        mappedPerRoofMonthlyPoa &&
        data.design_data.perRoofMonthlyPoa == null
      ) {
        data.design_data.perRoofMonthlyPoa = mappedPerRoofMonthlyPoa;
      }
      if (
        mappedPerRoofClippingKwh &&
        data.design_data.perRoofClippingKwh == null
      ) {
        data.design_data.perRoofClippingKwh = mappedPerRoofClippingKwh;
      }
      if (
        productionSnapshot.dcAcRatio != null &&
        data.design_data.dc_ac_ratio == null
      ) {
        data.design_data.dc_ac_ratio = productionSnapshot.dcAcRatio;
      }
      if (
        productionSnapshot.annualClippingKwh != null &&
        data.design_data.annual_clipping_kwh == null
      ) {
        data.design_data.annual_clipping_kwh =
          productionSnapshot.annualClippingKwh;
      }
      if (
        Array.isArray(productionSnapshot.monthlyClippingKwh) &&
        data.design_data.monthly_clipping_kwh == null
      ) {
        data.design_data.monthly_clipping_kwh =
          productionSnapshot.monthlyClippingKwh;
      }
      if (
        Array.isArray(productionSnapshot.monthlyProduction) &&
        data.design_data.monthlyProduction == null
      ) {
        data.design_data.monthlyProduction =
          productionSnapshot.monthlyProduction;
      }
      data.design_data.limitReached = Boolean(productionSnapshot.limitReached);
      data.design_data.limit_reached = Boolean(productionSnapshot.limitReached);
      if (productionSnapshot.annualProduction != null) {
        data.design_data.summary = {
          ...(data.design_data.summary || {}),
          solar_production: productionSnapshot.annualProduction,
          system_size:
            productionSnapshot.systemSizeKw != null
              ? productionSnapshot.systemSizeKw
              : data.design_data.summary?.system_size,
          total_panels:
            productionSnapshot.selectedPanelCount != null
              ? productionSnapshot.selectedPanelCount
              : data.design_data.summary?.total_panels,
        };
      }
    }

    // console.log(`handleProposal - iframe context: ${JSON.stringify(iframeContext)}\nhandleProposal - package: ${JSON.stringify(_package)}\nhandleProposal - data: ${JSON.stringify(data)}\n`)

    const stripDesignForLegacy = (d) => {
      if (!d || typeof d !== 'object') return d;
      const {
        id,
        azimuth,
        panel_orientation,
        horizontal,
        horizontal_size,
        horizontal_selected,
        vertical,
        vertical_size,
        vertical_selected,
        ac_monthly,
        solar_access,
        tsrf,
        tof,
      } = d;
      return {
        id,
        azimuth,
        panel_orientation,
        horizontal,
        horizontal_size,
        horizontal_selected,
        vertical,
        vertical_size,
        vertical_selected,
        ac_monthly,
        solar_access,
        tsrf,
        tof,
      };
    };

    const onCreated = ({ id }) => {
      refreshLead('proposals');
      if (!iframe) navigateTab(`proposals?id=${id}`);
      else if (iframeCallback) iframeCallback('NewProposal', id);
      if (loadAfter) {
        window.location.replace(`/iframe/${uid}?id=${id}`);
      }
      this.setState({ isCreatingProposal: false });
    };

    try {
      const proposal = await proposalAPI.create({
        ...data,
        version: CURRENT_PROPOSAL_VERSION,
        annotations,
        design_id: activeDesignId,
      });
      onCreated(proposal);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        const legacyData = {
          ...data,
          designs: Array.isArray(data?.designs)
            ? data.designs.map(stripDesignForLegacy)
            : data.designs,
        };
        try {
          const legacyProposal = await proposalAPI.create({
            ...legacyData,
            version: CURRENT_PROPOSAL_VERSION,
            annotations,
            design_id: activeDesignId,
          });
          onCreated(legacyProposal);
          return;
        } catch (legacyErr) {
          this.setState({ isCreatingProposal: false });
          if (iframe) throw legacyErr;
          return;
        }
      }
      this.setState({ isCreatingProposal: false });
      if (iframe) throw err;
    }
  };

  handleMode = (mode) => {
    const { currentAccordion, currentTab } = this.state;
    if (currentAccordion === 'segments' && currentTab === 'segment-labels') {
      this.setState3x({ mode, selectedLabel: '' });
    } else if (currentAccordion === 'annotations') {
      this.setState3x({ mode });
    } else {
      this.setState3x({ mode });
    }
  };

  handleMoveMode = (moveMode) => this.setState3x({ moveMode });

  handleCurrentAccordion = (currentAccordion) => {
    const prevAccordion = this.state.currentAccordion;
    let mode;
    let tool = 'Polygon';
    const { currentTab, losses } = this.state;
    const { iframe, designFrame, callback: iframeCallback } = this.props;

    // update mode, and tool based on opened accordion
    if (canvasKeys.includes(currentAccordion)) mode = 'draw';
    else mode = 'select';
    if (currentAccordion === 'segments' && currentTab === 'segment-labels')
      mode = 'label';
    if (currentAccordion === 'trees') tool = 'Circle';
    // if (currentAccordion === 'setback') this.updateSetbacks();

    if (iframe && !designFrame) {
      if (this.state.segments?.length === 0) {
        return false;
      }
    }

    const nextState = { currentAccordion, mode, tool, losses };
    if (currentAccordion === 'design') {
      nextState.selectionMode = 'segment';
      nextState.selectedObjects = {
        ...this.state.selectedObjects,
        panels: {},
      };
    }
    this.setState3x(nextState);

    if (currentAccordion === 'design' && prevAccordion === 'revise') {
      this.clearSelectedPanelsOnly();
    }

    if (currentAccordion === 'production') {
      const strings = this.state.stringingData;
      const inverterType = this.state.package?.inverter?.type;
      const isMicro = inverterType === 'M' || inverterType === 'Micro';
      const trySelect = () => {
        try {
          if (!(this.canvas3DElement && this.canvas3DElement.isLoaded))
            return false;
          if (!this.canvas3DElement?.selectPanelsFromStringing) return false;
          if (!Array.isArray(strings) || strings.length === 0) return false;
          this.canvas3DElement.selectPanelsFromStringing(strings, 'all');
          return true;
        } catch (e) {
          return false;
        }
      };

      if (!trySelect()) {
        let tries = 0;
        const retry = () => {
          tries += 1;
          if (tries > 8) return;
          if (trySelect()) return;
          setTimeout(retry, 100);
        };
        setTimeout(retry, 0);
      }

      if (!isMicro) {
      }
    }
    return true;
  };

  handleNewSegment = ({ geometry: geom, ...segment }) => {
    const [geometry] = filterDuplicates(geom);
    const newSegment = {
      id: canvasHandler.uuid(),
      azimuth: 0,
      pitch: 0,
      area: 0,
      height: 0,
      geometry,
      lines: Array(geometry.length).fill(''),
      setbacks: Array(geometry.length).fill(0),
      acAnnual: 0,
      acMonthly: Array(12).fill(0),
      poaMonthly: Array(12).fill(0),
      solarAccess: Array(13).fill(1),
      tsrf: Array(13).fill(1),
      tof: Array(13).fill(1),
      ...segment,
    };
    this.setStatex(
      ({ segments }) => ({ segments: [...segments, newSegment] }),
      null,
      ['segments']
    );
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
    return newSegment;
  };

  handleNewObstacle = (obstacle) => {
    const isLine = obstacle.shape === 'Line';
    const newObstacle = {
      height: isLine ? 0 : 12,
      radius: isLine ? 0 : 5,
      setback: isLine ? 25 : 0,
      ...obstacle,
    };
    const newObstacles = [...this.state.obstacles, newObstacle];
    this.setState3x({ obstacles: newObstacles });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  handleNewTree = (tree) => {
    const { resolution } = this.state;
    const newTree = {
      ...tree,
      model: 'Sphere',
      dsm: 'Keep',
      trunkHeight: 10,
      height:
        (
          parseFloat(
            distance(tree.geometry[0], tree.geometry[1]) * resolution
          ) / 30.48
        ).toFixed(2) * 2,
    };
    const newTrees = [...this.state.trees, newTree];
    this.setState3x({ trees: newTrees });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  handlePitchInDegreesChange = (value) => {
    const next = Boolean(value);
    this.setState({ pitch_in_degrees: next });
    this.context.CanvasAPI.saveCanvas({ pitch_in_degrees: next });
  };

  handleNewShape = (shape, scaled = true) => {
    // scale data back
    const { scale, currentAccordion } = this.state;
    const newShape = scaled ? scaleShape(shape, 1 / scale) : shape;

    // pass data to relevant handler
    switch (currentAccordion) {
      case 'segments':
        this.handleNewSegment(newShape);
        break;
      case 'obstacles':
        this.handleNewObstacle(newShape);
        break;
      case 'trees':
        this.handleNewTree(newShape);
        break;
      case 'annotations':
        if (newShape.objectType != '')
          this.newAnnotation({}, newShape.objectType, newShape);
      default:
        break;
    }
  };

  // manage annotation edits
  // TODO: setState by reference
  handleSaveEdits = (updated, scaled = true) => {
    // scale data back
    const { scale, currentAccordion: accordion } = this.state;
    const geom = scaled
      ? scaleShape(updated, 1 / scale, true)
      : updated.geometry;
    const [geometry, removed] = filterDuplicates(geom);

    // get current data based on accordion, and update the changes
    const { [accordion]: oldData } = this.state;
    const data = [...oldData];
    const index = data.map(({ id }) => id).indexOf(updated.id);

    // remove invalid geometries
    switch (accordion) {
      case 'segments':
        if (geometry.length < 3) return this.handleDeleteSegment(updated.id);
        data[index] = { ...data[index], geometry, panelAxis: null };

        // update line labels if any duplicate points were removed
        if (accordion === 'segments') {
          // && removed.length > 0) {
          let { lines } = data[index];
          lines = lines.filter((_, idx) => !removed.includes(idx));

          // if lines & geometry mismatch in length, create a new array with the correct length
          if (lines.length !== geometry.length)
            lines = Array(geometry.length).fill(null);
          data[index].lines = lines;
        }

        this.setStatex({ [accordion]: [...data] }, () => {
          if (accordion === 'segments') {
            this.getAzimuthFromLineLabel(index);
          }
        });
        // Use setTimeout to check after state update completes (only for segments)
        if (accordion === 'segments') {
          setTimeout(() => this.checkShadingRerunNeeded(), 0);
        }

        break;
      case 'obstacles':
        if (updated.shape === 'Line') {
          if (geometry.length < 2 || distance(geometry[0], geometry[1]) < 2)
            return;
          data[index] = { ...data[index], geometry: geometry.slice(0, 2) };
        } else if (updated.shape === 'Polygon') {
          if (geometry.length < 3) return this.handleDeleteObstacle(updated.id);
          data[index] = { ...data[index], geometry };
        }
        if (updated.shape === 'Point') {
          if (geometry.length !== 1)
            return this.handleDeleteObstacle(updated.id);
          data[index] = { ...data[index], geometry, radius: updated.radius };
        }
        this.setStatex({ [accordion]: [...data] });
        // Use setTimeout to check after state update completes
        setTimeout(() => this.checkShadingRerunNeeded(), 0);
        break;
      case 'trees':
        if (geometry.length !== 2) return this.handleDeleteTree(updated.id);
        data[index] = { ...data[index], geometry };
        this.setStatex({ [accordion]: [...data] });
        // Use setTimeout to check after state update completes
        setTimeout(() => this.checkShadingRerunNeeded(), 0);
        break;
      case 'annotations':
        if (geometry.length !== 2) annotations.splice(index, 1);
        else data[index] = { ...data[index], geometry };
        this.setAnnotations(annotations);
        break;
      default:
        break;
    }
  };

  handleAddPointToPolygon = (index, lineIndex, point, scaled = false) => {
    // scale back the data from draw canvas
    const { scale, currentAccordion: accordion } = this.state;
    const scaledPoint = !scaled
      ? [Math.round(point[0] / scale), Math.round(point[1] / scale)]
      : [Math.round(point[0]), Math.round(point[1])];
    if (['segments', 'obstacles'].includes(accordion)) {
      const { [accordion]: data } = this.state;
      // insert new point in polygon
      data[index].geometry.splice(lineIndex + 1, 0, scaledPoint);
      if (data[index].lines) data[index].lines.splice(lineIndex + 1, 0, null);
      if (accordion === 'segments') data[index].panelAxis = null;
      this.setState2x({ [accordion]: [...data], polyEdit: -1 });
      // Use setTimeout to check after state update completes
      setTimeout(() => this.checkShadingRerunNeeded(), 0);
    }
  };

  handleRemovePointFromPolygon = (index, pointIdxInGeometry) => {
    const { currentAccordion: accordion } = this.state;
    if (['segments', 'obstacles'].includes(accordion)) {
      const { [accordion]: data } = this.state;
      data[index].geometry.splice(pointIdxInGeometry, 1);
      if (data[index].lines) data[index].lines.splice(pointIdxInGeometry, 1);
      if (accordion === 'segments') data[index].panelAxis = null;
      if (data[index].geometry.length < 3) {
        data.splice(index, 1);
      }
      this.setState2x({ [accordion]: [...data], polyEdit: -1 });
      // Use setTimeout to check after state update completes
      setTimeout(() => this.checkShadingRerunNeeded(), 0);
    }
  };

  // TODO: clean up this method to reduce style guide conflicts
  // TODO: add option for labelling a line that has both points overlapped by another line
  handleLineLabel = (index, lineIndex, lines) => {
    const changedSegs = [];
    this.setStatex(
      ({ segments }) => {
        const segment = segments[index];
        segment.lines = lines;

        const op1 = segment.geometry[lineIndex];
        const op2 = segment.geometry[(lineIndex + 1) % segment.geometry.length];
        const overlappedOri = drawOrientation(segment.geometry, lineIndex);

        // label co-incident & overlapping lines
        if (lines[lineIndex] !== 'Eave') {
          segments.forEach((seg, j) => {
            if (j !== index) {
              const common = findCommonLine(segment.geometry, seg.geometry);
              if (common !== -1 && common.length === 2) {
                // console.log('entered');
                const li = Math.min(...common);
                const currentOri = drawOrientation(seg.geometry, li);
                if (
                  li === 0 &&
                  Math.max(...common) === seg.geometry.length - 1
                ) {
                  // console.log('ab', j);
                  const p1 = seg.geometry[Math.max(...common)];
                  const p2 =
                    seg.geometry[
                    (Math.max(...common) + 1) % seg.geometry.length
                    ];
                  // console.log(op1, p1, op2, p2);
                  if (overlappedOri === currentOri) {
                    if (isPointEqual(op1, p2) && isPointEqual(op2, p1)) {
                      // console.log('a1');
                      if (!seg.lines)
                        seg.lines = Array(seg.geometry.length).fill('');
                      seg.lines[Math.max(...common)] = lines[lineIndex];
                      if (!changedSegs.includes(j)) changedSegs.push(j);
                    }
                  } else if (isPointEqual(op1, p1) && isPointEqual(op2, p2)) {
                    if (!seg.lines)
                      seg.lines = Array(seg.geometry.length).fill('');
                    seg.lines[Math.max(...common)] = lines[lineIndex];
                    if (!changedSegs.includes(j)) changedSegs.push(j);
                  }
                } else {
                  // console.log('ac', j);
                  const p1 = seg.geometry[li];
                  const p2 = seg.geometry[(li + 1) % seg.geometry.length];
                  // console.log(op1, p1, op2, p2);
                  if (overlappedOri === currentOri) {
                    if (isPointEqual(op1, p2) && isPointEqual(op2, p1)) {
                      if (!seg.lines)
                        seg.lines = Array(seg.geometry.length).fill('');
                      seg.lines[li] = lines[lineIndex];
                      if (!changedSegs.includes(j)) changedSegs.push(j);
                    }
                  } else if (isPointEqual(op1, p1) && isPointEqual(op2, p2)) {
                    // console.log('b2');
                    if (!seg.lines)
                      seg.lines = Array(seg.geometry.length).fill('');
                    seg.lines[li] = lines[lineIndex];
                    if (!changedSegs.includes(j)) changedSegs.push(j);
                  }
                }
              } else if (common !== -1 && common.length === 1) {
                const li = common[0];
                // const currentOri = drawOrientation(seg.geometry, li);
                const p1 = seg.geometry[li];
                const condition = (p) =>
                  isPointNearLine(p, op1, op2, 0.5) && !isPointEqual(p, p1);
                const p2Idx = seg.geometry.findIndex(condition);
                if (p2Idx !== -1) {
                  if (li === 0 && p2Idx === seg.geometry.length - 1) {
                    if (!seg.lines)
                      seg.lines = Array(seg.geometry.length).fill('');
                    seg.lines[p2Idx] = lines[lineIndex];
                    if (!changedSegs.includes(j)) changedSegs.push(j);
                  } else if (p2Idx === 0 && li === seg.geometry.length - 1) {
                    if (!seg.lines)
                      seg.lines = Array(seg.geometry.length).fill('');
                    seg.lines[li] = lines[lineIndex];
                    if (!changedSegs.includes(j)) changedSegs.push(j);
                  } else {
                    if (!seg.lines)
                      seg.lines = Array(seg.geometry.length).fill('');
                    seg.lines[Math.min(li, p2Idx)] = lines[lineIndex];
                    if (!changedSegs.includes(j)) changedSegs.push(j);
                  }
                }
              }
            }
          });
        }
        return { segments: [...segments] };
      },
      () => {
        if (
          this.state.selectedLabel === 'Eave' ||
          this.state.selectedLabel === 'Ridge'
        ) {
          this.getAzimuthFromLineLabel(index);
          changedSegs.forEach((changedIdx) => {
            this.getAzimuthFromLineLabel(changedIdx);
          });
        }
        this.updateSetbacks(null, null, true);
      },
      ['segments']
    );
  };

  handleSegmentAzimuth = (idx, azimuth) => {
    const segments = [...this.state.segments];
    const { resolution } = this.state;
    const segment = segments[idx];
    azimuth = Number(azimuth);
    const area = getSegmentArea(segment, resolution);
    segments[idx] = { ...segment, azimuth, area, pvwatts_update: true };
    console.log('[handleSegmentAzimuth] Changing azimuth, shadingHasRun:', this.state.shadingHasRun);
    this.setState3x({ segments });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  handleSegmentPitchAndArea = (idx, pitch) => {
    // if idx param. is -1, the pitch value will be set for all segments.
    const segments = [...this.state.segments];
    const { resolution } = this.state;
    if (idx === -1) {
      for (let i = 0; i < segments.length; i += 1) {
        const segment = segments[i];

        pitch = Number(pitch);
        const area = getSegmentArea(segment, resolution);

        // update segment area & pitch
        segments[i] = { ...segment, pitch, area, pvwatts_update: true };
      }
      this.setState3x({ segments });
      // Use setTimeout to check after state update completes
      setTimeout(() => this.checkShadingRerunNeeded(), 0);
    } else {
      const segment = segments[idx];
      // console.log("test", Object.values(corners))
      pitch = Number(pitch);
      const area = getSegmentArea(segment, resolution);
      // const segArea = areaPolygon(segment.geometry) * slopeFactors[pitch];
      // const segArea = areaPolygon(segment.geometry) * slopeFactor;
      // const segAreaSqm = (segArea * samplingRate) / 10000;
      // const area = convertUnits(segAreaSqm).from('m2').to('ft2');

      // update segments area & pitch
      segments[idx] = { ...segment, pitch, area, pvwatts_update: true };
      // Set pitch for initial panels array
      // const panels = [...this.state.panels];
      // panels[idx] = {...panels[idx], pitch};
      this.setState3x({ segments });
      // Use setTimeout to check after state update completes
      setTimeout(() => this.checkShadingRerunNeeded(), 0);
    }
  };

  handleSegmentEaveHeight = (idx, height) => {
    // if idx param. is -1, the eave height value will be set for all segments.
    console.log('[handleSegmentEaveHeight] Changing height, shadingHasRun:', this.state.shadingHasRun);
    if (idx === -1) {
      const newSegments = this.state.segments.map((seg) => ({ ...seg }));
      newSegments.forEach((seg, i) => {
        newSegments[i].height = height;
      });
      this.setState3x({ segments: newSegments });
      // Use setTimeout to check after state update completes
      setTimeout(() => this.checkShadingRerunNeeded(), 0);
    } else {
      const newSegments = this.state.segments.map((seg) => ({ ...seg }));
      newSegments[idx].height = height;
      this.setState3x({ segments: newSegments });
      // Use setTimeout to check after state update completes
      setTimeout(() => this.checkShadingRerunNeeded(), 0);
    }
  };

  handleTreeUpdate = (tree, idx) => {
    const newTrees = [...this.state.trees];
    newTrees[idx] = tree;
    this.setState3x({ trees: newTrees });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  // TODO: re-write this + subsequent into a single method
  handleAddPointToPolygonFromMenu = (key) => {
    this.setState2x({
      mode: 'edit',
      polyEdit: 1,
      focusedPolygonToEdit: key,
    });
  };

  handleRemovePointFromPolygonFromMenu = (key) => {
    this.setState2x({
      mode: 'edit',
      polyEdit: 2,
      focusedPolygonToEdit: key,
    });
  };

  handleDeleteShape = (key) => {
    const { currentAccordion } = this.state;
    switch (currentAccordion) {
      case 'segments':
        // this.drawCanvasElement.removeShape(key);
        this.handleDeleteSegment(key);
        break;
      case 'obstacles':
        // this.drawCanvasElement.removeShape(key);
        this.handleDeleteObstacle(key);
        break;
      case 'trees':
        // this.drawCanvasElement.removeShape(key);
        this.handleDeleteTree(key);
        break;
      case 'annotations':
        this.handleDeleteAnnotation(key);
        break;
      default:
        break;
    }
  };

  handleDeleteSegment = (key) => {
    const { CanvasAPI } = this.context;
    const { segments, panels } = this.state;

    const newSegments = [...segments].filter((segment) => segment.id !== key);
    const newPanels = [...panels].filter((panel) => panel.id !== key);

    CanvasAPI.saveCanvas({ ['panels']: newPanels });
    this.setState3x({ segments: newSegments, panels: newPanels });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
    // this.setStatex(({ segments, panels }) => ({
    //   segments: segments.filter((segment) => segment.id !== key),
    //   panels: panels.filter((segment) => segment.segmentId !== key),
    // }));
  };

  handleDeleteObstacle = (key) => {
    this.setStatex(
      ({ obstacles }) => ({
        obstacles: obstacles.filter((obstacle) => obstacle.id !== key),
      }),
      null,
      ['obstacles']
    );
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  handleDeleteTree = (key) => {
    this.setStatex(
      ({ trees }) => ({
        trees: trees.filter((tree) => tree.id !== key),
      }),
      null,
      ['trees']
    );
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  handleDeleteAnnotation = (key) => {
    const index = this.state.annotations.map(({ id }) => id).indexOf(key);
    if (index === -1) return;
    annotations.splice(index, 1);
    this.setAnnotations(annotations);
  };

  handleNewPanel = async () => {
    const { viewMode, panels } = this.state;
    if (this.countPlacedPanels(panels) === 0) {
      await this.applyPendingPanelHardware();
    }
    if (viewMode === '3D') {
      this.canvas3DElement.toggleView('insert');
      return;
    }
    this.placePanelsElement.addNewPanel();
  };

  handleDeletePanel = () => {
    const { panels, selectedPanels, viewMode } = this.state;
    if (viewMode === '3D') {
      this.canvas3DElement.deletePanels();
      return;
    }
    if (!selectedPanels) return;

    const deleted = panels.map((panel) => {
      const horizontal = panel.horizontal.points.filter(
        (pt, jdx) => !panel.horizontal.selected[jdx]
      );
      const vertical = panel.vertical.points.filter(
        (pt, jdx) => !panel.vertical.selected[jdx]
      );
      return {
        ...panel,
        horizontal: {
          ...panel.horizontal,
          points: horizontal,
          selected: Array(horizontal.length).fill(false),
        },
        vertical: {
          ...panel.vertical,
          points: vertical,
          selected: Array(vertical.length).fill(false),
        },
      };
    });
    this.setState2x({ panels: deleted, selectedPanels: false });
    this.syncSelectedPanelFrom2D();
    this.scheduleSolarProduction('panels_deleted');
  };

  handleRotatePanel = () => {
    const { panels, selectedPanels, viewMode } = this.state;
    if (viewMode === '3D') {
      this.canvas3DElement.rotatePanels();
      return;
    }
    if (!selectedPanels) return;

    const rotated = panels.map((panel) => {
      const horizontal = [];
      const vertical = [];
      panel.horizontal.points.forEach((pt, jdx) => {
        if (panel.horizontal.selected[jdx]) vertical.push(pt);
        else horizontal.push(pt);
      });
      panel.vertical.points.forEach((pt, jdx) => {
        if (panel.vertical.selected[jdx]) horizontal.push(pt);
        else vertical.push(pt);
      });
      return {
        ...panel,
        horizontal: {
          ...panel.horizontal,
          points: horizontal,
          selected: Array(horizontal.length).fill(false),
        },
        vertical: {
          ...panel.vertical,
          points: vertical,
          selected: Array(vertical.length).fill(false),
        },
      };
    });
    this.setState2x({ panels: rotated, selectedPanels: false });
    this.syncSelectedPanelFrom2D();
    this.scheduleSolarProduction('panels_rotated');
  };

  handlePanelAzimuth = (index, azimuth) => {
    if (index === null) return;
    this.setStatex(
      ({ segments, panels }) => {
        const segment = segments[index];
        const rotated = rotateSegmentPanels(segment, panels[index], azimuth);
        panels.splice(index, 1, rotated);
        return { panels: [...panels] };
      },
      () => {
        this.resetProductionForPanelLayoutChange();
        try {
          this.context.CanvasAPI.saveCanvas({ panels: this.state.panels });
        } catch (e) {}
      },
      ['panels']
    );
  };

  getSetbacksForLines = (lines = [], pSetbackMode, pFireSetbacks) =>
    lines.map((label) => {
      if (pSetbackMode === 0)
        return label === 'Eave' ? 0 : pFireSetbacks.general;
      return pFireSetbacks[label?.toLowerCase()] || 0;
    });

  updateSetbacks = (
    pFireSetbacks = null,
    pSetbackMode = null,
    forceUpdate = false
  ) => {
    const { segments, setbackMode, fireSetbacks } = this.state;

    if (!pSetbackMode) pSetbackMode = setbackMode;
    if (!pFireSetbacks) pFireSetbacks = fireSetbacks;

    console.log('SETBACK MODE = ', setbackMode);

    let hasChanged = false;

    const updated = segments.map((seg) => {
      if (seg.setbacks && seg.setbacks.length > 0 && !forceUpdate) return seg;
      hasChanged = true;
      const setbacks = this.getSetbacksForLines(
        seg.lines,
        pSetbackMode,
        pFireSetbacks
      );
      return { ...seg, setbacks };
    });

    this.setState2x({
      segments: updated,
      setbackMode: pSetbackMode,
      fireSetbacks: pFireSetbacks,
    });
    if (hasChanged || forceUpdate)
      this.context.CanvasAPI.saveCanvas({
        segments: updated,
      });
    return hasChanged;
  };

  handleSetbackMode = (setbackMode) =>
    this.setStatex({ setbackMode }, () =>
      this.updateSetbacks(null, null, true)
    );

  handleFireSetbacks = (setback, label) => {
    this.setStatex(
      ({ fireSetbacks }) => ({
        fireSetbacks:
          label === 'general'
            ? {
                ...fireSetbacks,
                general: setback,
                eave: setback,
                ridge: setback,
                rake: setback,
                hip: setback,
                valley: setback,
              }
            : { ...fireSetbacks, [label]: setback },
      }),
      () => {
        this.updateSetbacks(null, null, true);
      },
      ['fireSetbacks']
    );
  };

  handleLineSetbackEdit = (editLines, setback) => {
    console.log(editLines, setback);
    const spec = Object();
    editLines.forEach(({ seg, line }) => {
      spec[seg] = { setbacks: { [line]: { $set: setback } } };
    });
    console.log(spec);
    this.setStatex(
      ({ segments }) => update({ segments }, { segments: spec }),
      null,
      ['segments']
    );
  };

  handlePanels = (panels) => {
    const prevPanels = this.state.panels;
    const pendingPanel = this.maybeApplyPendingPanelForFirstPlacement(
      prevPanels,
      panels
    );
    const packageUpdate = pendingPanel
      ? {
          package: {
            ...this.state.package,
            panel: pendingPanel,
            pendingPanel: null,
          },
        }
      : {};
    this.setState3x({
      panels,
      ...packageUpdate,
    });
  };

  handleTool = (tool) => this.setState3x({ tool });

  handleSelectedLabel = (selectedLabel) =>
    this.setState3x({ selectedLabel, mode: 'label' });

  clearLabelsHandler = () => {
    const { segments } = this.state;
    const newSegments = [...segments];
    newSegments.forEach((s) => {
      s.lines = s.geometry.map(() => '');
    });
    this.setStatex({ segments: newSegments }, () => {
      this.updateSetbacks(null, null, true);
    });
  };

  clearMeasurment = () => {
    const { segments } = this.state;
    const newSegments = [...segments];
    newSegments.forEach((s) => {
      s.pitch = 0;
      s.azimuth = 0;
      s.height = 0;
    });
    this.setSegmentParameters(newSegments);
  };

  resetProductionForPanelLayoutChange = () => {
    const updates = {
      productionHandled: false,
      totalAnnualKwh: 0,
      totalMonthlyKwh: Array(12).fill(0),
      totalClippingKwh: 0,
      totalMonthlyClippingKwh: Array(12).fill(0),
      dcAcRatio: null,
      stringingData: null,
      invertersUsed: null,
      productionPerPanel: 0,
      productionPerPanelData: null,
      productionPerStringData: null,
      hourlyProduction: null,
      productionHardware: null,
      selectedPanelCount: 0,
      systemSizeKw: null,
      mpptCountTotal: null,
    };
    this.lastProductionSnapshot = null;
    this.setState(updates);
    try {
      return Promise.resolve(
        this.context.CanvasAPI.saveCanvas({
          total_annual_kwh: 0,
          total_monthly_kwh: Array(12).fill(0).join(','),
          total_clipping_kwh: 0,
          total_monthly_clipping_kwh: Array(12).fill(0).join(','),
          dc_ac_ratio: null,
          stringing_data: null,
          inverters_used: null,
          production_per_panel: null,
          production_per_string: null,
          hourly_production: null,
          system_size_kw: null,
          mppt_count_total: null,
        })
      ).catch(() => {});
    } catch (e) {
      return Promise.resolve();
    }
  };

  clearPanels = () => {
    const { panelPlacementOnlySelectedRoof } = this.state;
    this.canvas3DElement.clearPanels(panelPlacementOnlySelectedRoof);

    // Clear panels state
    // If clearing all panels (not just selected roof), set to empty array to disable Calculate Production button
    // If clearing only selected roof, update those specific panels but keep the array structure
    let clearedPanels = [];

    if (panelPlacementOnlySelectedRoof) {
      // Only clear panels for selected roof segments - keep array structure
      const { panels, segments } = this.state;
      const selectedRoofIndices = this.state.selectedObjects?.roofSegments || [];
      clearedPanels = panels.map((panel, index) => {
        if (selectedRoofIndices.includes(index)) {
          // Return empty panel structure for selected roofs
          return {
            ...panel,
            id: panel?.id || segments[index]?.id || String(index),
            azimuth: panel?.azimuth || 0,
            horizontal: {
              ...panel?.horizontal,
              points: [],
              selected: [],
            },
            vertical: {
              ...panel?.vertical,
              points: [],
              selected: [],
            },
          };
        }
        return panel; // Keep existing panel for non-selected roofs
      });
    } else {
      // Clear all panels - set to empty array so panels.length === 0 and button is disabled
      clearedPanels = [];
    }

    // Clear stringing data when panels are cleared
    // since the panel layout has changed and production needs to be recalculated
    this.setState({ panels: clearedPanels });
    this.resetProductionForPanelLayoutChange();
  };

  queueSolarProductionRun = (reason = 'change', options = {}) => {
    const pending = this.pendingSolarProductionRun || {};
    this.pendingSolarProductionRun = {
      reason,
      options: {
        ...(pending.options || {}),
        ...(options || {}),
        persistSelectionState: true,
      },
    };
  };

  flushQueuedSolarProductionRun = () => {
    const queued = this.pendingSolarProductionRun;
    if (!queued) return;
    this.pendingSolarProductionRun = null;

    setTimeout(() => {
      if (this.state.productionIsRunning) {
        this.pendingSolarProductionRun = queued;
        setTimeout(() => {
          this.flushQueuedSolarProductionRun();
        }, 60);
        return;
      }
      this.scheduleSolarProduction(queued.reason || 'queued_change', {
        ...(queued.options || {}),
        forceRun: true,
      });
    }, 0);
  };

  scheduleSolarProduction = (reason = 'change', options = {}) => {
    try {
      clearTimeout(this.solarProductionDebounceTimer);
    } catch (e) { }

    const isPanelOrInverterChange =
      String(reason || '').includes('panel') || String(reason || '').includes('inverter');
    if (!options?.forceRun && isPanelOrInverterChange) {
      this.solarProductionDebounceReason = reason;
      if (String(reason || '').includes('panel')) {
        this.resetProductionForPanelLayoutChange();
      }
      return;
    }

    this.solarProductionDebounceReason = reason;
    if (this.state.productionIsRunning) {
      this.queueSolarProductionRun(reason, options);
      return;
    }

    this.solarProductionDebounceTimer = setTimeout(() => {
      try {
        this.handleSolarProduction({
          persistSelectionState: true,
          ...options,
          reason,
        });
      } catch (e) { }
    }, 900);
  };

  clearSelectedPanelsOnly = () => {
    const { panels, selectedObjects } = this.state;

    const clearedPanels = Array.isArray(panels)
      ? panels.map((panel) => {
        const hPoints = panel?.horizontal?.points || [];
        const vPoints = panel?.vertical?.points || [];
        return {
          ...panel,
          horizontal: {
            ...panel.horizontal,
            selected: Array(hPoints.length).fill(false),
          },
          vertical: {
            ...panel.vertical,
            selected: Array(vPoints.length).fill(false),
          },
        };
      })
      : panels;

    this.setState2x(
      {
        panels: clearedPanels,
        selectedPanels: false,
        selectedObjects: { ...selectedObjects, panels: {} },
      },
      () => {
        this.syncSelectedPanelFrom2D();
      }
    );
  };

  handleBuffer = (buffer) => this.setState3x({ buffer });

  handleRowSpacing = (rowSpacing) => this.setState3x({ rowSpacing });

  handlePanelTilt = (panelTilt) => this.setState3x({ panelTilt });

  handleAzimuthRestriction = (azimuthRestriction) =>
    this.setState3x({ azimuthRestriction });

  handleSegmentTabs = (currentTab) => {
    const mode = currentTab === 'segment-labels' ? 'label' : 'draw';
    this.setState2x({ mode, currentTab });
  };

  handleSelectedPanel = (id, isSelected = null) => {
    const [idx, hv, jdx] = id.split('_');
    const panelIndex = Number(idx);
    const selectedIndex = Number(jdx);
    const { panels: currentPanels } = this.state;

    const panels = currentPanels.map((panel, index) => {
      if (index !== panelIndex) return panel;
      const selected = [...panel[hv].selected];
      selected[selectedIndex] =
        isSelected === null ? !selected[selectedIndex] : isSelected;
      return {
        ...panel,
        [hv]: {
          ...panel[hv],
          selected,
        },
      };
    });

    const selectedPanels = panels.some(
      (panel) =>
        panel.vertical.selected.some((el) => el) ||
        panel.horizontal.selected.some((el) => el)
    );

    this.setState2x({ panels, selectedPanels });
    try {
      this.context.CanvasAPI?.saveCanvas?.({ panels });
    } catch (e) {}

    this.syncSelectedPanelFrom2D();
    if (this.state.onlySelectedPanels) {
      this.resetProductionForPanelLayoutChange();
    }
  };

  handleSelectedRoof = (index) => {
    const selectedRoof = index === undefined ? -1 : index;
    this.setState3x({ selectedRoof });
    this.syncSelectedRoofFrom2D();
  };

  // TODO: do we still need this?
  // setScroller = (left, top) => this.setStatex({ scroller: [left, top] });

  handleResetScale = () => {
    this.setState({ scale: SCALE });
    this.canvas3DElement.zoomCamera(0);
  };

  handleScaleUp = () => {
    this.setState(({ scale }) => ({ scale: Math.min(scale * 1.3, 10) }));
    this.canvas3DElement.zoomCamera(1.1);
  };

  handleScaleDown = () => {
    this.setState(({ scale }) => ({ scale: Math.max(scale / 1.3, SCALE) }));
    this.canvas3DElement.zoomCamera(0.9);
  };

  automaticAzimuthMode = (mode) => {
    this.setState3x({ automaticAzimuth: mode });
  };

  getAzimuthForLineLabel = (segment) => {
    if (!segment) return null;
    const { lines, geometry } = segment;
    if (!Array.isArray(lines) || !Array.isArray(geometry)) return null;
    if (!geometry.length) return null;

    const bestEdges = {
      Eave: { index: -1, len: 0 },
      Ridge: { index: -1, len: 0 },
      Valley: { index: -1, len: 0 },
    };

    const validEdgeType = Object.keys(bestEdges);
    geometry.forEach((v1, vi) => {
      const edgeType = lines[vi];
      if (validEdgeType.includes(edgeType)) {
        const vj = (vi + 1) % geometry.length;
        const v2 = geometry[vj];
        const edgeLength = distance(v1, v2);
        if (bestEdges[edgeType].len < edgeLength) {
          bestEdges[edgeType].index = vi;
          bestEdges[edgeType].len = edgeLength;
        }
      }
    });

    const edges = validEdgeType.map((k) => bestEdges[k]);
    const edge = edges.find((edge) => edge.index >= 0);
    if (!edge) return;
    const eaveIndex = edges.indexOf(edge);
    const eave = validEdgeType[eaveIndex] === 'Eave';

    // const eave = lines.indexOf('Eave');
    // const ridge = lines.indexOf('Ridge');
    // if (eave === -1 && ridge === -1) return;

    // const ll = eave === -1 ? ridge : eave;

    const p1 = segment.geometry[edge.index];
    const p2 = segment.geometry[(edge.index + 1) % segment.geometry.length];

    // Computing azimuth
    let dx = p2[0] - p1[0];
    let dy = -(p2[1] - p1[1]); // inverted to account for origin on top-left

    if (drawOrientation(segment.geometry, edge.index) === 'ccw') {
      // console.log(index, 'CCW');
      dx = -dx;
      dy = -dy;
    }
    const theta = eave
      ? (Math.atan2(-dy, dx) * 180) / Math.PI
      : (Math.atan2(dy, -dx) * 180) / Math.PI;
    let azimuth = (theta + 360) % 360;
    azimuth = Math.round((azimuth + Number.EPSILON) * 100) / 100;
    return azimuth;
  };

  getAzimuthFromLineLabel = (index) => {
    const { segments, automaticAzimuth } = this.state;
    if (!automaticAzimuth) return;

    const newSegments = [...segments];
    const segment = { ...newSegments[index] };
    const azimuth = this.getAzimuthForLineLabel(segment);
    if (azimuth === null) return;
    segment.azimuth = azimuth;

    newSegments[index] = segment;

    this.setState3x({ segments: newSegments });
  };

  mapsLink = () => {
    const { lead } = this.context;
    const [lat, lng] = lead.property.coordinates;
    return `https://www.google.com/maps/@${lat},${lng},61m/data=!3m1!1e3`;
  };

  toggleOverlay = () => this.setState({ showOverlay: !this.state.showOverlay });

  cleanCanvas = () => {
    const { removeShadingsAPI, lead } = this.context;
    // this.drawCanvasElement.cleanCanvas();
    try {
      this.canvas3DElement?.clearPanels?.(false);
    } catch (e) { }
    this.setState3x({
      segments: [],
      obstacles: [],
      trees: [],
      panels: [],
    });
    const request = {
      uid: lead.uid,
    };
    removeShadingsAPI.create(request).then((data) => {
      console.log('shading gradients removed');
    });
  };

  canvas_import_dxf = () => {
    const { canvasImportDxfAPI, lead } = this.context;
    const request = {
      uid: lead.uid,
    };
    canvasImportDxfAPI.create(request).then((data) => {
      console.log('import dxf');
    });
  };

  handleMouseWheel = (event) => {
    const { scale, viewMode, currentAccordion } = this.state;
    if (viewMode === '2D') {
      const wheel = event.deltaY / 120;
      // if (wheel < 0) this.handleScaleUp();
      // else this.handleScaleDown();
      this.mouseDownPosition = [event.clientX, event.clientY];
      const { top: ctop, left: cleft } = this.container.getBoundingClientRect();
      const { top, left } = event.target.getBoundingClientRect();
      const pos = [
        this.mouseDownPosition[0] - left,
        this.mouseDownPosition[1] - top,
      ];
      const newScale =
        wheel < 0 ? Math.min(scale * 1.3, 10) : Math.max(scale / 1.3, SCALE);
      const scaleRatio = newScale / scale;
      const newPos = [pos[0] * scaleRatio, pos[1] * scaleRatio];
      const size = CANVAS_SIZE * newScale;
      if (konvaKeys.includes(currentAccordion)) {
        // this.placePanelsElement.updateScale(newScale);
        // pass
      } else {
        // this.drawCanvasElement.updateScale(newScale, size);
      }
      this.container.scrollLeft =
        newPos[0] - (this.mouseDownPosition[0] - cleft);
      this.container.scrollTop = newPos[1] - (this.mouseDownPosition[1] - ctop);
    }
  };

  handleMouseDown = (event) => {
    if (this.mouseDownState) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (event.button === 2 || isIOS) {
      event.preventDefault();
      event.stopPropagation();
      this.mouseDownState = true;
      this.mouseDownPosition = [event.clientX, event.clientY];
      this.scrollPosition = [
        this.container.scrollLeft,
        this.container.scrollTop,
      ];
    }
  };

  handleMouseMove = (event) => {
    const { iframe, designFrame } = this.props;
    if (!designFrame) {
      if (
        this.canvas3DElement.isLoaded &&
        this.container &&
        !this.container.hidden
      )
        disableScroll.on({ disableScroll: true, disableWheel: true });
    }
    if (this.mouseDownState) {
      const pos = [event.clientX, event.clientY];
      this.container.scrollLeft =
        this.scrollPosition[0] - pos[0] + this.mouseDownPosition[0];
      this.container.scrollTop =
        this.scrollPosition[1] - pos[1] + this.mouseDownPosition[1];
    }
  };

  handleMouseUp = () => {
    this.mouseDownState = false;
    this.handleWindowMouseUp();
  };

  handleMouseLeave = () => {
    disableScroll.off();
  };

  handleMouseEnter = () => {
    const { iframe, designFrame } = this.props;
    if (!designFrame) {
      if (
        this.canvas3DElement.isLoaded &&
        this.container &&
        !this.container.hidden
      ) {
        disableScroll.on({ disableScroll: true, disableWheel: true });
      }
    }
  };

  // replace with inline arrow function
  goFullScreen = (enabled) => {
    this.setState({ isFullScreen: enabled });
  };

  changePanelHardware = (panel) => {
    // console.log(this.state.package);
    // console.log(panel);
    const { iframe, designFrame } = this.props;

    if (!panel || typeof panel !== 'object') {
      return Promise.resolve(false);
    }

    const { package: installationPackage } = this.state;
    panel.length = Math.max(500, Number(panel.length));
    panel.width = Math.max(500, Number(panel.width));

    // if (designFrame) {
    //   console.log(`designer custom panel type:
    //     \nlength: ${panel.length}
    //     \nwidth: ${panel.width}
    //     \ndegradation: ${panel.degradation}
    //     \nefficiency: ${panel.efficiency}
    //     \npower: ${panel.power}`
    //   );
    // }

    if (this.countPlacedPanels(this.state.panels) === 0) {
      this.firstManualInsertPanel = panel;
      return new Promise((resolve) => {
        this.setStatex(
          {
            package: {
              ...installationPackage,
              panel,
              pendingPanel: null,
            },
          },
          () => {
            try {
              this.context.CanvasAPI.saveCanvas({
                selected_panel_id:
                  panel.id !== undefined && panel.id !== null
                    ? String(panel.id)
                    : null,
              });
            } catch (e) { }
            resolve(true);
          }
        );
      });
    }

    this.firstManualInsertPanel = null;
    return new Promise((resolve) => {
      this.setStatex(
        { package: { ...installationPackage, pendingPanel: panel } },
        () => {
          resolve(true);
        }
      );
    });
  };

  applyPendingPanelHardware = () => {
    const { package: installationPackage } = this.state;
    const pendingPanel = installationPackage?.pendingPanel;
    if (!pendingPanel) return Promise.resolve(false);
    return new Promise((resolve) => {
      this.setStatex(
        {
          package: {
            ...installationPackage,
            panel: pendingPanel,
            pendingPanel: null,
          },
        },
        () => resolve(true)
      );
    });
  };

  changeInverterHardware = (inverter, additional_inverters = []) => {
    const { package: installationPackage } = this.state;
    if (inverter) {
      const prevId = installationPackage?.inverter?.id || null;
      const nextId = inverter?.id || null;
      if (prevId && inverter?.id && prevId === inverter.id) {
        return;
      }
      const inverterChanged = Boolean(prevId && nextId && prevId !== nextId);
      const newQuantity = inverterChanged
        ? 1
        : installationPackage.inverterQuantity || 1;

      const isPrimaryMicro =
        inverter?.type === 'M' || inverter?.type === 'Micro';

      const rawAdditional = isPrimaryMicro
        ? []
        : Array.isArray(additional_inverters) && additional_inverters.length > 0
          ? additional_inverters
          : installationPackage.additional_inverters || [];

      const filteredAdditional = isPrimaryMicro
        ? []
        : (rawAdditional || []).filter((ai) => {
          if (!ai?.id) return true;
          const invObj = (this.state.inverters || []).find(
            (x) => x.id === ai.id
          );
          return matchesPrimaryInverterType(inverter, invObj);
        });

      this.setStatex(
        {
          package: {
            ...installationPackage,
            inverter,
            inverterQuantity: newQuantity,
            additional_inverters: filteredAdditional,
          },
          hasAutoCalculatedInverterQuantity: false, // Reset to auto mode when changing inverters
        },
        () => {
          // Save selected inverter to canvas for persistence
          this.context.CanvasAPI.saveCanvas({
            inverter_quantity: newQuantity,
          });
          // Ensure additional inverters remain compatible with primary type
          this.context.CanvasAPI.saveCanvas({
            additional_inverters: filteredAdditional,
          });
        }
      );
    }
  };

  changeInverterQuantity = (quantity) => {
    this.setStatex(
      (prev) => ({
        package: { ...(prev.package || {}), inverterQuantity: quantity },
        hasAutoCalculatedInverterQuantity: true,
        primaryInverterAutoQuantity: false,
      }),
      () => {
        // Save inverter quantity to canvas for persistence
        this.context.CanvasAPI.saveCanvas({
          inverter_quantity: quantity,
          primary_inverter_auto_quantity: false,
        });
      }
    );
  };

  changeAdditionalInverters = (additionalInverters) => {
    const { package: installationPackage } = this.state;
    const primary = installationPackage?.inverter;
    const isPrimaryMicro = primary?.type === 'M' || primary?.type === 'Micro';
    const filtered = isPrimaryMicro
      ? []
      : (additionalInverters || []).filter((ai) => {
        if (!ai?.id) return true; // allow empty rows in UI
        const invObj = (this.state.inverters || []).find(
          (x) => x.id === ai.id
        );
        return matchesPrimaryInverterType(primary, invObj);
      });
    const hasConfiguredAdditionalInverters = filtered.some(
      (ai) => ai?.id || ai?.inverter_id
    );
    const nextPrimaryAuto = hasConfiguredAdditionalInverters
      ? false
      : this.state.primaryInverterAutoQuantity;
    this.setStatex(
      {
        package: {
          ...installationPackage,
          additional_inverters: filtered,
        },
        primaryInverterAutoQuantity: nextPrimaryAuto,
        hasAutoCalculatedInverterQuantity: !nextPrimaryAuto,
      },
      () => {
        // Save additional inverters to canvas for persistence
        this.context.CanvasAPI.saveCanvas({
          additional_inverters: filtered,
          primary_inverter_auto_quantity: nextPrimaryAuto,
        });
      }
    );
  };

  calculateOptimalInverterQuantity = () => {
    const { package: installationPackage, panels } = this.state;
    const { inverter } = installationPackage;

    if (!inverter || !panels || panels.length === 0) {
      return 1; // Default to 1 if no data available
    }

    // For micro inverters, quantity should equal panel count (1:1 ratio)
    if (inverter.type === 'M' || inverter.type === 'Micro') {
      const panelCount = panels.reduce((total, segment) => {
        return (
          total +
          (segment.horizontal?.length || 0) +
          (segment.vertical?.length || 0)
        );
      }, 0);
      return Math.max(1, panelCount); // At least 1, even with 0 panels
    }

    // Calculate total panel power (DC)
    const totalPanelPower =
      panels.reduce((total, segment) => {
        return (
          total +
          (segment.horizontal?.length || 0) +
          (segment.vertical?.length || 0)
        );
      }, 0) * (installationPackage.panel?.power || 0);

    if (totalPanelPower === 0) {
      return 1; // Default to 1 if no panels
    }

    // Calculate inverter quantity to achieve <5% clipping
    // Target: AC capacity should be at least 95% of DC capacity to keep clipping under 5%
    const inverterCapacity = Number(inverter.capacity || 0);
    if (inverterCapacity === 0) {
      return 1; // Default to 1 if no inverter capacity
    }

    const optimalQuantity = Math.ceil(
      totalPanelPower / (inverterCapacity * 0.95)
    );

    // Ensure at least 1 inverter and cap at 10 for practical reasons
    return Math.max(1, Math.min(10, optimalQuantity));
  };

  updateInverterQuantityAfterPlacement = () => {
    const optimalQuantity = this.calculateOptimalInverterQuantity();
    const { package: installationPackage } = this.state;
    const { inverter, additional_inverters = [] } = installationPackage;
    const { primaryInverterAutoQuantity } = this.state;

    // Always update for micro inverters (must match panel count)
    const isMicroInverter =
      inverter?.type === 'M' || inverter?.type === 'Micro';

    // Only update if:
    // 1. It's a micro inverter (always update to match panel count), OR
    // 2. Central inverter is in auto mode AND no additional inverters
    if (
      isMicroInverter ||
      (primaryInverterAutoQuantity && additional_inverters.length === 0)
    ) {
      this.setStatex({
        package: {
          ...installationPackage,
          inverterQuantity: optimalQuantity,
        },
      });
    }
  };

  calculateInverterClipping = () => {
    const { package: installationPackage, panels } = this.state;
    const { inverter, additional_inverters = [], panel } = installationPackage;

    if (!inverter || !panels || panels.length === 0 || !panel) {
      return { clippedPower: 0, clippingPercentage: 0 };
    }

    // Calculate total SELECTED panel count and power
    const totalPanelCount = panels.reduce((total, segment) => {
      const selectedHorizontal =
        segment.horizontal?.selected?.filter((isSelected) => isSelected)
          .length || 0;
      const selectedVertical =
        segment.vertical?.selected?.filter((isSelected) => isSelected).length ||
        0;
      return total + selectedHorizontal + selectedVertical;
    }, 0);

    const totalPanelPower = totalPanelCount * (panel.power || 0);

    if (totalPanelPower === 0) {
      return { clippedPower: 0, clippingPercentage: 0 };
    }

    // Calculate total inverter capacity
    let totalInverterCapacity =
      Number(inverter.capacity || 0) *
      Number(installationPackage.inverterQuantity || 1);

    // Add additional inverters
    additional_inverters.forEach((addInv) => {
      totalInverterCapacity +=
        Number(addInv.capacity || 0) * Number(addInv.quantity || 1);
    });

    if (totalInverterCapacity === 0) {
      return { clippedPower: 0, clippingPercentage: 0 };
    }

    // Calculate clipping based on inverter type
    const isMicroInverter = inverter.type === 'M' || inverter.type === 'Micro';
    const inverterEfficiency =
      Number(inverter.efficiency || (isMicroInverter ? 96 : 97)) / 100;

    // Convert DC panel power to expected AC output
    const expectedACPower = totalPanelPower * inverterEfficiency;

    // Calculate clipping based on AC power comparison
    const clippedACPower = Math.max(0, expectedACPower - totalInverterCapacity);

    // Convert clipped AC power back to DC equivalent for reporting
    const clippedDCPower = clippedACPower / inverterEfficiency;

    // Calculate percentage based on DC power (what's actually lost from panels)
    const clippingPercentage =
      totalPanelPower > 0 ? (clippedDCPower / totalPanelPower) * 100 : 0;

    // Calculate estimated annual energy loss (simplified calculation)
    const peakSunHours = 5.5; // Average daily peak sun hours for most US locations
    const clippingFactor = 0.7; // Only ~70% of days have strong enough sun to cause clipping
    const daysPerYear = 365;
    const clippedAnnualKWh =
      (clippedDCPower / 1000) * peakSunHours * clippingFactor * daysPerYear;

    return {
      clippedPower: clippedDCPower,
      clippingPercentage,
      clippedAnnualKWh,
      expectedACPower,
      actualACPower: Math.min(expectedACPower, totalInverterCapacity),
      dcToACRatio: totalPanelPower / totalInverterCapacity,
    };
  };

  updateInverterClippingAfterPlacement = () => {
    return this.calculateInverterClipping();
  };

  switchView = (viewMode) => {
    this.setState3x({ viewMode });
    this.canvas3DElement.setViewMode(viewMode);
    if (viewMode === '2D') this.setState({ scale: SCALE });
  };

  setSelectionMode = (type) => {
    this.setState({ selectionMode: type });
  };

  syncSelectedRoofFrom3D = () => {
    // Functional setState so we read the *queued* selectedObjects, not
    // a stale snapshot. Callers (addToSelectedObjects, clearSelection,
    // removeFromSelectedObjects) queue a setState mutating
    // selectedObjects.roofSegments and then synchronously call this
    // sync. In React 18, that setState is auto-batched, so reading
    // this.state here returns the PRE-batch value and computes a
    // stale selectedRoof — producing the canvas/Revise mismatch where
    // selectedRoof points to a segment while selectedObjects.roofSegments
    // is empty (or vice versa). The updater below sees the post-batch
    // roofSegments and stays consistent.
    //
    // saveState intentionally ignores selectedRoof (not in its
    // validKeys list, see saveState at index.js:6609-6618), so dropping
    // setState3x here loses no history tracking. We still mirror the
    // iframe State callback that setState3x would have fired so embedded
    // consumers continue to see selectedRoof changes.
    this.setState(
      (prevState) => {
        const selectedSegments = prevState.selectedObjects.roofSegments;
        const computed = selectedSegments.length
          ? selectedSegments[selectedSegments.length - 1]
          : -1;
        if (prevState.selectedRoof === computed) return null;
        return { selectedRoof: computed };
      },
      () => {
        // Match setState3x's always-fire-on-call iframe callback, so
        // consumers see the same cadence of State updates they did
        // before this fix. callbackDesignerIframe is a no-op outside
        // iframe mode, so this costs nothing for non-iframe consumers.
        this.callbackDesignerIframe(
          { selectedRoof: this.state.selectedRoof },
          false
        );
      }
    );
  };

  syncSelectedRoofFrom2D = () => {
    // Same stale-read fix as syncSelectedRoofFrom3D, but in the inverse
    // direction: caller (handleSelectedRoof) just queued a setState
    // changing selectedRoof and now wants selectedObjects.roofSegments
    // to mirror it. Using prevState gives us the post-batch selectedRoof.
    this.setState((prevState) => {
      const newSegments =
        prevState.selectedRoof !== -1 ? [prevState.selectedRoof] : [];
      const current = prevState.selectedObjects.roofSegments;
      const unchanged =
        current.length === newSegments.length &&
        current.every((v, i) => v === newSegments[i]);
      if (unchanged) return null;
      return {
        selectedObjects: {
          ...prevState.selectedObjects,
          roofSegments: newSegments,
        },
      };
    });
  };

  syncSelectedPanelFrom3D = () => {
    const { panels, selectedObjects } = this.state;
    const selectedPanels = selectedObjects.panels;

    let isSelectedPanels = false;

    panels.forEach((panel, segmentIndex) => {
      panel.horizontal.selected.forEach((_, pi) => {
        panel.horizontal.selected[pi] = false;
      });
      panel.vertical.selected.forEach((_, pi) => {
        panel.vertical.selected[pi] = false;
      });

      if (segmentIndex in selectedPanels) {
        selectedPanels[segmentIndex].forEach((panelIndex) => {
          isSelectedPanels = true;
          const panelCount = panel.horizontal.points.length;
          if (panelIndex < panelCount) {
            panel.horizontal.selected[panelIndex] = true;
          } else {
            panel.vertical.selected[panelIndex - panelCount] = true;
          }
        });
      }
    });

    this.setState2x({ panels, selectedPanels: isSelectedPanels });
  };

  syncSelectedPanelFrom2D = () => {
    const { panels, selectedObjects } = this.state;
    const selectedPanels = {};

    panels.forEach((panel, segmentIndex) => {
      const selectedList = [];
      panel.horizontal.selected.forEach((selected, pi) => {
        if (selected) selectedList.push(pi);
      });
      const horizontalCount = panel.horizontal.points.length;
      panel.vertical.selected.forEach((selected, pi) => {
        if (selected) selectedList.push(pi + horizontalCount);
      });
      if (selectedList) selectedPanels[segmentIndex] = selectedList;
    });

    this.setState({
      selectedObjects: { ...selectedObjects, panels: selectedPanels },
    });
  };

  addToSelectedObjects = (index, objectType, removeOld = false) => {
    const { currentAccordion, selectedObjects } = this.state;
    const objects = selectedObjects[objectType];

    let newObjects;
    if (!Array.isArray(objects))
      newObjects =
        !removeOld && objects ? JSON.parse(JSON.stringify(objects)) : {};
    else newObjects = !removeOld && objects ? [...objects] : [];

    const items = Array.isArray(index) ? index : [index];
    const restrictPanelsToSingleRoof =
      objectType === 'panels' &&
      (currentAccordion === 'design' || currentAccordion === 'revise');
    const targetPanelRoof =
      restrictPanelsToSingleRoof
        ? items.map((s) => (typeof s !== 'number' ? String(s).split('-')[0] : null)).find((s) => s !== null)
        : null;
    if (targetPanelRoof !== null) {
      Object.keys(newObjects).forEach((pi) => {
        if (pi !== targetPanelRoof) delete newObjects[pi];
      });
    }

    items.forEach((s) => {
      if (typeof s !== 'number') {
        const [pi, vi] = s.split('-');
        if (targetPanelRoof !== null && pi !== targetPanelRoof) return;
        const nvi = parseInt(vi, 10);
        if (!(pi in newObjects) || !Array.isArray(newObjects[pi]))
          newObjects[pi] = [];
        if (!newObjects[pi].includes(nvi)) newObjects[pi].push(nvi);
      } else if (!newObjects.includes(s)) newObjects.push(s);
    });

    Object.keys(newObjects)
      .reverse()
      .forEach((i) => {
        if (newObjects[i] === undefined) delete newObjects[i];
      });

    // Use setState2x for panels to trigger iframe callbacks, regular setState for others
    if (objectType === 'panels') {
      this.setState2x(
        {
          selectedObjects: { ...selectedObjects, [objectType]: newObjects },
        },
        () => {
          this.syncSelectedPanelFrom3D();
          if (
            this.state.onlySelectedPanels &&
            !this.suppressSolarProductionFromSelection
          ) {
            this.resetProductionForPanelLayoutChange();
          }
        }
      );
      return;
    } else {
      this.setState({
        selectedObjects: { ...selectedObjects, [objectType]: newObjects },
      });
    }

    switch (objectType) {
      case 'roofSegments':
        this.syncSelectedRoofFrom3D();
        // The "Only Selected Roof Segments" toggle is now derived from
        // selectedObjects.roofSegments in componentDidUpdate — no need
        // to flip the flag here. See the derivation block for rationale
        // (libs/canvas-library wipes the selection on accordion change
        // to 'revise', so a one-directional auto-ON would desync).
        break;
      default:
        break;
    }
  };

  addToSelectedObjectsBulk = (indices, objectType, clearExisting = false) => {
    const items = Array.isArray(indices) ? indices : [indices];
    if (!items || items.length === 0) return;
    this.addToSelectedObjects(items, objectType, clearExisting);
  };

  removeFromSelectedObjects = (index, objectType) => {
    const { selectedObjects } = this.state;
    const objects = selectedObjects[objectType];

    let newObjects;
    if (!Array.isArray(objects))
      newObjects = objects ? JSON.parse(JSON.stringify(objects)) : {};
    else newObjects = objects ? [...objects] : [];

    const items = Array.isArray(index) ? index : [index];

    items.forEach((s) => {
      if (typeof s !== 'number') {
        const [pi, vi] = s.split('-');
        const nvi = parseInt(vi, 10);
        if (!(pi in newObjects)) return;
        if (newObjects[pi].includes(nvi))
          newObjects[pi].splice(newObjects[pi].indexOf(nvi), 1);
      } else if (newObjects.includes(s))
        newObjects.splice(newObjects.indexOf(s), 1);
    });

    Object.keys(newObjects)
      .reverse()
      .forEach((i) => {
        if (newObjects[i] === undefined) delete newObjects[i];
      });

    // Use setState2x for panels to trigger iframe callbacks, regular setState for others
    if (objectType === 'panels') {
      this.setState2x(
        {
          selectedObjects: { ...selectedObjects, [objectType]: newObjects },
        },
        () => {
          this.syncSelectedPanelFrom3D();
          if (
            this.state.onlySelectedPanels &&
            !this.suppressSolarProductionFromSelection
          ) {
            this.resetProductionForPanelLayoutChange();
          }
        }
      );
      return;
    } else {
      this.setState({
        selectedObjects: { ...selectedObjects, [objectType]: newObjects },
      });
    }

    switch (objectType) {
      case 'roofSegments':
        this.syncSelectedRoofFrom3D();
        break;
      default:
        break;
    }
  };

  clearSelection = (objectType, forceClear = false) => {
    let notExist = false;
    const { selectedObjects, segments, panels } = this.state;
    const objects = selectedObjects[objectType];
    if (!Array.isArray(objects)) {
      notExist = Object.keys(objects).length === 0;
      notExist =
        notExist || Object.keys(objects).every((s) => objects[s].length === 0);
    } else {
      notExist = selectedObjects[objectType].length === 0;
    }

    if (!notExist || forceClear) {
      this.setState({
        selectedObjects: {
          roofSegments: selectedObjects.roofSegments.length
            ? []
            : selectedObjects.roofSegments,
          roofVertices: Object.keys(selectedObjects.roofVertices).length
            ? {}
            : selectedObjects.roofVertices,
          roofLines: Object.keys(selectedObjects.roofLines).length
            ? {}
            : selectedObjects.roofLines,
          obstacles: selectedObjects.obstacles.length
            ? []
            : selectedObjects.obstacles,
          fireSetbacks: Object.keys(selectedObjects.fireSetbacks).length
            ? {}
            : selectedObjects.fireSetbacks,
          panels: Object.keys(selectedObjects.panels).length
            ? {}
            : selectedObjects.panels,
          trees: selectedObjects.trees.length ? [] : selectedObjects.trees,
        },
      });
    } else {
      let selectedObjs = {};
      switch (objectType) {
        case 'roofSegments':
          selectedObjs = [];
          segments.forEach((segment, segmentIndex) => {
            selectedObjs.push(segmentIndex);
          });
          break;
        case 'roofVertices':
        case 'fireSetbacks':
          segments.forEach((segment, segmentIndex) => {
            selectedObjs[segmentIndex] = [];
            segment.geometry.forEach((vertex, vertexIndex) => {
              selectedObjs[segmentIndex].push(vertexIndex);
            });
          });
          break;
        case 'panels':
          panels.forEach((p, pi) => {
            selectedObjs[pi] = [];
            const allPanels = [...p.vertical.points, ...p.horizontal.points];
            allPanels.forEach((pl, pli) => {
              selectedObjs[pi].push(pli);
            });
          });
          break;
        default:
          break;
      }
      this.setState({
        selectedObjects: { ...selectedObjects, [objectType]: selectedObjs },
      });
    }

    switch (objectType) {
      case 'panels':
        this.syncSelectedPanelFrom3D();
        break;
      case 'roofSegments':
        this.syncSelectedRoofFrom3D();
        break;
      default:
        break;
    }
  };

  deleteSelectedObjects = (objectType) => {
    const { segments, selectedObjects } = this.state;
    switch (objectType) {
      case 'roofSegments': {
        const newSegments = [...segments];
        selectedObjects.roofSegments
          .sort()
          .reverse()
          .forEach((index) => {
            newSegments.splice(index, 1);
          });
        this.setState3x({ segments: newSegments });
        break;
      }
      default:
        break;
    }
  };

  setHeight = (value) => {
    const { segments, selectedObjects } = this.state;
    const newSegments = segments.map((seg) => ({ ...seg }));
    selectedObjects.roofSegments.forEach((index) => {
      if (value === '+')
        newSegments[index].height = (
          Number(newSegments[index].height) + 1
        ).toFixed(2);
      else if (value === '-')
        newSegments[index].height = Math.max(
          0,
          Number(newSegments[index].height) - 1
        ).toFixed(2);
      else newSegments[index].height = Math.max(0, Number(value)).toFixed(2);
    });
    this.setState3x({ segments: newSegments });
  };

  setPitch = (value, i) => {
    const { segments, selectedObjects } = this.state;
    const newSegments = segments.map((seg) => ({ ...seg }));
    selectedObjects.roofSegments.forEach((index) => {
      if (value === '+')
        newSegments[index].pitch = (
          Number(newSegments[index].pitch) + 1
        ).toFixed(2);
      else if (value === '-')
        newSegments[index].pitch = Math.max(
          0,
          Number(newSegments[index].pitch) - 1
        ).toFixed(2);
      else newSegments[index].pitch = Math.max(0, Number(value)).toFixed(2);
    });
    this.setState3x({ segments: newSegments });
  };

  setSetback = (length, changeClockwise) => {
    const { segments, obstacles, selectedObjects, setback, setbackMode } =
      this.state;
    const newSegments = [...segments];
    const newObstacles = [...obstacles];
    let changeSegments = false;
    let changeObstacles = false;
    const segLen = newSegments.length;
    Object.keys(selectedObjects.fireSetbacks).forEach((pi) => {
      if (pi < segLen) {
        const len = newSegments[pi].geometry.length;
        if (!newSegments[pi].setbacks)
          newSegments[pi].setbacks = new Array(len).fill(setback);
        selectedObjects.fireSetbacks[pi].forEach((vi) => {
          if (changeClockwise[pi])
            newSegments[pi].setbacks[len - vi - 1] = parseFloat(length);
          else newSegments[pi].setbacks[vi] = parseFloat(length);
        });
        newSegments[pi].setbacks = [...newSegments[pi].setbacks];
        changeSegments = true;
      } else {
        newObstacles[pi - segLen].setback = parseFloat(length);
        changeObstacles = true;
      }
    });
    if (changeSegments) {
      this.setState3x({ segments: newSegments });
      this.context.CanvasAPI.saveCanvas({
        segments: newSegments,
        setback_mode: setbackMode === 0 ? 'G' : 'E',
      });
      // Use setTimeout to check after state update completes
      setTimeout(() => this.checkShadingRerunNeeded(), 0);
    }
    if (changeObstacles) {
      this.setState3x({ obstacles: newObstacles });
      this.context.CanvasAPI.saveCanvas({ obstacles: newObstacles });
      // Use setTimeout to check after state update completes
      setTimeout(() => this.checkShadingRerunNeeded(), 0);
    }
  };

  setShadingStatus = (status) => {
    const updates = { shadingStatus: status };
    // When shading completes successfully (status === 0), mark shading as run and reset rerun flags
    if (status === 0) {
      console.log('[setShadingStatus] Shading completed, setting shadingHasRun=true');
      updates.shadingHasRun = true;
      updates.shadingNeedsRerun = false;
      updates.shadingRerunNoticeShown = false;
    }
    this.setState2x(updates);
  };

  /**
   * Checks if shading needs to be re-run after design changes
   * If shading has run and we haven't shown the notice yet, shows a snackbar
   * and sets flags to disable proposal button
   */
  checkShadingRerunNeeded = () => {
    const { shadingHasRun, shadingRerunNoticeShown, panels } = this.state;
    const { displaySnack } = this.context;

    // A roof change invalidates either:
    //   - existing panel layout (if the user has placed panels), and/or
    //   - existing shading results (if shading has already been run).
    // We only need to fire the alert / clear data when one of those is true.
    const hasPanels =
      Array.isArray(panels) &&
      panels.some(
        (p) =>
          (p?.horizontal?.points?.length || 0) > 0 ||
          (p?.vertical?.points?.length || 0) > 0
      );

    // Debug logging
    console.log('[checkShadingRerunNeeded]', {
      shadingHasRun,
      shadingRerunNoticeShown,
      hasPanels,
      hasDisplaySnack: !!displaySnack,
    });

    if (
      (shadingHasRun || hasPanels) &&
      !shadingRerunNoticeShown &&
      displaySnack
    ) {
      console.log('[checkShadingRerunNeeded] Showing snackbar and setting flags');

      // The roof has changed, so the existing panel layout and stringing are
      // no longer valid. Clear them and reset production; the user will need
      // to refill panels (and rerun shading, if it had been run) before
      // creating a proposal.
      if (hasPanels) {
        try {
          this.canvas3DElement?.clearPanels?.(false);
        } catch (e) {
          console.warn('[checkShadingRerunNeeded] clearPanels failed', e);
        }
        this.setState({ panels: [] });
        this.resetProductionForPanelLayoutChange();
        try {
          this.context.CanvasAPI.saveCanvas({ panels: [] });
        } catch (e) {
          // saveCanvas may not be ready yet; non-fatal.
        }
      }

      // Compose the message based on what was invalidated.
      let message;
      if (hasPanels && shadingHasRun) {
        message =
          'Please fill panels and re-run shading before creating a proposal';
      } else if (hasPanels) {
        message = 'Please fill the panels again before creating a proposal';
      } else {
        message = 'Please re-run shading before creating a proposal';
      }

      displaySnack({ variant: 'warning', message });
      this.setState2x({
        // Only flag shading as needing rerun if it had previously run; we
        // don't want the menu to show "rerun shading" for a design that has
        // never been shaded.
        shadingNeedsRerun: shadingHasRun,
        shadingRerunNoticeShown: true,
        productionHandled: false,
      });
    } else {
      console.log('[checkShadingRerunNeeded] Conditions not met:', {
        shadingHasRun,
        shadingRerunNoticeShown,
        hasPanels,
        hasDisplaySnack: !!displaySnack,
      });
    }
  };

  /**
   * Detects if shading analysis has been run by examining canvas data
   * Checks multiple indicators to determine if shading has been executed:
   * 1. 'color_range' being set with non-default values (indicates shading visualization)
   * 2. Segments having calculated shading values (not all default 1.0 values)
   * 
   * Note: The 'shaded' flag is not reliable and should not be used for detection
   * 
   * @param {Object} canvasData - The canvas data object from the API
   * @returns {boolean} - True if shading has been run, false otherwise
   */
  detectShadingHasRun = (canvasData) => {
    if (!canvasData || typeof canvasData !== 'object') return false;

    // Check 1: color_range is set with non-default values
    // Default is "0,2450", so if it exists and differs, shading likely ran
    if (canvasData.color_range && typeof canvasData.color_range === 'string') {
      const parts = canvasData.color_range.split(',');
      if (parts.length === 2) {
        const [min, max] = parts.map(Number);
        // If both values are valid numbers and not the default "0,2450"
        if (Number.isFinite(min) && Number.isFinite(max) && (min !== 0 || max !== 2450)) {
          return true;
        }
      }
    }

    // Check 3: Segments have calculated shading values (not all default 1.0)
    const segments = canvasData.segments || [];
    if (segments.length === 0) return false;

    // Check if any segment has non-default shading values
    for (const segment of segments) {
      if (!segment || typeof segment !== 'object') continue;

      // Check solar_access array (13 values: 12 months + annual at index 12)
      const solarAccess = segment.solar_access || segment.solarAccess;
      if (Array.isArray(solarAccess) && solarAccess.length >= 13) {
        // If any value is not 1.0, shading has run
        const hasCalculatedValues = solarAccess.some((val) => {
          const num = Number(val);
          return Number.isFinite(num) && num !== 1.0 && num >= 0 && num <= 1;
        });
        if (hasCalculatedValues) return true;
      }

      // Check tsrf array (13 values: 12 months + annual)
      const tsrf = segment.tsrf;
      if (Array.isArray(tsrf) && tsrf.length >= 13) {
        const hasCalculatedValues = tsrf.some((val) => {
          const num = Number(val);
          return Number.isFinite(num) && num !== 1.0 && num >= 0 && num <= 1;
        });
        if (hasCalculatedValues) return true;
      }

      // Check tof array (13 values: 12 months + annual)
      const tof = segment.tof;
      if (Array.isArray(tof) && tof.length >= 13) {
        const hasCalculatedValues = tof.some((val) => {
          const num = Number(val);
          return Number.isFinite(num) && num !== 1.0 && num >= 0 && num <= 1;
        });
        if (hasCalculatedValues) return true;
      }
    }

    return false;
  };

  setShadingIsRunning = (running) => {
    this.setState({ shadingIsRunning: running });
  };

  setServerSideShadingIsRunning = (running) => {
    this.setState({ serverSideShadingIsRunning: running });
  };

  setShadingTexture = (texture, month) => {
    this.setState2x({
      showOverlay: true,
      shadingMonth: month,
      shadingTexture: texture,
    });
  };

  moveRoofVertex = (x, y) => {
    const { segments, selectedObjects } = this.state;
    const newSegments = [...segments];
    Object.keys(selectedObjects.roofVertices).forEach((index) => {
      selectedObjects.fireSetbacks[index].forEach((vi) => {
        const vertex = newSegments[index].geometry[vi];
        vertex[0] += x;
        vertex[1] += y;
      });
    });
    this.setState3x({ segments: newSegments });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  setSegmentPosition = (vertices, roofAttributes) => {
    const { segments } = this.state;
    const newSegments = [...segments];
    const filteredMergedSegments = setSegmentAttributes(
      vertices,
      roofAttributes,
      newSegments
    );
    filteredMergedSegments.forEach((seg, segIndex) => {
      const P = Math.tan(newSegments[segIndex].pitch * 0.0174533) * 12;
      const slopeFactor = Math.sqrt(P * P + 12 * 12) / 12;
      const segArea = areaPolygon(newSegments[segIndex].geometry) * slopeFactor;
      const segAreaSqm = (segArea * samplingRate) / 10000;
      const area = convertUnits(segAreaSqm).from('m2').to('ft2');
      newSegments[segIndex].area = area;
    });
    this.setState3x({ segments: filteredMergedSegments });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
    setTimeout(() => {
      this.setState3x({
        optimizer1IsRunning: false,
        optimizer2IsRunning: false,
      });
    }, 500);
  };

  setSegmentParameters = (changedSegments) => {
    const { segments } = this.state;
    const newSegments = [...segments];
    newSegments.forEach((seg, segIndex) => {
      if (changedSegments[segIndex]) {
        newSegments[segIndex].pvwatts_update = true;
        newSegments[segIndex].pitch = Math.min(
          69.99,
          Number(changedSegments[segIndex].pitch).toFixed(2)
        );
        newSegments[segIndex].azimuth = Number(
          changedSegments[segIndex].azimuth
        ).toFixed(2);
        newSegments[segIndex].height = Number(
          changedSegments[segIndex].height
        ).toFixed(2);
        const P = Math.tan(newSegments[segIndex].pitch * 0.0174533) * 12;
        const slopeFactor = Math.sqrt(P * P + 12 * 12) / 12;
        const segArea =
          areaPolygon(newSegments[segIndex].geometry) * slopeFactor;
        const segAreaSqm = (segArea * samplingRate) / 10000;
        const area = convertUnits(segAreaSqm).from('m2').to('ft2');
        newSegments[segIndex].area = area;
      }
    });
    // const filteredNewSegments = newSegments.filter((el) => (el.pitch < 69 && el.height > 2.5));
    this.setState3x({ segments: newSegments });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
    setTimeout(() => {
      this.setState3x({
        measureIsRunning: false,
        optimizer1IsRunning: false,
        optimizer2IsRunning: false,
      });
    }, 500);
  };

  setPanelsOrientation = (changedSegments) => {
    const { segments } = this.state;
    segments.forEach((seg, segIndex) => {
      const orientation = Number(
        changedSegments[segIndex].panelOrientation || segments[segIndex].azimuth
      );
      if (seg?.panelOrientation !== orientation)
        seg.panelOrientation = orientation;
    });
  };

  changeEdgeTypes = (changedSegments) => {
    const { segments, automaticAzimuth, setbackMode, fireSetbacks } =
      this.state;
    const newSegments = segments.map((segment, si) => {
      const changedSegment = changedSegments?.[si] || {};
      const geometry = Array.isArray(changedSegment?.geometry)
        ? changedSegment.geometry.map((point) =>
            Array.isArray(point) ? [...point] : point
          )
        : Array.isArray(segment?.geometry)
          ? segment.geometry.map((point) =>
              Array.isArray(point) ? [...point] : point
            )
          : [];
      const changedLines = changedSegment?.lines;
      const geometryLength =
        geometry.length ||
        changedLines?.length ||
        segment?.lines?.length ||
        0;
      const lines = Array.isArray(changedLines)
        ? [
            ...changedLines.slice(0, geometryLength),
            ...Array(Math.max(0, geometryLength - changedLines.length)).fill(
              ''
            ),
          ]
        : Array.isArray(segment?.lines)
          ? [...segment.lines]
          : Array(geometryLength).fill('');

      const nextSegment = {
        ...segment,
        ...changedSegment,
        geometry,
        lines,
      };
      if (!automaticAzimuth) nextSegment.azimuth = segment.azimuth;
      else {
        const azimuth = this.getAzimuthForLineLabel(nextSegment);
        if (azimuth !== null) nextSegment.azimuth = azimuth;
      }
      nextSegment.setbacks = this.getSetbacksForLines(
        lines,
        setbackMode,
        fireSetbacks
      );
      return nextSegment;
    });
    this.skipNextSegmentsAutoSave = true;
    this.setStatex({ segments: newSegments }, () => {
      const savePromise = this.context?.CanvasAPI?.saveCanvas
        ? this.context.CanvasAPI.saveCanvas({ segments: newSegments })
        : Promise.resolve();
      savePromise
        .catch((error) => {
          console.warn('Failed to persist edge detection labels', error);
        })
        .finally(() => {
          this.setState3x({ edgeDetectionIsRunning: false });
        });
    });
  };

  setPanelPlacement = (panels) => {
    const prevPanels = this.state.panels;
    const pendingPanel = this.maybeApplyPendingPanelForFirstPlacement(
      prevPanels,
      panels
    );
    const packageUpdate = pendingPanel
      ? {
          package: {
            ...this.state.package,
            panel: pendingPanel,
            pendingPanel: null,
          },
        }
      : {};
    if (this.panelPlacementCallbackArmed) {
      this.saveState({ panels });
      this.setState({ panels, ...packageUpdate });
      if (this.panelPlacementFinalizeTimer) {
        clearTimeout(this.panelPlacementFinalizeTimer);
      }
      this.panelPlacementFinalizeTimer = setTimeout(async () => {
        this.panelPlacementFinalizeTimer = null;
        this.panelPlacementCallbackArmed = false;
        this.setState3x({ panelPlacementIsRunning: false }, false);
        await this.resetProductionForPanelLayoutChange();
        try {
          const latestPanels = this.state.panels;
          const selectedPanelId = this.state.package?.panel?.id;
          const payload = { panels: latestPanels };
          if (pendingPanel?.id !== undefined && pendingPanel?.id !== null)
            payload.selected_panel_id = String(pendingPanel.id);
          else if (selectedPanelId !== undefined && selectedPanelId !== null)
            payload.selected_panel_id = String(selectedPanelId);
          await this.context.CanvasAPI.saveCanvas(payload);
        } catch (e) {
          console.warn('Failed to persist panel placement', e);
        }
        this.unlockPageScroll();
        const { callback: iframeCallback } = this.props;
        if (iframeCallback) iframeCallback('PanelPlacement', '');
        this.resolvePanelPlacementCompletion();
      }, 500);
      return;
    }

    this.setState3x({ panels, ...packageUpdate });
    const productionResetPromise = this.resetProductionForPanelLayoutChange();
    try {
      const selectedPanelId = this.state.package?.panel?.id;
      const payload = { panels };
      if (pendingPanel?.id !== undefined && pendingPanel?.id !== null)
        payload.selected_panel_id = String(pendingPanel.id);
      else if (selectedPanelId !== undefined && selectedPanelId !== null)
        payload.selected_panel_id = String(selectedPanelId);
      Promise.resolve(productionResetPromise)
        .then(() => this.context.CanvasAPI.saveCanvas(payload))
        .catch((e) => {
          console.warn('Failed to persist panel placement', e);
        })
        .finally(() => {
          this.resolvePanelPlacementCompletion();
        });
    } catch (e) {
      console.warn('Failed to persist panel placement', e);
      this.resolvePanelPlacementCompletion();
    }
    setTimeout(() => {
      this.setState3x({ panelPlacementIsRunning: false });
      this.unlockPageScroll();
    }, 500);
    const { iframe, callback: iframeCallback } = this.props;
    if (iframeCallback) iframeCallback('PanelPlacement', '');
  };

  handleObstacleRadius = (oi, v) => {
    const { obstacles } = this.state;
    const newObstacle = [...obstacles];
    newObstacle[oi].radius = parseFloat(v);
    this.setState3x({ obstacles: newObstacle });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  handleObstacleHeight = (oi, v) => {
    const { obstacles } = this.state;
    const newObstacle = [...obstacles];
    newObstacle[oi].height = parseFloat(v);
    this.setState3x({ obstacles: newObstacle });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  handleObstacleSetback = (oi, v) => {
    const { obstacles } = this.state;
    const newObstacle = [...obstacles];
    newObstacle[oi].setback = parseFloat(v);
    this.setState3x({ obstacles: newObstacle });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  handleObstacleOrientation = (oi, v) => {
    const { obstacles } = this.state;
    const newObstacle = [...obstacles];
    newObstacle[oi].orientation = v;
    newObstacle[oi].height = 1;
    this.setState3x({ obstacles: newObstacle });
    // Use setTimeout to check after state update completes
    setTimeout(() => this.checkShadingRerunNeeded(), 0);
  };

  setSegmentShadingFactors = ([tsrf, tof, solarAccess]) => {
    const { segments } = this.state;
    const newSegments = [...segments];
    newSegments.forEach((seg, segIndex) => {
      seg.solarAccess = solarAccess[segIndex];
      seg.tsrf = tsrf[segIndex];
      seg.tof = tof[segIndex];
    });
    // Mark shading as run when shading factors are set and reset rerun flags
    console.log('[setSegmentShadingFactors] Setting shadingHasRun=true');
    this.setState3x({
      segments: newSegments,
      shadingHasRun: true,
      shadingNeedsRerun: false,
      shadingRerunNoticeShown: false,
    });
  };

  setSegmentShadingFactorsServerSide = (segmentsShadingFactors) => {
    const { segments } = this.state;
    if (
      !Array.isArray(segmentsShadingFactors) ||
      segmentsShadingFactors.length < segments.length
    ) {
      console.warn('[setSegmentShadingFactorsServerSide] Invalid server shading factors', {
        expectedSegments: segments.length,
        receivedSegments: Array.isArray(segmentsShadingFactors)
          ? segmentsShadingFactors.length
          : null,
      });
      return;
    }
    const newSegments = [...segments];
    newSegments.forEach((seg, segIndex) => {
      const serverSegment = segmentsShadingFactors[segIndex];
      seg.solarAccess = serverSegment.solarAccess;
      seg.tsrf = serverSegment.tsrf;
      seg.tof = serverSegment.tof;
    });
    // Mark shading as run when shading factors are set and reset rerun flags
    console.log('[setSegmentShadingFactorsServerSide] Setting shadingHasRun=true');
    this.setState3x({
      segments: newSegments,
      shadingHasRun: true,
      shadingNeedsRerun: false,
      shadingRerunNoticeShown: false,
    });
    this.context.CanvasAPI.saveCanvas({ segments: newSegments }).catch((e) => {
      console.warn('Failed to persist server-side shading factors', e);
    });
  };

  setOptPitchAndAzimuth = (pitch, azimuth) => {
    const { updateLead } = this.context;
    const optPitchAndAzimuth = { pitch, azimuth };
    updateLead({ property: { optimal_roof: optPitchAndAzimuth } }, true);
  };

  setDSMParams = (DSMParams) => {
    const { updateLead } = this.context;
    updateLead({ property: { dsm_params: DSMParams } }, true);
  };

  goFullScreen = (e) => {
    this.setState({ isFullScreen: e });
  };

  capture_image = async () => {
    const image_data = await this.canvas3DElement.exportToJPG();
    return image_data;
  };

  newAnnotation = (position, type, params = {}) => {
    const { annotations } = this.state;
    const typeName = type.toLowerCase();
    const typeCaption = typeName == 'custom' ? 'Annotation' : type;
    const typeLength = annotations.filter(
      (a) => a.type.toLowerCase() === typeName
    ).length;
    const newAnnotations = [...annotations];
    newAnnotations.push({
      position: { ...position },
      text: `${typeCaption}_${typeLength + 1}`,
      type,
      ...params,
    });
    this.setAnnotations(newAnnotations);
  };

  setAnnotations = (annotations) => {
    this.setState3x({ annotations });
    this.context.CanvasAPI.saveCanvas({ annotations: annotations });
  };

  removeAnnotation = (index) => {
    const { annotations } = this.state;
    const newAnnotations = [...annotations];
    newAnnotations.splice(index, 1);
    this.setAnnotations(newAnnotations);
  };

  handleCanvasData = (data) => {
    const { segments, obstacles, trees, panels, annotations } = this.state;
    const newSegments = data.segments || segments;
    const newObstacles = data.obstacles || obstacles;
    const newTrees = data.trees || trees;
    // const newPanels = data.panels || panels;
    // const newAnnotations = data.annotations || annotations;

    newSegments.forEach((seg, segIndex) => {
      seg.azimuth = Number(seg.azimuth);
      seg.pitch = Number(seg.pitch);
      seg.height = Number(seg.height);
      seg.lines = Array(seg.geometry.length).fill('');
      seg.solarAccess = seg.solar_access;
      if (seg.acMonthly)
        seg.acAnnual = seg.acMonthly.reduce((a, b) => a + b, 0);
      else {
        seg.acMonthly = Array(12).fill(0);
        seg.acAnnual = 0;
      }
      seg.area = 0;
    });
    newObstacles.forEach((obstacle) => {
      obstacle.radius = Number(obstacle.radius);
      obstacle.height = Number(obstacle.height);
      obstacle.setback = Number(obstacle.setback);
    });
    newTrees.forEach((tree) => {
      tree.radius = Number(tree.radius);
      tree.height = Number(tree.height);
      const rawTrunkHeight = tree.trunkHeight ?? tree.trunk_height;
      if (rawTrunkHeight !== undefined && rawTrunkHeight !== null) {
        const parsedTrunkHeight = Number(rawTrunkHeight);
        tree.trunkHeight = Number.isNaN(parsedTrunkHeight)
          ? undefined
          : parsedTrunkHeight;
      } else if (tree.trunkHeight !== undefined) {
        delete tree.trunkHeight;
      }
      if (tree.trunk_height !== undefined) delete tree.trunk_height;
      if (
        Number.isFinite(tree.trunkHeight) &&
        Number.isFinite(tree.height) &&
        tree.trunkHeight > tree.height
      ) {
        tree.trunkHeight = tree.height;
      }
    });

    this.setState3x({
      segments: newSegments,
      obstacles: newObstacles,
      trees: newTrees,
      panels: [],
      annotations: [],
    });
  };

  translateCanvas = async (
    offsetX,
    offsetY,
    flipX,
    flipY,
    translateRoofs,
    translateObstacles,
    translateTrees
  ) => {
    const { CanvasAPI } = this.context;
    const { segments, obstacles, trees } = this.state;
    const scaleX = flipX ? -1 : 1;
    const scaleY = flipY ? -1 : 1;
    const w2 = CANVAS_SIZE / 2;
    let newSegments = segments;
    let newObstacles = obstacles;
    let newTrees = trees;
    if (translateRoofs) {
      newSegments = segments.map((s) => {
        const newGeometry = s.geometry.map((v) => [
          (v[0] - w2) * scaleX + offsetX + w2,
          (v[1] - w2) * scaleY + offsetY + w2,
        ]);
        return { ...s, geometry: newGeometry };
      });
    }
    if (translateObstacles) {
      newObstacles = obstacles.map((o) => {
        const newGeometry = o.geometry.map((v) => [
          (v[0] - w2) * scaleX + offsetX + w2,
          (v[1] - w2) * scaleY + offsetY + w2,
        ]);
        return { ...o, geometry: newGeometry };
      });
    }
    if (translateTrees) {
      newTrees = trees.map((t) => {
        const newGeometry = t.geometry.map((v) => [
          (v[0] - w2) * scaleX + offsetX + w2,
          (v[1] - w2) * scaleY + offsetY + w2,
        ]);
        return { ...t, geometry: newGeometry };
      });
    }
    this.setState3x({
      segments: newSegments,
      obstacles: newObstacles,
      trees: newTrees,
    });
    CanvasAPI.saveCanvas({
      segments: newSegments,
      obstacles: newObstacles,
      trees: newTrees,
    });
  };

  getUIState = () => {
    const {
      currentAccordion,
      tool,
      designType,
      buffer,
      rowSpacing,
      panelTilt,
      azimuthRestriction,
      setbackMode,
      fireSetbacks,
      mode,
      moveMode,
      selectedLabel,
      automaticAzimuth,
      pitch_in_degrees,
      viewMode,
      selectionMode,
      coordinates,
      optPitchAndAzimuth,
      segments,
      obstacles,
      trees,
      panels,
      annotations,
      losses,
      measureIsRunning,
      optimizer1IsRunning,
      optimizer2IsRunning,
      edgeDetectionIsRunning,
      AIIsRunning,
      panelPlacementIsRunning,
      panelPlacementFillMode,
      panelPlacementLayoutMode,
      panelPlacementOnlySelectedRoof,
      shadingMonth,
      shadingStatus,
      shadingIsRunning,
      serverSideShadingIsRunning,
      solarPanels,
      colorRange,
      roofAreasSqFt,
      roofAreasUnit,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
      weatherDatasetSelected,
      weatherDatasetFetchedPath,
      resolution,
      selectedRoof,
      selectedPanels,
    } = this.state;

    const hasDSM = Boolean(this.context?.lead?.property?.dsm);

    const data = {
      currentAccordion,
      tool,
      designType,
      buffer,
      rowSpacing,
      panelTilt,
      azimuthRestriction,
      setbackMode,
      fireSetbacks,
      mode,
      moveMode,
      selectedLabel,
      automaticAzimuth,
      hasDSM,
      pitchInDegrees: pitch_in_degrees ?? true,
      viewMode,
      selectionMode,
      coordinates,
      optPitchAndAzimuth,
      segments,
      obstacles,
      trees,
      panels,
      annotations,
      losses,
      package: this.state.package,
      measureIsRunning,
      optimizer1IsRunning,
      edgeDetectionIsRunning,
      AIIsRunning,
      panelPlacementIsRunning,
      panelPlacementFillMode,
      panelPlacementLayoutMode,
      panelPlacementOnlySelectedRoof,
      shadingMonth,
      shadingStatus,
      shadingIsRunning,
      serverSideShadingIsRunning,
      solarPanels,
      colorRange,
      roofAreasSqFt,
      roofAreasUnit,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
      weatherDatasetSelected,
      weatherDatasetFetchedPath,
      resolution,
      selectedRoof,
      selectedPanels,
    };

    return data;
  };

  setStatex = (data, callback, stateNames) => {
    const isFunc = data instanceof Function;
    if (isFunc) this.saveState(data, stateNames, true);
    else this.saveState(data);

    if (!callback) this.setState(data);
    else this.setState(data, callback);

    if (isFunc && stateNames) {
      setTimeout(() => {
        const stateValues = Object.assign(
          {},
          ...stateNames.map((x) => ({ [x]: this.state[x] }))
        );
        this.callbackDesignerIframe(stateValues);
        this.saveState(stateValues, stateNames, false, true);
      }, 500);
    } else if (typeof data === 'object') {
      this.callbackDesignerIframe(data);
    }
  };

  setState2x = (data, callback) => {
    this.saveState(data);
    this.setState(data, callback);
    this.callbackDesignerIframe(data);
  };

  setState3x = (data, saveState = true) => {
    if (saveState) this.saveState(data);
    this.setState(data);
    this.callbackDesignerIframe(data, false);
  };

  callbackDesignerIframe = (data, check = true) => {
    const { iframe, designFrame, callback: iframeCallback } = this.props;
    if (iframe && designFrame && iframeCallback) {
      const resolvedAzimuthRestriction =
        data?.azimuthRestriction ?? this.state.azimuthRestriction;
      if (!check) {
        const payload =
          resolvedAzimuthRestriction === undefined
            ? data
            : { ...data, azimuthRestriction: resolvedAzimuthRestriction };
        iframeCallback('State', payload);
      } else {
        const {
          tool,
          designType,
          segments,
          obstacles,
          trees,
          panels,
          annotations,
          solarPanels,
          buffer,
          rowSpacing,
          panelTilt,
          azimuthRestriction,
          automaticAzimuth,
          setbackMode,
          fireSetbacks,
          mode,
          selectedLabel,
          moveMode,
          currentAccordion,
          viewMode,
          selectionMode,
          coordinates,
          optPitchAndAzimuth,
          pitchUnit,
          shadingMonth,
          shadingStatus,
          package: package_,
          selectedRoof,
          weatherDatasetSelected,
          weatherDatasetFetchedPath,
        } = data;

        const filteredData = {
          tool,
          designType,
          segments,
          obstacles,
          trees,
          panels,
          annotations,
          // solarPanels,
          buffer,
          rowSpacing,
          panelTilt,
          azimuthRestriction:
            azimuthRestriction === undefined
              ? resolvedAzimuthRestriction
              : azimuthRestriction,
          automaticAzimuth,
          setbackMode,
          fireSetbacks,
          mode,
          selectedLabel,
          moveMode,
          currentAccordion,
          viewMode,
          selectionMode,
          coordinates,
          optPitchAndAzimuth,
          pitchUnit,
          shadingMonth,
          shadingStatus,
          package: package_,
          selectedRoof,
          weatherDatasetSelected,
          weatherDatasetFetchedPath,
        };

        Object.keys(filteredData).forEach((key) =>
          filteredData[key] === undefined ? delete filteredData[key] : {}
        );
        if (filteredData) iframeCallback('State', filteredData);
      }
    }
  };

  setHistoryEnabled = (enabled) => {
    this.historyState.state = enabled ? 'enabled' : 'disabled';
  };

  undo = () => {
    if (
      this.histories.length &&
      this.historyState.current >= 0 &&
      this.historyState.state !== 'disabled'
    ) {
      this.historyState.current = Math.max(this.historyState.current - 1, 0);
      this.applyState(true);
    }
  };

  redo = () => {
    if (
      this.histories.length &&
      this.historyState.current < this.histories.length &&
      this.historyState.state !== 'disabled'
    ) {
      this.historyState.current = Math.min(
        this.historyState.current + 1,
        this.histories.length - 1
      );
      this.applyState(false);
    }
  };

  applyState = () => {
    const historyItem = this.histories[this.historyState.current];
    if (!historyItem) return;
    const data = {};
    Object.keys(historyItem).forEach((k) => {
      if (historyItem[k] !== undefined) data[k] = JSON.parse(historyItem[k]);
    });
    this.setState3x(data, false);
  };

  saveState = (data, keys = null, onlySaveOld = false, updateOld = false) => {
    if (!(this.canvas3DElement && this.canvas3DElement.isLoaded)) return;
    const validKeys = [
      'segments',
      'obstacles',
      'trees',
      'annotations',
      'setbackMode',
      'fireSetbacks',
    ];
    const previousHistoryItem = {};
    const historyItem = {};
    const maxSteps = 100;
    if (!keys) keys = Object.keys(data);
    keys.forEach((key) => {
      if (validKeys.includes(key)) {
        const oldValue = JSON.stringify(this.state[key]);
        if (oldValue === undefined) return;
        if (onlySaveOld) {
          previousHistoryItem[key] = oldValue;
          return;
        }
        const newValue = JSON.stringify(data[key]);
        if (newValue === undefined) return;
        if (updateOld) historyItem[key] = newValue;
        else if (oldValue !== newValue) {
          previousHistoryItem[key] = oldValue;
          historyItem[key] = newValue;
        }
      }
    });
    const currentCount = Object.keys(historyItem).length;
    const previousCount = Object.keys(previousHistoryItem).length;
    if (currentCount || previousCount) {
      const initHistory = this.histories.length == 0;
      if (!initHistory) {
        const oldKeys = Object.keys(this.histories[this.histories.length - 1]);
        const mergeState = keys.some((x) => !oldKeys.includes(x));
        if (mergeState)
          this.histories[this.histories.length - 1] = {
            ...this.histories[this.histories.length - 1],
            ...previousHistoryItem,
          };
      }
      const items = [];
      if (previousCount && initHistory) items.push(previousHistoryItem);
      if (currentCount) items.push(historyItem);
      if (items.length) {
        this.histories.splice(
          this.historyState.current + 1,
          maxSteps,
          ...items
        );
        if (this.histories.length > maxSteps) this.histories.shift();
        this.historyState.current = this.histories.length - 1;
      }
    }
  };

  checkIsLoaded = () => {
    return this.canvas3DElement.isLoaded;
  };

  checkIsShadingRunning = () => {
    return this.state.shadingIsRunning;
  };

  checkIsPVWattsHandled = () => {
    return this.state.pvwattsHandled;
  };

  setPanelPlacementFillMode = (fillMode) => {
    this.setState3x({ panelPlacementFillMode: fillMode });
  };

  setPanelPlacementLayoutMode = (fillMode) => {
    this.setState3x({ panelPlacementLayoutMode: fillMode });
  };

  setPanelPlacementOnlySelectedRoof = (onlySelectedRoof) => {
    // Always honour the user's toggle click. The previous ON-without-
    // selection guard, combined with a bidirectional derivation in
    // componentDidUpdate, made the toggle unresponsive in the common
    // no-selection state — after toggling OFF (which clears selection),
    // there's no selection to re-enable it from. The derivation is now
    // one-directional (only auto-ON on selection); the OFF branch here
    // continues to mirror the requirement that toggling OFF clears the
    // current roof-segment selection.
    const nextState = { panelPlacementOnlySelectedRoof: onlySelectedRoof };
    if (onlySelectedRoof) {
      nextState.mode = 'select';
      nextState.selectionMode = 'segment';
    }
    if (!onlySelectedRoof) {
      nextState.selectedRoof = -1;
      nextState.selectedObjects = {
        ...this.state.selectedObjects,
        roofSegments: [],
      };
    }
    this.setState3x(nextState);
    try {
      if (this.context.CanvasAPI?.saveCanvas) this.context.CanvasAPI.saveCanvas({ panelPlacementOnlySelectedRoof: onlySelectedRoof });
    } catch (e) {}
  };

  placeGroundMount = (gridOption, segmentOption, panelOptions) => {
    const { segments } = this.state;

    const { rows, columns } = gridOption;
    const { height, pitch, azimuth } = segmentOption;
    const {
      solarPanel,
      buffer,
      rowSpacing,
      panelTilt,
      panelPlacementFillMode,
      panelPlacementLayoutMode,
    } = panelOptions;
    const { resolution } = this.state;

    const mmToSceneUnit = (mm, resolution, scale = 1) =>
      Math.max(0.001, mm / 10 / Number(resolution)) * scale || 0.001;
    const length = mmToSceneUnit(Number(solarPanel.length), resolution);
    const width = mmToSceneUnit(Number(solarPanel.width), resolution);

    const [x, y] = [150, 150];
    const w = (length + buffer) * columns;
    const h = (width + buffer) * rows;

    const geom = [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ];

    const segment = {
      pitch: Number(pitch),
      azimuth: Number(azimuth),
      height: Number(height),
      lines: Array(geom.length).fill('Eave'),
    };

    this.handleNewSegment({ geometry: geom, ...segment });
  };

  render() {
    const {
      iframe,
      designFrame,
      companyName,
      callback: iframeCallback,
      context: iframeContext = {},
    } = this.props;

    const { panelNotSet } = this.state;
    if (panelNotSet) return <></>;

    // if (Object.keys(this.state.package).length === 0) {
    //   return !iframe ? (
    //     <Alert variant="warning">
    //       <Alert.Heading>No Installation Package Was Found</Alert.Heading>
    //       <p>
    //         It looks like you tried to create a design without having configured an
    //         installation package in your settings.
    //         <br />
    //         It will only take a minute, you&apos;re almost there!
    //       </p>
    //     </Alert>
    //   ) : <p>No Installation Package Was Found</p>;
    // }

    const {
      lead: { property },
      validUtility,
    } = this.context;
    if (!property?.coordinates && !iframe) {
      // && property?.effectiveRate === undefined
      // && property?.fixedCharge === undefined)) {
      return (
        <Alert className="w-75 m-5" variant="info">
          <InfoCircle size={20} className="mr-2" />
          Property Details, and Utility Details must be filled out before
          creating a design.
        </Alert>
      );
    }

    const hasCanvasDsm = Object.prototype.hasOwnProperty.call(
      this.state,
      'dsm'
    );
    const activeDsm = hasCanvasDsm ? this.state.dsm : property.dsm;
    const hasDSM = activeDsm != null;

    const {
      isFullScreen,
      overlay,
      showOverlay,
      scale,
      resolution,
      currentAccordion: accordion,
      tool,
      segments,
      obstacles,
      trees,
      panels,
      annotations,
      buffered,
      selectedRoof,
      selectedLabel,
      selectedPanels,
      shade,
      buffer,
      rowSpacing,
      panelTilt,
      azimuthRestriction,
      fireSetbacks,
      mode,
      moveMode,
      setbackMode,
      polyEdit,
      focusedPolygonToEdit,
      colorRange,
      designType,
      package: installationPackage,
      shadingMonth,
      shadingStatus,
      viewMode,
      coordinates,
      setback,
      selectionMode,
      selectedObjects,
      shadingTexture,
      shadingIsRunning,
      serverSideShadingIsRunning,
      optimizer1IsRunning,
      optimizer2IsRunning,
      edgeDetectionIsRunning,
      measureIsRunning,
      panelPlacementIsRunning,
      AIIsRunning,
      solarPanels,
      inverters,
      automaticAzimuth,
      updateRGBTextureIsRunning,
      panelPlacementFillMode,
      panelPlacementLayoutMode,
      panelPlacementOnlySelectedRoof,
      hasAutoCalculatedInverterQuantity,
      // Production state
      productionIsRunning,
      productionHandled,
      totalAnnualKwh,
      totalMonthlyKwh,
      totalClippingKwh,
      totalMonthlyClippingKwh,
      dcAcRatio,
      stringingData,
      invertersUsed,
      productionPerPanel,
      selectedPanelCount,
      productionHardware,
      // Target production state
      useTargetProduction,
      targetProductionKwh,
      // Auto quantity state
      primaryInverterAutoQuantity,
      // Snapshot of additional inverters saved on the Installation Package,
      // used to lazily prefill rows when the user clicks "+ Add" in the
      // Production accordion.
      packageAdditionalInverters,
    } = this.state;

    // Destructure hardware from installation package
    const {
      panel: panelHardware,
      inverter: inverterHardware,
      additional_inverters: additionalInvertersHardware = [],
    } = installationPackage || {};

    // value that is passed to context indicating id client's device is running iOS
    const isIos = ios();

    const fullAddress = `${property.street}, ${property.city}, ${property.state}`;
    const canvasData = canvasKeys.includes(accordion)
      ? this.state[accordion]
      : [];
    const ISODate = new Date().toISOString();
    const overlaySrc = `${overlay}?iso=${ISODate}`;
    const size = CANVAS_SIZE * scale;

    const show2D = viewMode === '2D';
    const showPlacePanels = konvaKeys.includes(accordion);
    this.lastRenderedPlacedPanelCount = this.countPlacedPanels(panels);
    const currentPanel = installationPackage?.pendingPanel || panelHardware;
    const appliedPanel = panelHardware;

    const providerValues = {
      coordinates,
      resolution: property.resolution,
      buffer,
      rowSpacing,
      panelTilt,
      azimuthRestriction,
      setback,
      accordion,
      panelType: currentPanel,
      selectionMode,
      selectedObjects,
      shadingIsRunning,
      serverSideShadingIsRunning,
      optimizer1IsRunning,
      optimizer2IsRunning,
      edgeDetectionIsRunning,
      measureIsRunning,
      panelPlacementIsRunning,
      AIIsRunning,
      optPitchAndAzimuth: property.optimal_roof,
      dsmParams: property.dsm_params,
      optPOA: property.optimal_poa,
      shadingMonth,
      shadingStatus,
      viewMode,
      iframe,
      designFrame,
      mode,
      tool,
      selectedLabel,
      setRoofLineLabel: this.setRoofLineLabel,
      ios: isIos,
      moveMode,
      automaticAzimuth,
      designType,
      updateRGBTextureIsRunning,
      annotations,
      panelPlacementFillMode,
      panelPlacementLayoutMode,
      panelPlacementOnlySelectedRoof,
      setSelectionMode: this.setSelectionMode,
      addToSelectedObjects: this.addToSelectedObjects,
      addToSelectedObjectsBulk: this.addToSelectedObjectsBulk,
      removeFromSelectedObjects: this.removeFromSelectedObjects,
      clearSelection: this.clearSelection,
      setHeight: this.setHeight,
      setPitch: this.setPitch,
      setSetback: this.setSetback,
      deleteSelectedObjects: this.deleteSelectedObjects,
      setSegmentPosition: this.setSegmentPosition,
      changeEdgeTypes: this.changeEdgeTypes,
      setPanelPlacement: this.setPanelPlacement,
      moveRoofVertex: this.moveRoofVertex,
      setSegmentShadingFactors: this.setSegmentShadingFactors,
      setSegmentShadingFactorsServerSide:
        this.setSegmentShadingFactorsServerSide,
      setShadingTexture: this.setShadingTexture,
      setShadingStatus: this.setShadingStatus,
      setShadingIsRunning: this.setShadingIsRunning,
      setServerSideShadingIsRunning: this.setServerSideShadingIsRunning,
      setOptPitchAndAzimuth: this.setOptPitchAndAzimuth,
      setSegmentParameters: this.setSegmentParameters,
      setDSMParams: this.setDSMParams,
      automaticAzimuthMode: this.automaticAzimuthMode,
      setPanelsOrientation: this.setPanelsOrientation,
      setViewMode: this.switchView,
      setMode: this.handleMode,
      setMoveMode: this.handleMoveMode,
      handleNewShape: this.handleNewShape,
      handleSaveEdits: this.handleSaveEdits,
      handleAddPointToPolygon: this.handleAddPointToPolygon,
      handleRemovePointFromPolygon: this.handleRemovePointFromPolygon,
      handleDeleteShape: this.handleDeleteShape,
      handleLineLabel: this.handleLineLabel,
      mapsLink: this.mapsLink,
      undo: this.undo,
      redo: this.redo,
      setHistoryEnabled: this.setHistoryEnabled,
      setUpdateRGBTextureIsRunning: this.setUpdateRGBTextureIsRunning,
      setAnnotations: this.setAnnotations,
      newAnnotation: this.newAnnotation,
      handleCanvasData: this.handleCanvasData,
      translateCanvas: this.translateCanvas,
      setPanelPlacementFillMode: this.setPanelPlacementFillMode,
      setPanelPlacementLayoutMode: this.setPanelPlacementLayoutMode,
      setPanelPlacementOnlySelectedRoof: this.setPanelPlacementOnlySelectedRoof,
      clearPanels: this.clearPanels,
      placeGroundMount: this.placeGroundMount,
    };

    const viewerHeight = !iframe ? CANVAS_SIZE * SCALE + 2 : '100%';

    const handlers = isIos
      ? {
        onPointerDown: this.handleMouseDown,
        onPointerMove: this.handleMouseMove,
        onPointerUp: this.handleMouseUp,
        onPointerEnter: this.handleMouseEnter,
        onPointerLeave: this.handleMouseLeave,
      }
      : {
        onMouseDown: this.handleMouseDown,
        onMouseMove: this.handleMouseMove,
        onMouseUp: this.handleMouseUp,
        onMouseEnter: this.handleMouseEnter,
        onMouseLeave: this.handleMouseLeave,
      };

    // TODO: Remove after migrating old data from /media to /imagery
    function parsePropertyDataURL(property, type) {
      try {
        if (!property) return null;
        const imagery = property[type];
        if (imagery && imagery.startsWith('pd_')) {
          if (type == 'rgb') {
            return `${process.env.REACT_APP_PROPERTY_DATA_RGB_PUBLIC_URL}/${imagery}/rgb.tiff`;
          } else if (type == 'dsm') {
            return `${process.env.REACT_APP_PROPERTY_DATA_DSM_PUBLIC_URL}/${imagery}/dsm.tiff`;
          }
        } else {
          return imagery.startsWith('http') ? imagery : `/media/${imagery}`;
        }
      } catch (error) {
        console.log('Error parsing property imagery', error);
        return null;
      }
    }

    const hasCanvasRgb = Object.prototype.hasOwnProperty.call(
      this.state,
      'rgb'
    );
    const effectiveProperty = property
      ? {
          rgb: hasCanvasRgb ? this.state.rgb : property.rgb,
          dsm: activeDsm,
        }
      : null;
    let rgbImage = undefined;
    let dsmImage = undefined;
    if (effectiveProperty) {
      rgbImage = parsePropertyDataURL(effectiveProperty, 'rgb');
      dsmImage = parsePropertyDataURL(effectiveProperty, 'dsm');
    }

    const viewer = (
      <div
        aria-hidden="true"
        ref={(e) => {
          this.container = e;
        }}
        className="mw-100 position-relative rounded border border-primary overflow-auto"
        onWheel={this.handleMouseWheel}
        {...handlers}
        style={{
          maxHeight: viewerHeight,
          height: viewerHeight,
        }}
      >
        <Canvas3D
          ref={(canvas3D) => {
            this.canvas3DElement = canvas3D;
          }}
          visible
          imgSrc={rgbImage}
          dsmSrc={dsmImage}
          segments={segments}
          obstacles={obstacles}
          trees={trees}
          panels={panels}
          annotations={annotations || []}
          size={size}
          iframeCallback={iframeCallback}
          initViewMode={'2D'}
          // Stringing overlay (only when Production accordion is open)
          stringingData={this.state.stringingData || []}
          showStringing={Boolean(
            this.state.currentAccordion === 'production' &&
            Array.isArray(this.state.stringingData) &&
            this.state.stringingData.length > 0
          )}
          forceShowStringing={false}
          stringingPanelIndexMode={this.state.stringingPanelIndexMode || 'all'}
          stringingSelectedPanelIndexMap={
            this.state.stringingSelectedPanelIndexMap || null
          }
          // LeadContext props
          lead={this.context.lead}
          user={this.context.user}
          displaySnack={this.context.displaySnack}
          saveShadingsAPI={this.context.saveShadingsAPI}
          runShadingsAPI={this.context.runShadingsAPI}
          runShadingRequiredClientDataAPI={
            this.context.runShadingRequiredClientDataAPI
          }
          exportDesignAPI={this.context.exportDesignAPI}
        />
        {iframe &&
          companyName !== 'Astrawatt' &&
          companyName !== 'demand-iq' && <IFrameLogo />}
      </div>
    );

    if (iframe) {
      return (
        <DesignContext.Provider value={providerValues}>
          <Fullscreen
            isEnter={isFullScreen}
            onChange={(e) => this.setState({ isFullScreen: e })}
          >
            {viewer}
          </Fullscreen>
        </DesignContext.Provider>
      );
    }

    if (iframe && designFrame) {
      //
      return (
        <DesignContext.Provider value={providerValues}>
          <Row className="bg-light py-3 full-screenable-node font-vh g-0">
            <Col lg={8} md={12} s={12} xs={12} className="mb-5">
              <Controls
                mode={mode}
                moveMode={moveMode}
                setMode={this.handleMode}
                setMoveMode={this.handleMoveMode}
                resetScale={this.handleResetScale}
                scaleUp={this.handleScaleUp}
                scaleDown={this.handleScaleDown}
                mapsLink={this.mapsLink}
                viewMode={konvaKeys.includes(accordion) ? '3D' : viewMode}
                switchView={this.switchView}
              />
              {/* <Fullscreen isEnter={isFullScreen} onChange={(e) => this.setStatex({ isFullScreen: e })}> */}
              <div className="mh-100" style={{ height: 600 }}>
                {viewer}
              </div>
              {/* </Fullscreen> */}
              {/* <ControlsBottom
                saveCanvas={this.handleSaveAll}
                cleanCanvas={this.cleanCanvas}
                disabled={!accordion || konvaKeys.includes(accordion)}
              /> */}
            </Col>
            <Col lg={4} md={12} s={12} xs={12} className="mt-5 mw-100">
              <Menu
                activeKey={accordion}
                designType={designType}
                hasDSM={hasDSM}
                shaded={shade}
                buffer={buffer}
                rowSpacing={rowSpacing}
                panelTilt={panelTilt}
                azimuthRestriction={azimuthRestriction}
                fireSetbacks={fireSetbacks}
                setbackMode={setbackMode}
                shadingIsRunning={shadingIsRunning}
                serverSideShadingIsRunning={serverSideShadingIsRunning}
                setbackModeHandler={this.handleSetbackMode}
                fireSetbackHandler={this.handleFireSetbacks}
                colorRange={colorRange}
                currentAccordionHandler={this.handleCurrentAccordion}
                currentTabHandler={this.handleSegmentTabs}
                tool={tool}
                toolHandler={this.handleTool}
                selectedLabel={selectedLabel}
                selectedRoof={selectedRoof}
                selectedPanels={selectedPanels}
                selectedLabelHandler={this.handleSelectedLabel}
                clearLabelsHandler={this.clearLabelsHandler}
                deleteShapeHandler={this.handleDeleteShape}
                addPointToPolygonHandler={this.handleAddPointToPolygonFromMenu}
                removePointFromPolygonHandler={
                  this.handleRemovePointFromPolygonFromMenu
                }
                obstacleHeightHandler={this.handleObstacleHeight}
                obstacleRadiusHandler={this.handleObstacleRadius}
                obstacleSetbackHandler={this.handleObstacleSetback}
                obstacleOrientationHandler={this.handleObstacleOrientation}
                newPanelHandler={this.handleNewPanel}
                deletePanelHandler={this.handleDeletePanel}
                rotatePanelHandler={this.handleRotatePanel}
                setPVwattsLosses={this.setPVwattsLosses}
                losses={this.state.losses}
                segments={segments}
                resolution={resolution}
                segmentAzimuthHandler={this.handleSegmentAzimuth}
                segmentPitchAndAreaHandler={this.handleSegmentPitchAndArea}
                segmentEaveHeightHandler={this.handleSegmentEaveHeight}
                treeUpdateHandler={this.handleTreeUpdate}
                pitchInDegrees={this.state.pitch_in_degrees ?? true}
                onPitchInDegreesChange={this.handlePitchInDegreesChange}
                obstacles={obstacles}
                trees={trees}
                panels={panels}
                placementHandler={this.handlePlacement}
                shadingHandler={this.handleShading}
                weatherDataset={this.state.weatherDatasetSelected}
                setWeatherDataset={this.setWeatherDatasetSelected}
                weatherDatasetFetchedPath={this.state.weatherDatasetFetchedPath}
                weatherDatasetReady={(() => {
                  const p = this.state.weatherDatasetFetchedPath;
                  const sel = this.state.weatherDatasetSelected;
                  if (!p || !sel) return false;
                  const base = (String(p).split('/').pop() || '').trim();
                  const expectedFilename = `${sel}.csv`;
                  return base === expectedFilename;
                })()}
                onWeatherDatasetFetched={this.handleWeatherDatasetFetched}
                measureHandler={this.handleMeasure}
                runOptimizationHandler={this.runOptimizationHandler}
                runEdgeDetectionHandler={this.runEdgeDetectionHandler}
                runAIHandler={this.runAIHandler}
                proposalHandler={this.handleProposal}
                isCreatingProposal={this.state.isCreatingProposal}
                shadingNeedsRerun={this.state.shadingNeedsRerun}
                shadingHasRun={this.state.shadingHasRun}
                panelAzimuthHandler={this.handlePanelAzimuth}
                currentPanel={currentPanel}
                appliedPanel={appliedPanel}
                currentInverter={inverterHardware}
                currentInverterQuantity={
                  installationPackage?.inverterQuantity || 1
                }
                hasAutoCalculatedInverterQuantity={
                  hasAutoCalculatedInverterQuantity
                }
                currentAdditionalInverters={additionalInvertersHardware}
                packageAdditionalInverters={packageAdditionalInverters}
                changePanelHardware={this.changePanelHardware}
                changeInverterHardware={this.changeInverterHardware}
                changeInverterQuantity={this.changeInverterQuantity}
                changeAdditionalInverters={this.changeAdditionalInverters}
                setbackHandler={this.handleSetback}
                bufferHandler={this.handleBuffer}
                rowSpacingHandler={this.handleRowSpacing}
                panelTiltHandler={this.handlePanelTilt}
                azimuthRestrictionHandler={this.handleAzimuthRestriction}
                showOverlay={showOverlay}
                toggleOverlay={this.toggleOverlay}
                shadingMonth={shadingMonth}
                shadingStatus={shadingStatus}
                roofAreasSqFt={this.state.roofAreasSqFt}
                roofAreasUnit={this.state.roofAreasUnit}
                solarPanels={solarPanels}
                inverters={inverters}
                panelPlacementFillMode={panelPlacementFillMode}
                setPanelPlacementFillMode={this.setPanelPlacementFillMode}
                panelPlacementLayoutMode={panelPlacementLayoutMode}
                setPanelPlacementLayoutMode={this.setPanelPlacementLayoutMode}
                panelPlacementOnlySelectedRoof={panelPlacementOnlySelectedRoof}
                setPanelPlacementOnlySelectedRoof={
                  this.setPanelPlacementOnlySelectedRoof
                }
                clearPanels={this.clearPanels}
                // Production props
                productionIsRunning={productionIsRunning}
                productionHandled={productionHandled}
                totalAnnualKwh={totalAnnualKwh}
                totalClippingKwh={totalClippingKwh}
                dcAcRatio={dcAcRatio}
                stringingData={stringingData}
                invertersUsed={invertersUsed}
                productionPerPanel={productionPerPanel}
                selectedPanelCount={selectedPanelCount}
                productionHardware={productionHardware}
                systemSizeKw={this.state.systemSizeKw}
                mpptCountTotal={this.state.mpptCountTotal}
                handleSolarProduction={this.handleSolarProduction}
                // Target production props
                useTargetProduction={useTargetProduction}
                targetProductionKwh={targetProductionKwh}
                maxDcAcRatio={this.state.maxDcAcRatio}
                onMaxDcAcRatioChange={(value) => {
                  const next =
                    value === null || value === undefined || Number.isNaN(value)
                      ? 1.5
                      : value;
                  this.setState({ maxDcAcRatio: next });
                  this.context.CanvasAPI.saveCanvas({
                    max_dc_ac_ratio: next,
                  });
                }}
                onlySelectedPanels={this.state.onlySelectedPanels}
                onOnlySelectedPanelsChange={(value) => {
                  this.setState({ onlySelectedPanels: value });
                  this.context.CanvasAPI.saveCanvas({
                    only_selected_panels: value,
                  });
                }}
                onUseTargetProductionChange={(value) => {
                  const nextTarget = value ? this.state.targetProductionKwh : 0;
                  this.setState({
                    useTargetProduction: value,
                    targetProductionKwh: nextTarget,
                  });
                  this.context.CanvasAPI.saveCanvas({
                    use_target_production: value,
                    target_production_kwh: value ? nextTarget : null,
                  });
                }}
                onTargetProductionKwhChange={(value) => {
                  this.setState({ targetProductionKwh: value });
                  this.context.CanvasAPI.saveCanvas({
                    // If user is setting a target, ensure toggle is persisted as enabled
                    use_target_production:
                      value > 0 ? true : this.state.useTargetProduction,
                    target_production_kwh: value > 0 ? value : null,
                  });
                }}
                // Auto quantity props
                primaryInverterAutoQuantity={primaryInverterAutoQuantity}
                onPrimaryInverterAutoQuantityChange={(value) => {
                  const nextQty = value
                    ? this.state.package?.inverterQuantity
                    : 1;
                  this.setState((prev) => ({
                    primaryInverterAutoQuantity: value,
                    hasAutoCalculatedInverterQuantity: !value,
                    package: {
                      ...(prev.package || {}),
                      inverterQuantity: value
                        ? prev.package?.inverterQuantity || 1
                        : 1,
                    },
                  }));
                  this.context.CanvasAPI.saveCanvas({
                    primary_inverter_auto_quantity: value,
                    inverter_quantity: value ? nextQty || 1 : 1,
                  });
                }}
              />
            </Col>
          </Row>
        </DesignContext.Provider>
      );
    }

    return (
      <DesignContext.Provider value={providerValues}>
        <Card className="shadow w-100 mt-3 bg-primary text-white" body>
          <Button
            className="float-right"
            onClick={() => this.setState({ isFullScreen: !isFullScreen })}
          >
            <ArrowsFullscreen />
          </Button>
          Here is your custom solar design for
          <h3 className="mt-2">{fullAddress}</h3>
        </Card>
        <Fullscreen
          isEnter={isFullScreen}
          onChange={(e) => this.setState({ isFullScreen: e })}
        >
          <Row className="bg-light py-3 full-screenable-node" noGutters>
            <Col xl={8} lg={12} md={12} s={12} xs={12} className="mb-3 pr-2">
              <Controls
                mode={mode}
                moveMode={moveMode}
                setMode={this.handleMode}
                setMoveMode={this.handleMoveMode}
                resetScale={this.handleResetScale}
                scaleUp={this.handleScaleUp}
                scaleDown={this.handleScaleDown}
                mapsLink={this.mapsLink}
                viewMode={konvaKeys.includes(accordion) ? '3D' : viewMode}
                switchView={this.switchView}
              />
              {viewer}
              <ControlsBottom
                saveCanvas={this.handleSaveAll}
                cleanCanvas={this.cleanCanvas}
                exportJSON={this.handleDownload3DJSON}
                exportDXF={this.handleDownloadDXF}
                disabled={!accordion || konvaKeys.includes(accordion)}
              />
            </Col>
            <Col xl={4} lg={12} md={12} s={12} xs={12}>
              <Menu
                activeKey={accordion}
                designType={designType}
                hasDSM={hasDSM}
                shaded={shade}
                buffer={buffer}
                rowSpacing={rowSpacing}
                panelTilt={panelTilt}
                azimuthRestriction={azimuthRestriction}
                fireSetbacks={fireSetbacks}
                setbackMode={setbackMode}
                shadingIsRunning={shadingIsRunning}
                serverSideShadingIsRunning={serverSideShadingIsRunning}
                setbackModeHandler={this.handleSetbackMode}
                fireSetbackHandler={this.handleFireSetbacks}
                colorRange={colorRange}
                currentAccordionHandler={this.handleCurrentAccordion}
                currentTabHandler={this.handleSegmentTabs}
                tool={tool}
                toolHandler={this.handleTool}
                selectedLabel={selectedLabel}
                selectedRoof={selectedRoof}
                selectedPanels={selectedPanels}
                selectedLabelHandler={this.handleSelectedLabel}
                clearLabelsHandler={this.clearLabelsHandler}
                deleteShapeHandler={this.handleDeleteShape}
                addPointToPolygonHandler={this.handleAddPointToPolygonFromMenu}
                removePointFromPolygonHandler={
                  this.handleRemovePointFromPolygonFromMenu
                }
                obstacleHeightHandler={this.handleObstacleHeight}
                obstacleRadiusHandler={this.handleObstacleRadius}
                obstacleSetbackHandler={this.handleObstacleSetback}
                obstacleOrientationHandler={this.handleObstacleOrientation}
                newPanelHandler={this.handleNewPanel}
                deletePanelHandler={this.handleDeletePanel}
                rotatePanelHandler={this.handleRotatePanel}
                setPVwattsLosses={this.setPVwattsLosses}
                losses={this.state.losses}
                segments={segments}
                resolution={resolution}
                segmentAzimuthHandler={this.handleSegmentAzimuth}
                segmentPitchAndAreaHandler={this.handleSegmentPitchAndArea}
                segmentEaveHeightHandler={this.handleSegmentEaveHeight}
                treeUpdateHandler={this.handleTreeUpdate}
                pitchInDegrees={this.state.pitch_in_degrees ?? true}
                onPitchInDegreesChange={this.handlePitchInDegreesChange}
                obstacles={obstacles}
                trees={trees}
                panels={panels}
                placementHandler={this.handlePlacement}
                shadingHandler={this.handleShading}
                weatherDataset={this.state.weatherDatasetSelected}
                setWeatherDataset={this.setWeatherDatasetSelected}
                weatherDatasetFetchedPath={this.state.weatherDatasetFetchedPath}
                weatherDatasetReady={(() => {
                  const p = this.state.weatherDatasetFetchedPath;
                  const sel = this.state.weatherDatasetSelected;
                  if (!p || !sel) return false;
                  const base = (String(p).split('/').pop() || '').trim();
                  const expectedFilename = `${sel}.csv`;
                  return base === expectedFilename;
                })()}
                onWeatherDatasetFetched={this.handleWeatherDatasetFetched}
                measureHandler={this.handleMeasure}
                runOptimizationHandler={this.runOptimizationHandler}
                runEdgeDetectionHandler={this.runEdgeDetectionHandler}
                runAIHandler={this.runAIHandler}
                proposalHandler={this.handleProposal}
                isCreatingProposal={this.state.isCreatingProposal}
                shadingNeedsRerun={this.state.shadingNeedsRerun}
                shadingHasRun={this.state.shadingHasRun}
                panelAzimuthHandler={this.handlePanelAzimuth}
                currentPanel={currentPanel}
                appliedPanel={appliedPanel}
                currentInverter={inverterHardware}
                currentInverterQuantity={
                  installationPackage?.inverterQuantity || 1
                }
                hasAutoCalculatedInverterQuantity={
                  hasAutoCalculatedInverterQuantity
                }
                currentAdditionalInverters={additionalInvertersHardware}
                packageAdditionalInverters={packageAdditionalInverters}
                changePanelHardware={this.changePanelHardware}
                changeInverterHardware={this.changeInverterHardware}
                changeInverterQuantity={this.changeInverterQuantity}
                changeAdditionalInverters={this.changeAdditionalInverters}
                setbackHandler={this.handleSetback}
                bufferHandler={this.handleBuffer}
                rowSpacingHandler={this.handleRowSpacing}
                panelTiltHandler={this.handlePanelTilt}
                azimuthRestrictionHandler={this.handleAzimuthRestriction}
                showOverlay={showOverlay}
                toggleOverlay={this.toggleOverlay}
                shadingMonth={shadingMonth}
                shadingStatus={shadingStatus}
                roofAreasSqFt={this.state.roofAreasSqFt}
                roofAreasUnit={this.state.roofAreasUnit}
                solarPanels={solarPanels}
                inverters={inverters}
                panelPlacementFillMode={panelPlacementFillMode}
                setPanelPlacementFillMode={this.setPanelPlacementFillMode}
                panelPlacementLayoutMode={panelPlacementLayoutMode}
                setPanelPlacementLayoutMode={this.setPanelPlacementLayoutMode}
                panelPlacementOnlySelectedRoof={panelPlacementOnlySelectedRoof}
                setPanelPlacementOnlySelectedRoof={
                  this.setPanelPlacementOnlySelectedRoof
                }
                clearPanels={this.clearPanels}
                // Production props
                productionIsRunning={productionIsRunning}
                productionHandled={productionHandled}
                totalAnnualKwh={totalAnnualKwh}
                totalClippingKwh={totalClippingKwh}
                dcAcRatio={dcAcRatio}
                stringingData={stringingData}
                invertersUsed={invertersUsed}
                productionPerPanel={productionPerPanel}
                selectedPanelCount={selectedPanelCount}
                productionHardware={productionHardware}
                systemSizeKw={this.state.systemSizeKw}
                mpptCountTotal={this.state.mpptCountTotal}
                handleSolarProduction={this.handleSolarProduction}
                // Target production props
                useTargetProduction={useTargetProduction}
                targetProductionKwh={targetProductionKwh}
                maxDcAcRatio={this.state.maxDcAcRatio}
                onMaxDcAcRatioChange={(value) => {
                  const next =
                    value === null || value === undefined || Number.isNaN(value)
                      ? 1.5
                      : value;
                  this.setState({ maxDcAcRatio: next });
                  this.context.CanvasAPI.saveCanvas({
                    max_dc_ac_ratio: next,
                  });
                }}
                onlySelectedPanels={this.state.onlySelectedPanels}
                onOnlySelectedPanelsChange={(value) => {
                  this.setState({ onlySelectedPanels: value });
                  this.context.CanvasAPI.saveCanvas({
                    only_selected_panels: value,
                  });
                }}
                onUseTargetProductionChange={(value) => {
                  const nextTarget = value ? this.state.targetProductionKwh : 0;
                  this.setState({
                    useTargetProduction: value,
                    targetProductionKwh: nextTarget,
                  });
                  this.context.CanvasAPI.saveCanvas({
                    use_target_production: value,
                    target_production_kwh: value ? nextTarget : null,
                  });
                }}
                onTargetProductionKwhChange={(value) => {
                  this.setState({ targetProductionKwh: value });
                  this.context.CanvasAPI.saveCanvas({
                    // If user is setting a target, ensure toggle is persisted as enabled
                    use_target_production:
                      value > 0 ? true : this.state.useTargetProduction,
                    target_production_kwh: value > 0 ? value : null,
                  });
                }}
                // Auto quantity props
                primaryInverterAutoQuantity={primaryInverterAutoQuantity}
                onPrimaryInverterAutoQuantityChange={(value) => {
                  const nextQty = value
                    ? this.state.package?.inverterQuantity
                    : 1;
                  this.setState((prev) => ({
                    primaryInverterAutoQuantity: value,
                    hasAutoCalculatedInverterQuantity: !value,
                    package: {
                      ...(prev.package || {}),
                      inverterQuantity: value
                        ? prev.package?.inverterQuantity || 1
                        : 1,
                    },
                  }));
                  this.context.CanvasAPI.saveCanvas({
                    primary_inverter_auto_quantity: value,
                    inverter_quantity: value ? nextQty || 1 : 1,
                  });
                }}
              />
            </Col>
          </Row>
        </Fullscreen>
        <ShadeReport
          wattage={panelHardware?.power || 0}
          segments={segments}
          panels={panels}
          optPitchAndAzimuth={property.optimal_roof}
        />
      </DesignContext.Provider>
    );
  }
}

DesignTool.contextType = LeadContext;
export default DesignTool;
